import { VentasResumenLine } from "@/components/VentasResumenLine";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatCOP, formatNumber, formatDate, formatDateTime, API, hoyISO, mesISOLocal } from "@/lib/api";
import PeriodoSelector, { rangoDePeriodo } from "@/components/PeriodoSelector";
import FiltroMenu from "@/components/FiltroMenu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import MoneyInput from "@/components/MoneyInput";
import DescargarGuiaMenu from "@/components/DescargarGuiaMenu";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Plus, Search, Trash2, Check, Truck, PackageCheck, X, ChevronRight, FileText, Wallet, Upload, Clock, Layers, RefreshCw, Hand, ShieldAlert, WifiOff, FileSpreadsheet, Eye, EyeOff, Package, Edit2, Lock, Unlock, Undo2, Sparkles, StickyNote, UserRound, AlertTriangle, Paperclip } from "lucide-react";
import IntelligentOrderDialog from "@/components/IntelligentOrderDialog";
import { toast } from "sonner";
import UsageTip from "@/components/UsageTip";
import PDFExportButton from "@/components/PDFExportButton";
import AccionesMenu from "@/components/AccionesMenu";
import ImageLightbox from "@/components/ImageLightbox";
import PrepararVisitaModal from "@/components/PrepararVisitaModal";
import { getOfflineClientes, getOfflineProductos, getOfflinePrecios, queueOfflinePedido, listOfflinePedidos, removeOfflinePedido, getVisitaMeta } from "@/lib/offlineStore";
import QuickClienteModal from "@/components/QuickClienteModal";
import FileInput from "@/components/FileInput";
import ExportExcelFechasModal from "@/components/ExportExcelFechasModal";
import DescargaFormatoModal from "@/components/DescargaFormatoModal";
import DevolucionPedidoModal from "@/components/DevolucionPedidoModal";
import CuposDisponiblesPanel from "@/components/CuposDisponiblesPanel";
import DateField from "@/components/DateField";
import { fuzzyFilter, fuzzyMatch, fuzzySearch } from "@/lib/fuzzySearch";
import { useHasPerm, useAuth } from "@/context/AuthContext";
import { useBackTrap, useBeforeUnloadGuard } from "@/hooks/useBackTrap";
import { esProductoDePrueba } from "@/lib/utils";
import { useSuppressCritical } from "@/context/NotifSuppressContext";

// Jun/2026: transportadoras del dropdown (las 4 primeras son las más usadas por Beem)
const TRANSPORTADORAS_CONOCIDAS = ["ECOENVIOS", "ROA", "TRANSPRENSA", "OCCIENVIOS", "Servientrega", "Coordinadora", "Envía", "TCC", "Interrapidísimo", "Deprisa", ""];

const ESTADO_META = {
  borrador: { label: "Borrador", color: "bg-slate-100 text-slate-700 border border-slate-300", ring: "ring-slate-200" },
  confirmado: { label: "Confirmado", color: "bg-blue-100 text-blue-800 border border-blue-300", ring: "ring-blue-200" },
  despachado: { label: "Despachado", color: "bg-amber-100 text-amber-800 border border-amber-300", ring: "ring-amber-200" },
  entregado: { label: "Entregado", color: "bg-emerald-100 text-emerald-800 border border-emerald-300", ring: "ring-emerald-200" },
  cancelado: { label: "Cancelado", color: "bg-rose-100 text-rose-800 border border-rose-300", ring: "ring-rose-200" },
  devuelto: { label: "Devuelto", color: "bg-purple-100 text-purple-800 border border-purple-300", ring: "ring-purple-200" },
};

const METODOS_PAGO = ["Contra entrega", "Transferencia", "Efectivo", "Credito", "Consignacion"];

function _diasDesde(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = Math.max(0, Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24)));
    if (diff === 0) return "hoy";
    if (diff === 1) return "hace 1 día";
    if (diff < 30) return `hace ${diff} días`;
    if (diff < 60) return "hace ~1 mes";
    const meses = Math.floor(diff / 30);
    return `hace ${meses} meses`;
  } catch { return ""; }
}

export default function PedidosPage() {
  const { user } = useAuth();
  const canCreate = useHasPerm("ventas.crear");
  const canEditarPerm = useHasPerm("ventas.editar");
  const _canGestionarDespachos = useHasPerm("despachos.gestionar");
  // Los operadores NO deben ver borradores (no son suyos, solo generan ruido).
  // Un operador es quien tiene rol "operador" O no puede crear ventas pero sí gestionar despachos.
  // Jun/2026: un operador que TAMBIÉN monta pedidos (ventas.crear / asignar_vendedor)
  // NO es "puro": necesita ver y retomar sus borradores.
  const _canAsignarVendedor = useHasPerm("ventas.asignar_vendedor");
  const canAsignarVendedorList = _canAsignarVendedor;
  const isOperadorPuro = !canCreate && !_canAsignarVendedor && (user?.role === "operador" || _canGestionarDespachos);
  const isAdminList = user?.role === "admin";
  const isDirector = user?.role === "director" || user?.role === "admin";
  // Jun/2026: selección múltiple de pedidos cancelados para eliminarlos definitivamente.
  const [selCancelados, setSelCancelados] = useState(new Set());
  const [eliminandoCancelados, setEliminandoCancelados] = useState(false);
  const [items, setItems] = useState([]);
  const [editarQuick, setEditarQuick] = useState(null); // pedido a editar directo desde la lista
  const [exportExcelOpen, setExportExcelOpen] = useState(false);
  const [estado, setEstado] = useState("");
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("todos"); // todos | mios | sin_asignar
  // Jun/2026: selector de periodo unificado (mes ◀ ▶ o rango). Por defecto sin filtro (todo).
  const [periodo, setPeriodo] = useState(() => {
    const sp = new URLSearchParams(window.location.search); // ?desde=&hasta= (desde el panel principal)
    return { modo: "rango", mes: mesISOLocal(), desde: sp.get("desde") || "", hasta: sp.get("hasta") || "" };
  });
  const { desde: fechaDesde = "", hasta: fechaHasta = "" } = rangoDePeriodo(periodo);
  const [openBuilder, setOpenBuilder] = useState(false);
  const [prepararOpen, setPrepararOpen] = useState(false);
  const [pdfSignal, setPdfSignal] = useState(0);
  const [detail, setDetail] = useState(null);
  const [listasByCli, setListasByCli] = useState({});
  const [guiaMenu, setGuiaMenu] = useState(null); // {pedidoId, nombre} - visor rápido desde la lista
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const canTomarPerm = useHasPerm("despachos.tomar");
  const canGestionarPerm = useHasPerm("despachos.gestionar");
  const canDespachar = canTomarPerm || canGestionarPerm;

  // Suprimir modal crítico mientras se crea un pedido (bloquea al usuario si no)
  useSuppressCritical(openBuilder);

  // Auto-abrir el wizard cuando llegamos con ?nuevo=1 (desde el BottomNav / atajos)
  // Auto-abrir el detalle cuando llegamos con ?open=<id> (desde notificaciones)
  useEffect(() => {
    if (searchParams.get("nuevo") === "1") {
      setOpenBuilder(true);
      const params = new URLSearchParams(searchParams);
      params.delete("nuevo");
      params.delete("t");
      setSearchParams(params, { replace: true });
      return;
    }
    const openId = searchParams.get("open") || searchParams.get("highlight");
    if (openId) {
      api.get(`/pedidos/${openId}`).then((r) => setDetail(r.data)).catch((e) => console.warn("No se pudo abrir pedido", e));
      const params = new URLSearchParams(searchParams);
      params.delete("open");
      params.delete("highlight");
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (estado) params.set("estado", estado);
    else if (isOperadorPuro) {
      // Operador puro: al ver "Todos", excluir borradores (no son suyos, son del vendedor).
      params.set("excluir_borradores", "1");
    }
    if (q) params.set("q", q);
    if (scope === "mios") params.set("mios", "1");
    else if (scope === "sin_asignar") params.set("sin_operador", "1");
    if (fechaDesde) params.set("fecha_desde", fechaDesde);
    if (fechaHasta) params.set("fecha_hasta", fechaHasta);
    if (estado === "cancelado") params.set("limit", "2000");
    params.set("resumen", "1"); // Jun/2026: lista liviana (sin items completos)
    const { data } = await api.get(`/pedidos?${params.toString()}`);
    setItems(data);
  }, [estado, q, scope, fechaDesde, fechaHasta]);
  useEffect(() => { load(); }, [load]);
  // Jun/2026: la selección de cancelados se limpia al cambiar filtros (evita borrar ids no visibles)
  useEffect(() => { setSelCancelados(new Set()); }, [estado, q, scope, fechaDesde, fechaHasta]);

  // Refetch cuando el director cambia el nombre del usuario actual (el snapshot
  // `vendedor_nombre` / `operador_nombre` en los pedidos cambió — mar/2026)
  useEffect(() => {
    const onNameChange = () => load();
    window.addEventListener("user-name-changed", onNameChange);
    return () => window.removeEventListener("user-name-changed", onNameChange);
  }, [load]);

  // Auto-refresh cada 15s cuando el operador está viendo "Sin operador" o "Todos" — para ver pedidos nuevos sin tener que recargar
  useEffect(() => {
    if (!canDespachar) return undefined;
    const t = setInterval(() => { if (document.visibilityState === "visible") load(); }, 45000);
    return () => clearInterval(t);
  }, [canDespachar, load]);

  const tomarRapido = async (e, pedidoId) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/pedidos/${pedidoId}/tomar`);
      if (data?.auto_confirmado) {
        toast.success("Pedido tomado y confirmado ✅");
      } else {
        toast.success("Pedido tomado 🙋");
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo tomar");
      load();
    }
  };

  // Jun/2026: la lista viene en modo resumen; al abrir cargamos el pedido completo.
  const abrirDetalle = async (p) => {
    setDetail(p);
    try {
      const { data } = await api.get(`/pedidos/${p.id}`);
      setDetail((cur) => (cur && cur.id === p.id ? data : cur));
    } catch (e) { console.warn("No se pudo cargar el detalle completo", e); }
  };

  const onCreated = async (id) => {
    setOpenBuilder(false);
    load();
    if (id) {
      const { data } = await api.get(`/pedidos/${id}`);
      setDetail(data);
    }
  };

  return (
    <div>
      <header className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Ventas</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground mt-2">Pedidos</h1>
          <p className="text-muted-foreground mt-2">
            {canDespachar && !canCreate
              ? "Toma y despacha los pedidos del equipo. Aprueba los pagos y actualiza el estado."
              : canCreate
                ? "Cree, confirme, despache y siga sus pedidos con precios automáticos por cliente."
                : "Consulte los pedidos, su estado y saldo pendiente."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <PDFExportButton
            endpoint="pedidos"
            filename="beem-pedidos"
            label="Exportar PDF"
            testid="export-pedidos-pdf"
            hidden
            openSignal={pdfSignal}
            fields={[
              { key: "desde", label: "Desde", type: "date" },
              { key: "hasta", label: "Hasta", type: "date" },
              { key: "estado", label: "Estado", type: "select", options: [
                { value: "borrador", label: "Borrador" },
                { value: "confirmado", label: "Confirmado" },
                { value: "despachado", label: "Despachado" },
                { value: "entregado", label: "Entregado" },
                { value: "cancelado", label: "Cancelado" },
              ] },
              { key: "vendedor_id", label: "Vendedor", type: "select", optionsFrom: "vendedores" },
            ]}
          />
          <AccionesMenu
            label="Exportar"
            testid="pedidos-exportar-menu"
            items={[
              { label: "Exportar PDF", icon: FileText, onClick: () => setPdfSignal((n) => n + 1), testid: "export-pedidos-pdf", hidden: !isDirector },
              { label: "Exportar Excel", icon: FileSpreadsheet, onClick: () => setExportExcelOpen(true), testid: "export-pedidos-excel-btn", title: "Dos hojas: resumen y detalle de items" },
              { label: "Preparar visita (sin internet)", icon: WifiOff, onClick: () => setPrepararOpen(true), testid: "preparar-visita-btn", hidden: !canCreate, title: "Descarga clientes, productos y precios para trabajar sin internet" },
            ]}
          />
          {canCreate && (
            <Button
              onClick={() => {
                // Feb/2026: en móvil usamos el Modo Vendedor rápido (/pedidos/movil),
                // igual que el FAB flotante del BottomNav. En desktop seguimos
                // abriendo el builder tradicional.
                if (typeof window !== "undefined" && window.innerWidth < 768) {
                  navigate("/pedidos/movil");
                  return;
                }
                setOpenBuilder(true);
              }}
              data-testid="new-pedido-btn"
              className="bg-primary hover:bg-primary/90 h-11"
            >
              <Plus className="w-4 h-4 mr-2" /> Nuevo pedido
            </Button>
          )}
        </div>
      </header>

      <UsageTip storageKey="pedidos" steps={(() => {
        const tips = [];
        if (canCreate) {
          tips.push("Escoge un cliente y agrega productos. El <b>precio sugerido</b> se aplica automáticamente según su lista.");
          tips.push("Si necesitas vender por debajo del mínimo, escribe el <b>motivo</b> y espera aprobación de un director.");
        }
        if (canDespachar) {
          tips.push("<b>Toma</b> los pedidos sin operador para asignártelos, y muévelos a <b>despachado</b> cuando salgan de bodega.");
          tips.push("Aprueba o rechaza los pagos de un pedido directamente desde su detalle: el saldo se actualiza al instante.");
        }
        if (!canCreate && !canDespachar) {
          tips.push("Aquí encuentras el histórico de pedidos con su estado, saldo pendiente y detalle línea por línea.");
        }
        return tips;
      })()} />

      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
          <Input placeholder="Buscar por numero o cliente..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-11" data-testid="search-pedido" />
        </div>
        <FiltroMenu label="Estado" value={estado} onChange={setEstado} neutro="" testid="filtro-estado" opciones={[
          { v: "", label: "Todos" },
          ...Object.entries(ESTADO_META).filter(([k]) => !(isOperadorPuro && k === "borrador")).map(([k, m]) => ({ v: k, label: m.label })),
        ]} />
      </div>

      {/* Una sola fila: periodo + vista + refrescar (Jun/2026: menos barras, menos ruido) */}
      <div className="flex gap-2 mb-4 items-center flex-wrap">
        <PeriodoSelector value={periodo} onChange={setPeriodo} testid="fecha" />
        {canDespachar && (
          <>
            <FiltroMenu label="Vista" value={scope} onChange={setScope} neutro="todos" testid="scope" opciones={[
              { v: "todos", label: "Todos" },
              { v: "mios", label: "Solo míos", dot: "sky" },
              { v: "sin_asignar", label: "Sin operador", dot: "amber" },
            ]} />
            <button onClick={load} className="ml-auto h-9 w-9 rounded-full border border-border text-muted-foreground hover:bg-accent flex items-center justify-center" data-testid="refresh-btn" title="Refrescar (auto cada 15s)">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>

      {isAdminList && estado === "cancelado" && items.length > 0 && (
        <div className="flex gap-2 mb-3 items-center flex-wrap rounded-xl border border-rose-200 bg-rose-50/60 dark:bg-rose-500/10 px-3 py-2" data-testid="cancelados-bulk-bar">
          <span className="text-xs font-semibold text-rose-800 dark:text-rose-300">
            {selCancelados.size > 0 ? `${selCancelados.size} seleccionado${selCancelados.size === 1 ? "" : "s"}` : "Elimina pedidos cancelados que ya no necesitas"}
          </span>
          <button
            onClick={() => setSelCancelados(new Set(items.filter((p) => (p.cancelled_motivo || "").startsWith("expirado_auto")).map((p) => p.id)))}
            className="h-8 px-3 rounded-full text-xs font-medium border border-border text-muted-foreground hover:bg-accent"
            data-testid="sel-expirados-btn"
          >⏱ Seleccionar expirados</button>
          <button
            onClick={() => setSelCancelados(selCancelados.size === items.length ? new Set() : new Set(items.map((p) => p.id)))}
            className="h-8 px-3 rounded-full text-xs font-medium border border-border text-muted-foreground hover:bg-accent"
            data-testid="sel-todos-cancelados-btn"
          >{selCancelados.size === items.length ? "Quitar selección" : "Seleccionar todos"}</button>
          <button
            disabled={selCancelados.size === 0 || eliminandoCancelados}
            onClick={async () => {
              if (!window.confirm(`¿Eliminar definitivamente ${selCancelados.size} pedido(s) cancelado(s)? Esta acción no se puede deshacer.`)) return;
              setEliminandoCancelados(true);
              try {
                const { data } = await api.post("/pedidos/_/eliminar-cancelados", { ids: [...selCancelados] });
                toast.success(`${data.eliminados} pedido(s) eliminado(s)`);
                setSelCancelados(new Set());
                load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "No se pudo eliminar");
              } finally { setEliminandoCancelados(false); }
            }}
            className="ml-auto h-8 px-3 rounded-full text-xs font-semibold bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50 flex items-center gap-1"
            data-testid="eliminar-cancelados-btn"
          ><Trash2 className="w-3.5 h-3.5" /> Eliminar seleccionados</button>
        </div>
      )}

      <div className="bg-card border border-border rounded-xl beem-table overflow-hidden">
        {/* Header: solo desktop */}
        <div className="hidden md:grid grid-cols-12 gap-3 px-6 py-3 border-b border-border text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
          <div className="col-span-2">Numero</div>
          <div className="col-span-3">Cliente</div>
          <div className="col-span-2">Vendedor</div>
          <div className="col-span-2">Estado / Operador</div>
          <div className="col-span-1 text-right">Items</div>
          <div className="col-span-1 text-right">Total</div>
          <div className="col-span-1 text-center">Acción</div>
        </div>
        {items.length === 0 && (
          <div className="px-6 py-16 text-center text-muted-foreground/70">
            <ShoppingCart className="w-8 h-8 mx-auto text-muted-foreground/50" />
            <div className="mt-3 text-sm">No hay pedidos aún. Crea el primero.</div>
          </div>
        )}
        {items.map((p) => {
          const m = ESTADO_META[p.estado] || ESTADO_META.borrador;
          const tomable = canDespachar && !p.operador_id && p.estado === "confirmado";
          return (
            <div key={p.id} onClick={() => abrirDetalle(p)} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 px-4 md:px-6 py-4 border-b border-border/60 items-start md:items-center hover:bg-muted/40 cursor-pointer transition-colors" data-testid={`pedido-row-${p.id}`}>
              <div className="md:col-span-2 font-mono text-sm font-semibold text-foreground flex items-center gap-1.5 min-w-0 flex-wrap">
                {isAdminList && estado === "cancelado" && (
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-rose-600 cursor-pointer"
                    checked={selCancelados.has(p.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => setSelCancelados((prev) => { const n = new Set(prev); n.has(p.id) ? n.delete(p.id) : n.add(p.id); return n; })}
                    data-testid={`sel-cancelado-${p.id}`}
                  />
                )}
                {p.estado === "cancelado" && (p.cancelled_motivo || "").startsWith("expirado_auto") && (
                  <span className="px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-200 text-[9px] font-bold uppercase tracking-wider" title={p.cancelled_motivo} data-testid={`badge-expirado-${p.id}`}>Expirado</span>
                )}
                {p.numero ? (
                  <span className="break-all">{p.numero}</span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-200 text-[10px] font-bold uppercase tracking-wider">BORRADOR</span>
                )}
                {p.bloqueado_por_excepcion && (
                  <span
                    className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-800 border border-amber-500/40 font-bold uppercase tracking-wider"
                    title="Este pedido tiene solicitudes de excepción de precio pendientes de aprobación"
                    data-testid={`badge-exc-${p.id}`}
                  >
                    <ShieldAlert className="w-3 h-3" /> Excepción
                  </span>
                )}
              </div>
              <div className="md:col-span-3 min-w-0">
                <div className="text-sm text-foreground truncate">{p.cliente_nombre}</div>
                <div className="text-xs text-muted-foreground">{formatDate(p.fecha)}{p.ciudad ? ` · ${p.ciudad}` : ""}</div>
              </div>
              <div className="md:col-span-2 text-sm text-foreground/80 truncate min-w-0"><span className="md:hidden text-[10px] uppercase text-muted-foreground/70 mr-1">Vendedor:</span>{p.vendedor_nombre}</div>
              <div className="md:col-span-2 min-w-0">
                <span className={`text-xs px-2 py-1 rounded-full ${m.color}`}>{m.label}</span>
                {p.operador_nombre && (
                  <div className="text-[10px] text-emerald-700 dark:text-emerald-400 mt-1 truncate inline-flex items-center gap-1"><UserRound className="w-3 h-3" /> {p.operador_nombre}</div>
                )}
                {!p.operador_nombre && p.estado === "confirmado" && (
                  <div className="text-[10px] text-amber-600 mt-1 inline-flex items-center gap-1"><Hand className="w-3 h-3" /> Sin operador</div>
                )}
              </div>
              <div className="md:col-span-1 text-left md:text-right text-sm tabular-nums text-muted-foreground"><span className="md:hidden text-[10px] uppercase mr-1">Items:</span>{p.items?.length || 0}</div>
              <div className="md:col-span-1 text-left md:text-right">
                <div className="text-sm font-semibold tabular-nums text-foreground">{formatCOP(p.total)}</div>
                {(() => {
                  const total = Number(p.total || 0);
                  const saldo = Number(p.saldo_pendiente ?? (total - (p.total_pagado || 0)));
                  const pagado = Number(p.total_pagado || 0);
                  if (total <= 0) return null;
                  if (p.esta_pagado || (pagado > 0 && saldo === 0)) {
                    return <div className="text-[10px] font-semibold text-emerald-700 tabular-nums inline-flex items-center gap-1" data-testid={`saldo-${p.id}`}><Check className="w-3 h-3" /> Pagado</div>;
                  }
                  if (pagado > 0) {
                    return <div className="text-[10px] font-semibold text-amber-700 tabular-nums" data-testid={`saldo-${p.id}`}>Debe {formatCOP(saldo)}</div>;
                  }
                  return <div className="text-[10px] text-muted-foreground/70 tabular-nums" data-testid={`saldo-${p.id}`}>Sin pagos</div>;
                })()}
              </div>
              <div className="md:col-span-1 flex justify-start md:justify-center gap-1 flex-wrap">
                {p.guia && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setGuiaMenu({ pedidoId: p.id, nombre: p.numero || p.id.slice(0, 8) }); }}
                    className="h-8 px-2 rounded-full text-xs font-semibold bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100 flex items-center gap-1 btn-tactile"
                    title={`Ver guía ${p.guia.numero || ""}${p.guia.transportadora ? " · " + p.guia.transportadora : ""}`}
                    data-testid={`guia-quick-${p.id}`}
                  >
                    <Paperclip className="w-3 h-3" />
                    <span className="hidden sm:inline">Guía</span>
                  </button>
                )}
                {tomable && (
                  <button
                    onClick={(e) => tomarRapido(e, p.id)}
                    className="h-8 px-3 rounded-full text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1 btn-tactile"
                    title="Tomar este pedido para prepararlo"
                    data-testid={`tomar-quick-${p.id}`}
                  >
                    <Hand className="w-3.5 h-3.5" /> Tomar
                  </button>
                )}
                {(() => {
                  const puedeEditarQuick = canEditarPerm
                    && ["borrador", "confirmado"].includes(p.estado)
                    && !p.edicion_bloqueada
                    && (p.vendedor_id === user?.id || p.creado_por_id === user?.id || canAsignarVendedorList);
                  if (!puedeEditarQuick) return null;
                  return (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const { data } = await api.get(`/pedidos/${p.id}`);
                          setEditarQuick(data);
                        } catch (err) {
                          toast.error("No se pudo abrir el pedido para editar");
                        }
                      }}
                      className="h-8 px-3 rounded-full text-xs font-semibold border border-primary/40 text-primary hover:bg-primary/5 flex items-center gap-1 btn-tactile"
                      title="Editar items de este pedido"
                      data-testid={`editar-quick-${p.id}`}
                    >
                      <Edit2 className="w-3.5 h-3.5" /> Editar
                    </button>
                  );
                })()}
                {!tomable && !(canEditarPerm && ["borrador", "confirmado"].includes(p.estado) && !p.edicion_bloqueada && (p.vendedor_id === user?.id || p.creado_por_id === user?.id || canAsignarVendedorList)) && (
                  <span className="text-xs text-muted-foreground/60">—</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {openBuilder && <PedidoBuilder onClose={() => setOpenBuilder(false)} onSaved={onCreated} />}
      <ExportExcelFechasModal
        open={exportExcelOpen}
        onOpenChange={setExportExcelOpen}
        titulo="Exportar pedidos a Excel"
        onExportar={async ({ desde, hasta }) => {
          try {
            const params = new URLSearchParams();
            if (estado) params.set("estado", estado);
            if (q) params.set("q", q);
            if (scope === "mios") params.set("mios", "1");
            else if (scope === "sin_asignar") params.set("sin_operador", "1");
            if (desde) params.set("fecha_desde", desde);
            if (hasta) params.set("fecha_hasta", hasta);
            params.set("limit", "2000");
            const { data } = await api.get(`/pedidos/export/excel?${params.toString()}`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
            const a = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 10);
            a.href = url; a.download = `Pedidos-Beem-${stamp}.xlsx`; a.click();
            URL.revokeObjectURL(url);
            toast.success("Excel descargado (resumen + items).");
          } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo descargar el Excel");
          }
        }}
      />
      {editarQuick && (
        <PedidoBuilder
          existing={editarQuick}
          onClose={() => setEditarQuick(null)}
          onSaved={async () => { setEditarQuick(null); load(); }}
        />
      )}
      {detail && <PedidoDetail pedido={detail} onClose={() => { setDetail(null); load(); }} />}
      <PrepararVisitaModal open={prepararOpen} onOpenChange={setPrepararOpen} />
      {guiaMenu && (
        <DescargarGuiaMenu
          pedidoId={guiaMenu.pedidoId}
          nombreArchivo={guiaMenu.nombre}
          onClose={() => setGuiaMenu(null)}
        />
      )}
    </div>
  );
}

/* ---------------- Pedido Builder ---------------- */
function PedidoBuilder({ onClose, onSaved, existing = null }) {
  const { user: _authUser } = useAuth();
  const canAsignarVendedor = useHasPerm("ventas.asignar_vendedor") || _authUser?.role === "admin";
  // Jun/2026: edición de pedido ya despachado/entregado — sin autoguardado, solo items/descuento.
  const esPostDespacho = !!existing && ["despachado", "entregado"].includes(existing.estado);
  // Feb/2026: clave única del borrador local por usuario. Si el usuario cambia
  // de página (sidebar) o cierra el navegador, al volver se le ofrece restaurar
  // su progreso. Solo aplica cuando estamos creando (no editando existente).
  const BORRADOR_LS_KEY = `beem_pedido_borrador_local_v2_${_authUser?.id || "anon"}`;
  const [vendedoresAsignables, setVendedoresAsignables] = useState([]);
  // vendedorAsigId: cuando el usuario tiene permiso para asignar y elige a alguien
  // más (o a sí mismo). Si queda vacío, el backend lo asigna al usuario actual.
  const [vendedorAsigId, setVendedorAsigId] = useState(existing?.vendedor_id || "");
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  // Jun/2026 (rendimiento con 200+ ítems): índices precalculados en vez de
  // búsquedas lineales sobre 650+ productos en cada render.
  const productosById = useMemo(() => new Map(productos.map((p) => [p.id, p])), [productos]);
  const prodIndex = useMemo(() => {
    const byPrefix = new Map(); const byFamKey = new Map();
    for (const p of productos) {
      const nombre = String(p.nombre || "").trim();
      const i = nombre.lastIndexOf(" - ");
      const prefijo = i > 0 ? nombre.substring(0, i).trim().toUpperCase() : "";
      const fk = (p.familia_key || "").trim().toUpperCase();
      if (prefijo) { if (!byPrefix.has(prefijo)) byPrefix.set(prefijo, []); byPrefix.get(prefijo).push(p); }
      if (fk) { if (!byFamKey.has(fk)) byFamKey.set(fk, []); byFamKey.get(fk).push(p); }
    }
    return { byPrefix, byFamKey };
  }, [productos]);
  const [listas, setListas] = useState([]);
  // Feb/2026: lee snapshot de localStorage antes de inicializar estados — así
  // si el usuario cambió de página y volvió, su borrador se restaura automático.
  const _snap = (() => {
    if (existing) return null;
    try {
      const raw = localStorage.getItem(BORRADOR_LS_KEY);
      if (!raw) return null;
      const s = JSON.parse(raw);
      if (!s || !Array.isArray(s.items) || s.items.length === 0) return null;
      // No restaurar snapshots viejos (>24h)
      if (s.ts && (Date.now() - s.ts) > 24 * 3600 * 1000) {
        localStorage.removeItem(BORRADOR_LS_KEY);
        return null;
      }
      return s;
    } catch { return null; }
  })();
  const [clienteId, setClienteId] = useState(existing?.cliente_id || _snap?.clienteId || "");
  const [items, setItems] = useState(
    existing?.items?.map((i) => ({ producto_id: i.producto_id, cantidad: i.cantidad, precio_unitario: i.precio_unitario, es_surtido: !!i.es_surtido, motivo_excepcion: i.motivo_excepcion || null, notas: i.notas || "" })) ||
    _snap?.items || []
  );
  // Último producto agregado: se resalta y se hace scroll hasta él (pedidos grandes)
  const [ultimoAgregado, setUltimoAgregado] = useState(null);
  useEffect(() => {
    if (!ultimoAgregado) return;
    const el = document.querySelector(`[data-pid="${ultimoAgregado}"]`);
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [ultimoAgregado]);
  const [fecha, setFecha] = useState(existing?.fecha || _snap?.fecha || hoyISO());
  const [ciudad, setCiudad] = useState(existing?.ciudad || _snap?.ciudad || "");
  const [metodo, setMetodo] = useState(existing?.metodo_pago || _snap?.metodo || "");
  const [descuento, setDescuento] = useState(existing?.descuento ?? _snap?.descuento ?? 0);
  const [notas, setNotas] = useState(existing?.notas || _snap?.notas || "");
  const [requiereFE, setRequiereFE] = useState(!!existing?.requiere_factura_electronica);
  const [saving, setSaving] = useState(false);
  // Feb/2026: modal de confirmación al salir con cambios sin guardar.
  // mode: null (cerrado) | "close" (via X/ESC) | { nav: fn } (via sidebar)
  const [exitConfirm, setExitConfirm] = useState(null);
  const [exitSaving, setExitSaving] = useState(false);
  // Autoguardado al editar pedido existente (Feb/2026): cada cambio (items,
  // precios, cantidades, notas, descuento, etc.) se persiste solo con debounce
  // de 1.5s. Al cerrar el modal se hace un flush final. Solo aplica cuando
  // `existing` está seteado (edición). Estados: idle | saving | saved | error.
  const [autoSaveStatus, setAutoSaveStatus] = useState("idle");
  const [autoSaveLastAt, setAutoSaveLastAt] = useState(null);
  const [autoSaveError, setAutoSaveError] = useState(null);
  const autoSaveTimerRef = useRef(null);
  // Skip primer render para no dispararnos con la carga inicial de estados
  const autoSaveInitialSkipRef = useRef(true);
  const [prodSearch, setProdSearch] = useState("");
  const [showIntel, setShowIntel] = useState(false);
  // Oct/2026: resalta la fila del item que el vendedor está editando (focus
  // en cantidad o precio) para que no se pierda cuando hay 20+ ítems en el
  // carrito. Se limpia al perder foco o cambiar de fila.
  const [editandoItemIdx, setEditandoItemIdx] = useState(null);
  const [notaAbiertaIdx, setNotaAbiertaIdx] = useState(null); // Jun/2026: nota por ítem
  // Jun/2026: "lupita" — reemplazar un ítem por otro producto sin borrarlo (típico tras pedido con IA)
  const [reemplazarIdx, setReemplazarIdx] = useState(null);
  const [reemplazarSearch, setReemplazarSearch] = useState("");
  const [cliSearch, setCliSearch] = useState("");
  const [cliSearchOpen, setCliSearchOpen] = useState(false);
  const [quickCliOpen, setQuickCliOpen] = useState(false);
  // Privacidad: por defecto OCULTAMOS el rango sug/mín para que un cliente
  // curioso mirando la pantalla no vea el margen. El vendedor lo puede mostrar
  // temporalmente con el toggle.
  const [mostrarPrecios, setMostrarPrecios] = useState(() => {
    try { return localStorage.getItem("beem_mostrar_rango_precios") === "1"; } catch { return false; }
  });
  const togglePrecios = () => {
    setMostrarPrecios((v) => {
      const next = !v;
      try { localStorage.setItem("beem_mostrar_rango_precios", next ? "1" : "0"); } catch {}
      return next;
    });
  };

  // Nov/2026: cada recurso se carga por separado (antes un fallo en /clientes o
  // /listas-precios dejaba el buscador de productos vacío en silencio).
  const [catalogoError, setCatalogoError] = useState(null);
  const [catalogoCargando, setCatalogoCargando] = useState(true);
  const cargarCatalogo = useCallback(async () => {
    setCatalogoCargando(true); setCatalogoError(null);
    const [rc, rp, rl] = await Promise.allSettled([
      api.get("/clientes"),
      api.get("/productos", { params: { limit: 5000 } }),
      api.get("/listas-precios"),
    ]);
    const lista = (r) => (r.status === "fulfilled" && Array.isArray(r.value?.data) ? r.value.data : null);
    const prods = lista(rp), clis = lista(rc), lts = lista(rl);
    setListas(lts || []);
    if (clis) setClientes(clis);
    if (prods) setProductos(prods);
    if (!prods || !clis) {
      const [offCli, offProd] = await Promise.all([getOfflineClientes().catch(() => []), getOfflineProductos().catch(() => [])]);
      if (!clis && offCli.length) setClientes(offCli);
      if (!prods && offProd.length) { setProductos(offProd); toast.info(`Productos desde tu cache local (${offProd.length}) — sin conexión con el servidor`); }
      if (!prods && !offProd.length) {
        const why = rp.status === "rejected" ? (rp.reason?.response?.status ? `error ${rp.reason.response.status}` : (rp.reason?.message || "sin respuesta")) : "respuesta inválida";
        setCatalogoError(why);
        toast.error(`No se pudo cargar el catálogo de productos (${why}). Toca "Reintentar".`);
      }
    }
    setCatalogoCargando(false);
  }, []);
  useEffect(() => { cargarCatalogo(); }, [cargarCatalogo]);

  // Memoria de precios: cada vez que cambia el cliente, cargamos el último precio pactado por producto
  const [preciosCliente, setPreciosCliente] = useState({});
  // Cupo y deuda en vivo: incluye deuda_pendiente y capacidad_endeudamiento del cliente (excluyendo pedido en edición)
  const [cupoInfo, setCupoInfo] = useState(null);
  useEffect(() => {
    if (!clienteId) { setPreciosCliente({}); setCupoInfo(null); return; }
    // Precios: intenta red, cae a IndexedDB si falla (crítico para trabajar offline)
    api.get(`/pedidos/_/precios-cliente/${clienteId}`)
      .then(({ data }) => setPreciosCliente(data || {}))
      .catch(async () => {
        const off = await getOfflinePrecios(clienteId);
        setPreciosCliente(off);
        if (Object.keys(off).length > 0) {
          toast.info("Precios del cliente cargados del cache offline");
        }
      });
    api.get(`/clientes/${clienteId}`)
      .then(({ data }) => setCupoInfo(data))
      .catch(() => {
        // Offline: usa el cliente del cache local (sin datos de cupo enriquecidos)
        const c = clientes.find((x) => x.id === clienteId);
        setCupoInfo(c || null);
      });
  }, [clienteId]);

  const cliente = clientes.find((c) => c.id === clienteId);
  // Preload del texto del input de búsqueda cuando ya hay cliente seleccionado
  // (por ej. al editar un pedido existente).
  useEffect(() => {
    if (cliente && !cliSearch) setCliSearch(cliente.nombre || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cliente?.id]);

  // Feb/2026: carga lista de vendedores asignables cuando el usuario tiene el permiso.
  useEffect(() => {
    if (!canAsignarVendedor) return;
    api.get("/pedidos/_/vendedores-asignables")
      .then(({ data }) => setVendedoresAsignables(data || []))
      .catch(() => { /* silencio: endpoint sólo disponible con permiso */ });
  }, [canAsignarVendedor]);

  // Feb/2026: aviso al usuario si acabamos de restaurar su borrador local.
  const restauradoRef = useRef(!!_snap);
  useEffect(() => {
    if (restauradoRef.current && !existing && items.length > 0) {
      const n = items.length;
      toast.success(
        `Restaurado tu pedido en construcción (${n} item${n>1?"s":""}). Si no lo quieres, cierra sin cliente y se descarta.`,
        { duration: 7000 }
      );
      restauradoRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Feb/2026: persiste snapshot del borrador en localStorage en CADA cambio.
  // Esto protege contra: (a) usuario clickea sidebar y cambia de página,
  // (b) cierra el navegador por accidente, (c) refresh. No aplica al editar.
  useEffect(() => {
    if (existing) return;
    if (!items || items.length === 0) {
      // Sin items no vale la pena guardar; limpia por si quedó algo viejo
      try { localStorage.removeItem(BORRADOR_LS_KEY); } catch {}
      return;
    }
    try {
      localStorage.setItem(BORRADOR_LS_KEY, JSON.stringify({
        ts: Date.now(),
        clienteId, items, fecha, ciudad, metodo,
        descuento: Number(descuento) || 0, notas,
      }));
    } catch {}
  }, [existing, BORRADOR_LS_KEY, clienteId, items, fecha, ciudad, metodo, descuento, notas]);

  // Feb/2026: también persiste cuando el usuario cambia de pestaña o cierra.
  useEffect(() => {
    if (existing) return;
    const handler = () => {
      if (!items || items.length === 0) return;
      try {
        localStorage.setItem(BORRADOR_LS_KEY, JSON.stringify({
          ts: Date.now(), clienteId, items, fecha, ciudad, metodo,
          descuento: Number(descuento) || 0, notas,
        }));
      } catch {}
    };
    window.addEventListener("beforeunload", handler);
    document.addEventListener("visibilitychange", handler);
    return () => {
      window.removeEventListener("beforeunload", handler);
      document.removeEventListener("visibilitychange", handler);
    };
  }, [existing, BORRADOR_LS_KEY, clienteId, items, fecha, ciudad, metodo, descuento, notas]);

  // Feb/2026: aviso nativo del navegador si intenta cerrar pestaña con items sin guardar.
  useEffect(() => {
    if (items.length === 0) return;
    const h = (e) => {
      e.preventDefault();
      e.returnValue = "Tienes un pedido en construcción sin guardar.";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [items.length]);
  const politica = cliente?.lista_precios_id ? listas.find((l) => l.id === cliente.lista_precios_id) : null;

  const pricingFor = (prod) => {
    // Sugerido base
    const baseSug = prod.precio_sugerido || prod.precio_base || 0;
    const baseMin = prod.precio_minimo || Math.round(baseSug * 0.9);
    let sug, minimo;
    if (!politica) {
      sug = baseSug; minimo = baseMin;
    } else {
      const overrides = politica.overrides || {};
      const minOv = politica.min_override || {};
      sug = overrides[prod.id] !== undefined ? overrides[prod.id] : (politica.descuento_pct ? Math.round(baseSug * (1 - politica.descuento_pct / 100)) : baseSug);
      minimo = minOv[prod.id] !== undefined ? minOv[prod.id] : baseMin;
      if (minimo > sug) minimo = sug;
    }
    // Memoria: si hay último precio con este cliente, lo priorizamos como sugerido
    const memo = preciosCliente[prod.id];
    if (memo && memo.ultimo_precio) {
      return { sugerido: memo.ultimo_precio, minimo, base_sugerido: sug, memo };
    }
    return { sugerido: sug, minimo, base_sugerido: sug, memo: null };
  };

  // Selector de variante cuando el usuario elige un producto padre
  const [variantPickerOpen, setVariantPickerOpen] = useState(false);
  const [variantPadre, setVariantPadre] = useState(null);
  const [variantList, setVariantList] = useState([]);
  const [variantLoading, setVariantLoading] = useState(false);

  // Preview grande del item (click en un item del pedido)
  const [previewItem, setPreviewItem] = useState(null); // { producto, item, pricing }
  // Jun/2026: tocar la FOTO en el buscador abre la imagen (no agrega); tocar el resto de la fila agrega.
  const [fotoPreview, setFotoPreview] = useState(null); // producto

  // Cantidades "pendientes" por producto en la búsqueda: el vendedor puede escribir
  // la cantidad ANTES de hacer click, para no tener que bajar hasta la fila del pedido.
  // key = producto id | familia key ; value = cantidad (number, admite decimales para metro/yarda)
  const [pendingQty, setPendingQty] = useState({});
  // Feb/2026: precio inline opcional. Si el vendedor lo escribe, se usa como
  // precio_unitario del item recién agregado. Si no, usamos el sugerido.
  const [pendingPrice, setPendingPrice] = useState({});
  const setPendingPriceFor = (key, val) => {
    setPendingPrice((prev) => ({ ...prev, [key]: val }));
  };
  const clearPendingPrice = (key) => setPendingPrice((prev) => { const c = { ...prev }; delete c[key]; return c; });
  const getPendingPrice = (key) => {
    const raw = pendingPrice[key];
    if (raw === undefined || raw === null || raw === "") return null;
    const n = Number(String(raw).replace(/[^\d]/g, ""));
    return isFinite(n) && n > 0 ? n : null;
  };
  // Feb/2026: cuando el vendedor enfoca un input de cantidad, resaltamos la fila
  // entera para que no confunda al escribir cantidades en la lista de 150+ ítems.
  const [focusedProductId, setFocusedProductId] = useState(null);
  const setPendingQtyFor = (key, val) => {
    // Feb/2026: aceptamos decimales (ej 13.5m). Guardamos crudo mientras el usuario
    // escribe para no romper la tipeo de "13," → "13.5" ni forzar redondeos raros.
    // El valor final se coacciona a número al agregarlo al pedido.
    setPendingQty((prev) => ({ ...prev, [key]: val }));
  };
  const clearPendingQty = (key) => setPendingQty((prev) => { const c = { ...prev }; delete c[key]; return c; });
  const getPendingQty = (key) => {
    const raw = pendingQty[key];
    if (raw === undefined || raw === "") return 1;
    const n = parseFloat(String(raw).replace(",", "."));
    return isFinite(n) && n > 0 ? n : 1;
  };

  // Jun/2026: los ítems se mantienen AGRUPADOS por familia (prefijo del nombre antes de " - ",
  // o familia_key) en el orden en que apareció cada familia. Así la remisión y la preparación
  // en bodega salen juntas (todas las cintas, todas las mariposas…).
  const agruparPorFamilia = useCallback((lista, mapa) => {
    const byId = mapa || productosById;
    const keyDe = (it) => {
      const p = byId.get(it.producto_id);
      if (!p) return `__${it.producto_id}`;
      const n = String(p.nombre || "").trim();
      const i = n.lastIndexOf(" - ");
      return (i > 0 ? n.substring(0, i) : (p.familia_key || n)).trim().toUpperCase();
    };
    const grupos = new Map();
    for (const it of lista) {
      const k = keyDe(it);
      if (!grupos.has(k)) grupos.set(k, []);
      grupos.get(k).push(it);
    }
    return [...grupos.values()].flat();
  }, [productosById]);

  const addProducto = async (prod, cantidadOverride) => {
    // Feb/2026: admite decimales (ej 13.5m). Fallback a 1 si el input está vacío/invalido.
    let cant = Number(cantidadOverride);
    if (!isFinite(cant) || cant <= 0) cant = getPendingQty(prod.id);
    if (!isFinite(cant) || cant <= 0) cant = 1;
    if (items.some((it) => it.producto_id === prod.id)) {
      toast.info("Ya esta en el pedido");
      return;
    }
    // Si es padre: cargar variantes
    if (prod.es_padre) {
      setProdSearch("");
      // Si solo tiene UNA variante, la agregamos automáticamente sin
      // abrir el picker (evita un click extra al vendedor cuando no hay
      // realmente colores para elegir).
      try {
        const { data } = await api.get(`/productos/${prod.id}/variantes`);
        const variantes = data.variantes || [];
        if (variantes.length === 1) {
          addVariante(variantes[0], cant);
          return;
        }
        setVariantPadre(prod);
        setVariantList([...variantes].sort((a, b) => String(a.color || a.nombre || "").localeCompare(String(b.color || b.nombre || ""), "es", { sensitivity: "base", numeric: true })));
        setVariantPickerOpen(true);
      } catch {
        setVariantPadre(prod);
        setVariantList([]);
        setVariantPickerOpen(true);
      }
      return;
    }
    // Bloqueo por stock: sin stock no se puede agregar
    const dispProd = (prod.stock_actual || 0) - (prod.stock_reservado || 0);
    if (dispProd <= 0) {
      toast.error(`${prod.nombre} sin stock disponible. Espera el próximo contenedor.`);
      return;
    }
    const p = pricingFor(prod);
    const precioOverride = getPendingPrice(prod.id);
    setItems(agruparPorFamilia([...items, { producto_id: prod.id, cantidad: cant, precio_unitario: precioOverride ?? p.sugerido, motivo_excepcion: "" }]));
    clearPendingQty(prod.id);
    clearPendingPrice(prod.id);
    // Oct/2026: feedback inmediato — el vendedor en desktop ve claramente que
    // el agregado funcionó (antes la lista de items quedaba fuera de vista
    // bajo el dropdown y sentía que no pasaba nada).
    toast.success(`+${cant} ${prod.sku || prod.nombre}`, { duration: 1800 });
    // NO borramos prodSearch — así el vendedor puede seguir seleccionando
    // más colores del mismo filtro sin tener que re-escribir la búsqueda.
  };

  // Feb/2026: batch-add — el vendedor escribe cantidades en varios items de un
  // grupo y con un solo botón los agrega todos al pedido sin cerrar la
  // búsqueda. Silenciosamente omite items sin cantidad, sin stock, o ya
  // agregados. Retorna cuántos se agregaron.
  const addTodosDelGrupo = (grupo) => {
    let agregados = 0;
    let omitidos = 0;
    const nuevos = [];
    for (const p of grupo.prods) {
      const raw = pendingQty[p.id];
      if (raw === undefined || raw === "") continue;
      const cant = parseFloat(String(raw).replace(",", "."));
      if (!isFinite(cant) || cant <= 0) continue;
      if (items.some((it) => it.producto_id === p.id)) { omitidos++; continue; }
      const disp = (p.stock_actual || 0) - (p.stock_reservado || 0);
      if (disp <= 0) { omitidos++; continue; }
      const pr = pricingFor(p);
      const precioOvr = getPendingPrice(p.id);
      nuevos.push({ producto_id: p.id, cantidad: cant, precio_unitario: precioOvr ?? pr.sugerido, motivo_excepcion: "" });
      agregados++;
    }
    if (!agregados) {
      toast.error("Escribe cantidad en al menos uno de los ítems del grupo");
      return 0;
    }
    setItems(agruparPorFamilia([...items, ...nuevos]));
    // Limpiar cantidades y precios pendientes de este grupo
    setPendingQty((prev) => {
      const next = { ...prev };
      for (const p of grupo.prods) delete next[p.id];
      return next;
    });
    setPendingPrice((prev) => {
      const next = { ...prev };
      for (const p of grupo.prods) delete next[p.id];
      return next;
    });
    // Feb/2026 (bug fix): cerrar el listado de productos tras un batch-add para
    // que el vendedor vuelva a ver el pedido tal como va quedando sin tener que
    // scrollear o cerrar manualmente los filtros.
    setProdSearch("");
    setProdCat("");
    setExpandedFamilia(null);
    toast.success(`${agregados} ítem${agregados === 1 ? "" : "s"} agregado${agregados === 1 ? "" : "s"} al pedido${omitidos ? ` · ${omitidos} omitido${omitidos === 1 ? "" : "s"}` : ""}`);
    return agregados;
  };

  const addVariante = (v, cantidadOverride) => {
    let cant = Number(cantidadOverride);
    if (!isFinite(cant) || cant <= 0) cant = getPendingQty(v.id);
    if (!isFinite(cant) || cant <= 0) cant = 1;
    // Si ya está en el pedido, aviso
    if (items.some((it) => it.producto_id === v.id)) {
      toast.info("Esta variante ya está en el pedido");
      return;
    }
    // Bloqueo por stock
    const disp = (v.stock_actual || 0) - (v.stock_reservado || 0);
    if (disp <= 0) {
      toast.error(`${v.variante_label || v.nombre} sin stock disponible.`);
      return;
    }
    // Aseguramos que la variante esté en la lista `productos` para que pricingFor y subtotal funcionen
    setProductos((prev) => (prev.some((x) => x.id === v.id) ? prev : [...prev, v]));
    const p = pricingFor(v);
    setItems((prev) => agruparPorFamilia([...prev, { producto_id: v.id, cantidad: cant, precio_unitario: p.sugerido, motivo_excepcion: "" }], new Map([...productosById, [v.id, v]])));
    setUltimoAgregado(v.id);
    clearPendingQty(v.id);
    // Cerrar picker (permite agregar solo una variante por click; el user puede reabrir si quiere otra)
    setVariantPickerOpen(false);
    setVariantPadre(null);
    setVariantList([]);
    toast.success(`Variante agregada: ${v.variante_label || v.nombre}${cant > 1 ? ` × ${cant}` : ""}`);
  };

  const addSurtido = () => {
    // "Bodega decide": agregamos el producto padre con es_surtido=true, cant=1.
    // El operador definirá el desglose después.
    const padre = variantPadre;
    if (!padre) return;
    if (items.some((it) => it.producto_id === padre.id && it.es_surtido)) {
      toast.info("Ya hay un ítem surtido de este producto en el pedido");
      return;
    }
    // Stock agregado disponible entre variantes
    const stockAgg = variantList.reduce((s, v) => s + Math.max(0, (v.stock_actual || 0) - (v.stock_reservado || 0)), 0);
    if (stockAgg <= 0) {
      toast.error("Ninguna variante tiene stock. No se puede agregar surtido.");
      return;
    }
    setProductos((prev) => (prev.some((x) => x.id === padre.id) ? prev : [...prev, padre]));
    const p = pricingFor(padre);
    setItems((prev) => agruparPorFamilia([...prev, {
      producto_id: padre.id, cantidad: 1, precio_unitario: p.sugerido || 0,
      motivo_excepcion: "", es_surtido: true,
    }], new Map([...productosById, [padre.id, padre]])));
    setUltimoAgregado(padre.id);
    setVariantPickerOpen(false);
    setVariantPadre(null);
    setVariantList([]);
    toast.success(`Surtido agregado. La bodega definirá los colores al preparar.`);
  };
  const updateItem = (idx, patch) => {
    // Clamp de cantidad al stock disponible cuando corresponda
    if ("cantidad" in patch) {
      const it = items[idx];
      const p = productosById.get(it.producto_id);
      if (p) {
        const disp = (p.stock_actual || 0) - (p.stock_reservado || 0);
        const pedida = Number(patch.cantidad);
        if (!Number.isNaN(pedida) && disp > 0 && pedida > disp) {
          patch = { ...patch, cantidad: disp };
          toast.warning(`Máximo disponible de ${p.nombre}: ${disp} ${p.unidad || "un"}`);
        }
      }
    }
    const next = [...items]; next[idx] = { ...next[idx], ...patch }; setItems(next);
  };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  // Jun/2026: conteo de ítems por familia_key (para el botón "aplicar precio al grupo").
  // Jun/2026: clave de grupo para "aplicar precio al grupo" = prefijo del nombre
  // ("CINTA DOBLE RAZO 3MM" en "... - Rojo"); así 3MM y 7MM no se mezclan aunque
  // compartan familia_key. Fallback a familia_key si el nombre no tiene " - ".
  const grupoPrecioKey = (prod) => {
    if (!prod) return "";
    const n = String(prod.nombre || "").trim();
    const i = n.lastIndexOf(" - ");
    return i > 0 ? n.substring(0, i).trim().toUpperCase() : (prod.familia_key || "").trim().toUpperCase();
  };
  const famKeyCounts = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      const fk = grupoPrecioKey(productosById.get(it.producto_id));
      if (fk) m.set(fk, (m.get(fk) || 0) + 1);
    }
    return m;
  }, [items, productosById]); // eslint-disable-line react-hooks/exhaustive-deps
  const subtotal = useMemo(() => items.reduce((acc, it) => {
    const p = productosById.get(it.producto_id);
    return acc + Math.round((Number(it.precio_unitario) || 0) * (Number(it.cantidad) || 0));
  }, 0), [items, productos]);
  const total = Math.max(0, subtotal - (Number(descuento) || 0));

  // Update prices cuando el usuario cambia el cliente activamente.
  // No corre al montar (no sobrescribe precios manuales al editar un pedido existente).
  // También re-ejecuta cuando llega la memoria de precios asincrónicamente para el mismo cliente.
  const prevClienteRef = useRef(existing?.cliente_id || "");
  useEffect(() => {
    if (!clienteId) return;
    const clienteCambio = prevClienteRef.current !== clienteId;
    prevClienteRef.current = clienteId;
    // Recalcular precios solo si:
    // 1) El cliente cambió de verdad (no en el mount inicial de un existing), o
    // 2) Llegó la memoria de precios (mismo cliente, preciosCliente pasó de {} a lleno)
    if (!clienteCambio && Object.keys(preciosCliente).length === 0) return;
    setItems((prev) => prev.map((it) => {
      const p = productosById.get(it.producto_id);
      if (!p) return it;
      const pr = pricingFor(p);
      // Si hay memoria explícita, usarla siempre. Si no, solo pisar en cambio de cliente.
      if (pr.memo) return { ...it, precio_unitario: pr.sugerido };
      if (clienteCambio) return { ...it, precio_unitario: pr.sugerido };
      return it;
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId, preciosCliente]);

  // Jun/2026: en pedidos largos, saltar directo al ítem que necesita atención.
  const irAlItem = (idx) => {
    setEditandoItemIdx(idx);
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="item-row-${idx}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      const inp = el?.querySelector(`[data-testid="item-motivo-exc-${idx}"]`) || el?.querySelector(`[data-testid="item-precio-${idx}"]`);
      setTimeout(() => inp?.focus?.(), 350);
    });
  };
  const itemsBajoMinimo = items.reduce((acc, it, idx) => {
    const p = productosById.get(it.producto_id);
    if (!p) return acc;
    const pr = pricingFor(p);
    if (Number(it.precio_unitario) < pr.minimo) acc.push({ idx, sinMotivo: !it.motivo_excepcion });
    return acc;
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!clienteId) { toast.error("Selecciona un cliente"); return; }
    if (items.length === 0) { toast.error("Agrega al menos un producto"); return; }
    // Validar que items bajo minimo tengan motivo
    for (let idx = 0; idx < items.length; idx++) {
      const it = items[idx];
      const p = productosById.get(it.producto_id);
      if (!p) continue;
      const pr = pricingFor(p);
      if (Number(it.precio_unitario) < pr.minimo && !it.motivo_excepcion) {
        toast.error(`Ítem #${idx + 1} — ${p.nombre}: precio bajo mínimo (${formatCOP(pr.minimo)}). Escribe el motivo o sube el precio.`, { duration: 6000 });
        irAlItem(idx);
        return;
      }
    }
    // Validar que el descuento global no lleve el total por debajo de la suma de mínimos
    const subtotal = items.reduce((s, it) => s + (Number(it.cantidad) * Number(it.precio_unitario)), 0);
    const totalMinimo = items.reduce((s, it) => {
      const p = productosById.get(it.producto_id);
      const pr = p ? pricingFor(p) : { minimo: 0 };
      return s + (Number(it.cantidad) * (pr.minimo || 0));
    }, 0);
    const totalConDescuento = subtotal - (Number(descuento) || 0);
    if (Number(descuento) > 0 && totalConDescuento < totalMinimo) {
      toast.error(
        `El descuento deja el total ($${totalConDescuento.toLocaleString("es-CO")}) por debajo de la suma de mínimos ($${totalMinimo.toLocaleString("es-CO")}). Reduce el descuento.`,
        { duration: 6000 },
      );
      return;
    }
    // Factura electrónica exige RUT del cliente
    if (requiereFE && !cliente?.rut) {
      toast.error(
        "El cliente no tiene RUT cargado. Sube el RUT desde su ficha o desmarca 'Requiere factura electrónica'.",
        { duration: 6000 },
      );
      return;
    }
    if (esPostDespacho && !window.confirm(
      `Este pedido ya fue ${existing.estado}. Los cambios en items/descuento ajustarán el inventario automáticamente (se reingresa lo anterior y se descuenta lo nuevo). ¿Continuar?`
    )) return;
    setSaving(true);
    try {
      const payload = {
        cliente_id: clienteId,
        fecha, ciudad: ciudad || null, metodo_pago: metodo || null,
        descuento: Number(descuento) || 0, notas: notas || null,
        requiere_factura_electronica: !!requiereFE,
        vendedor_id: canAsignarVendedor && vendedorAsigId ? vendedorAsigId : null,
        items: agruparPorFamilia(items).map((it) => {
          const p = productosById.get(it.producto_id);
          const pr = p ? pricingFor(p) : { minimo: 0 };
          const bajo = Number(it.precio_unitario) < pr.minimo;
          return {
            producto_id: it.producto_id,
            cantidad: Number(it.cantidad),
            precio_unitario: Number(it.precio_unitario),
            motivo_excepcion: bajo ? it.motivo_excepcion : null,
            es_surtido: !!it.es_surtido,
            notas: (it.notas || "").trim() || null,
          };
        }),
      };
      // Snapshot para modo offline (queda en la cola con datos legibles)
      const clienteObj = clientes.find((c) => c.id === clienteId);
      const itemsSnap = payload.items.map((it) => {
        const p = productosById.get(it.producto_id);
        return {
          producto_nombre: p?.nombre || "Producto",
          sku: p?.sku,
          cantidad: it.cantidad,
          precio_unitario: it.precio_unitario,
          subtotal: it.cantidad * it.precio_unitario,
        };
      });
      const totalSnap = itemsSnap.reduce((a, b) => a + b.subtotal, 0);
      payload.__snapshot = {
        cliente_nombre: clienteObj?.nombre,
        total: totalSnap,
      };

      const { data } = existing
        ? await api.patch(`/pedidos/${existing.id}`, payload)
        : await api.post("/pedidos", payload);
      if (data.__offline) {
        toast.success(`Pedido guardado sin conexión. Se subirá al volver la red.`, { duration: 5000 });
      } else if (data.bloqueado_por_excepcion) {
        toast.success(`Pedido ${data.numero} ${existing ? "actualizado" : "creado"} como BORRADOR. Excepción(es) enviadas al director para aprobación.`, { duration: 6000 });
      } else {
        toast.success(`Pedido ${data.numero} ${existing ? "actualizado" : "creado"}`);
      }
      onSaved(data.id);
      // Limpiar snapshot local — ya quedó persistido en el backend
      try { localStorage.removeItem(BORRADOR_LS_KEY); } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear");
    } finally { setSaving(false); }
  };

  // Feb/2026: auto-guardar como borrador cuando el usuario cierra el modal por
  // error (click fuera / Escape). Sólo dispara si hay al menos un item + cliente
  // seleccionado (evita crear borradores vacíos). Silencioso: solo un toast.
  const autoSaveBorrador = async () => {
    if (existing) return true; // No auto-guardar si estamos editando un pedido existente
    if (items.length === 0) return true; // Nada útil que guardar
    try {
      const clienteObj = clientes.find((c) => c.id === clienteId);
      const itemsSnap = items.map((it) => {
        const p = productosById.get(it.producto_id);
        return {
          producto_nombre: p?.nombre || "Producto",
          sku: p?.sku,
          cantidad: Number(it.cantidad),
          precio_unitario: Number(it.precio_unitario),
          subtotal: Number(it.cantidad) * Number(it.precio_unitario),
        };
      });
      const totalSnap = itemsSnap.reduce((a, b) => a + b.subtotal, 0);
      const payload = {
        cliente_id: clienteId || null,
        fecha,
        ciudad: ciudad || null,
        metodo_pago: metodo || null,
        descuento: Number(descuento) || 0,
        notas: notas || null,
        requiere_factura_electronica: !!requiereFE,
        forzar_borrador: true, // marca especial para el backend: no valida stock/precios
        vendedor_id: canAsignarVendedor && vendedorAsigId ? vendedorAsigId : null,
        items: agruparPorFamilia(items).map((it) => ({
          producto_id: it.producto_id,
          cantidad: Number(it.cantidad),
          precio_unitario: Number(it.precio_unitario),
          motivo_excepcion: null,
          es_surtido: !!it.es_surtido,
          notas: (it.notas || "").trim() || null,
        })),
        __snapshot: { cliente_nombre: clienteObj?.nombre || "Sin asignar", total: totalSnap },
      };
      const { data } = await api.post("/pedidos", payload);
      toast.success(
        `Borrador ${data.numero || "guardado"} — tu progreso quedó salvo${data.__offline ? " (offline)" : ""}. Ábrelo desde la lista para continuar.`,
        { duration: 6000 }
      );
      // Limpiar snapshot local — ya quedó persistido en backend
      try { localStorage.removeItem(BORRADOR_LS_KEY); } catch {}
      return true;
    } catch (err) {
      // El snapshot queda en localStorage y se restaura al volver a abrir Nuevo pedido.
      console.warn("autoSaveBorrador falló:", err);
      toast.error(`No se pudo guardar el borrador: ${err?.response?.data?.detail || "sin conexión"}. Tu pedido sigue aquí.`, { duration: 7000 });
      return false;
    }
  };

  // Autoguardado al editar pedido existente (Feb/2026).
  // Guarda silenciosamente los cambios cada 1.5s tras el último cambio. Si el
  // usuario cierra el modal, se hace un flush final para asegurar cero pérdida.
  const autoSaveExisting = useCallback(async () => {
    if (!existing) return true;
    // Jun/2026: borradores sin cliente también se autoguardan (antes se perdían).
    if (items.length === 0) return true;
    if (!clienteId && existing.estado !== "borrador") return true;
    // No dispares si estamos en medio del save manual
    if (saving) return true;
    setAutoSaveStatus("saving");
    setAutoSaveError(null);
    try {
      // Payload identico a save() pero sin validaciones agresivas
      const payload = {
        cliente_id: clienteId || null,
        fecha,
        ciudad: ciudad || null,
        metodo_pago: metodo || null,
        descuento: Number(descuento) || 0,
        notas: notas || null,
        requiere_factura_electronica: !!requiereFE,
        vendedor_id: canAsignarVendedor && vendedorAsigId ? vendedorAsigId : null,
        // Jun/2026: en borradores el autoguardado NUNCA debe fallar por stock/RUT
        // (igual que el auto-borrador del POST). Se valida al confirmar.
        forzar_borrador: existing.estado === "borrador",
        items: agruparPorFamilia(items).map((it) => ({
          producto_id: it.producto_id,
          cantidad: Number(it.cantidad),
          precio_unitario: Number(it.precio_unitario),
          motivo_excepcion: it.motivo_excepcion || null,
          es_surtido: !!it.es_surtido,
          notas: (it.notas || "").trim() || null,
        })),
      };
      await api.patch(`/pedidos/${existing.id}`, payload);
      setAutoSaveStatus("saved");
      setAutoSaveLastAt(new Date());
      return true;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Error al guardar";
      setAutoSaveStatus("error");
      setAutoSaveError(msg);
      console.warn("autoSaveExisting falló:", msg);
      return false;
    }
  }, [existing, clienteId, items, fecha, ciudad, metodo, descuento, notas, requiereFE, saving, canAsignarVendedor, vendedorAsigId, agruparPorFamilia]);

  // Debounce: cada vez que cambian los campos relevantes, agenda un save 1.5s
  // después. Cancela agendamientos anteriores para no acumular POSTs.
  useEffect(() => {
    if (!existing || esPostDespacho) return;
    // El primer render carga los estados desde `existing` → no dispares save
    if (autoSaveInitialSkipRef.current) {
      autoSaveInitialSkipRef.current = false;
      return;
    }
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveTimerRef.current = null;
      autoSaveExisting();
    }, 1500);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [existing, clienteId, items, fecha, ciudad, metodo, descuento, notas, requiereFE, autoSaveExisting]);

  // Flush al cerrar el modal cuando estamos editando: guarda lo pendiente
  // (si hay timer agendado) antes de cerrar, para que jamás se pierda un cambio.
  const flushAutoSaveAndClose = async () => {
    if (existing && autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
      const ok = await autoSaveExisting();
      if (!ok) { setExitConfirm({ kind: "close" }); return; }
    }
    onClose();
  };

  // Filtro por categoría (chips) + búsqueda (nombre/SKU). Sin límite artificial
  // — mostramos TODOS los matches con scroll dentro del dropdown; agrupados por
  // "familia" (todo antes del último " - " en el nombre) igual que el catálogo
  // público. Un click sobre una familia con varias variantes abre un mini panel
  // con todas para elegir un color específico.
  const [prodCat, setProdCat] = useState("");
  const [expandedFamilia, setExpandedFamilia] = useState(null);

  // Feb/2026: inferir categoría para productos huérfanos (import nuevo sin
  // categoria asignada) usando los otros productos de su misma familia. Así
  // el filtro del dropdown no los oculta cuando el vendedor filtra por una
  // categoría conocida.
  const categoriaPorFamilia = (() => {
    const mapa = new Map();
    for (const p of productos) {
      const cat = p.categoria_nombre || p.categoria;
      if (!cat) continue;
      const famKey = (p.familia_key || "").trim().toUpperCase();
      if (famKey) mapa.set(famKey, cat);
      const nombre = String(p.nombre || "");
      const idx = nombre.lastIndexOf(" - ");
      if (idx > 0) {
        const prefijo = nombre.substring(0, idx).trim().toUpperCase();
        if (prefijo && !mapa.has(prefijo)) mapa.set(prefijo, cat);
      }
    }
    return mapa;
  })();

  const categoriaDe = (p) => {
    const direct = p.categoria_nombre || p.categoria;
    if (direct) return direct;
    const famKey = (p.familia_key || "").trim().toUpperCase();
    if (famKey && categoriaPorFamilia.has(famKey)) return categoriaPorFamilia.get(famKey);
    const nombre = String(p.nombre || "");
    const idx = nombre.lastIndexOf(" - ");
    if (idx > 0) {
      const prefijo = nombre.substring(0, idx).trim().toUpperCase();
      if (categoriaPorFamilia.has(prefijo)) return categoriaPorFamilia.get(prefijo);
    }
    return null;
  };

  // Oct/2026: normalizador para comparación de categorías. Antes el filtro
  // comparaba `categoriaDe(p) === prodCat` como strings exactos, de modo que
  // productos recién importados con `categoria_nombre` con caps/espacios
  // ligeramente distintos NO matcheaban y "desaparecían" al aplicar filtro
  // (bug reportado: "los colores agotados que se cargaron en estos días no
  // aparecen cuando aplico filtro").
  const _normCat = (s) => (s || "").toString().trim().toUpperCase().replace(/\s+/g, " ");

  const categoriasProd = (() => {
    // Agrupamos por categoría normalizada para que categorías con espacios/
    // caps distintos no aparezcan como entradas duplicadas en el dropdown.
    const map = new Map(); // normKey -> { display, count }
    for (const p of productos) {
      const raw = categoriaDe(p);
      if (!raw) continue;
      const key = _normCat(raw);
      if (!key) continue;
      if (!map.has(key)) map.set(key, { display: raw, count: 0 });
      map.get(key).count += 1;
    }
    return Array.from(map.entries())
      .map(([key, v]) => [v.display, v.count, key])
      .sort((a, b) => b[1] - a[1]);
  })();

  const filteredProdsRaw = useMemo(() => {
    let base = productos;
    if (prodCat) {
      const target = _normCat(prodCat);
      base = base.filter((p) => _normCat(categoriaDe(p)) === target);
    }
    if (!prodSearch) return base;
    // Oct/2026: scored fuzzy — tolera typos ("satianado" → "satinado") y
    // sinónimos ("dos centimetros" → "2cm"). Boost en SKU/código para que un
    // "A123" vaya al tope aunque el nombre no matchee.
    // Feb/2026: `strict: true` — "cinta 4 cm verde" NO debe traer "cinta 3mm rosa".
    return fuzzySearch(base, prodSearch, ["nombre", "sku", "codigo", "familia_key", "categoria_nombre", "categoria", "descripcion", "color"], { limit: 200, boostFields: ["sku", "codigo"], strict: true });
  }, [productos, prodCat, prodSearch]);

  // Agrupar por nombre-prefijo (mismo criterio que ve el usuario). Antes
  // agrupaba por `familia_key` primero → si el import de contenedor creaba
  // una familia_key NUEVA para colores que YA existían con otra familia_key,
  // aparecían DUPLICADOS en el dropdown. Feb/2026: ahora el prefijo del
  // nombre ("CINTA 4CM" en "CINTA 4CM - Curuba Pastel") es la clave primaria
  // y mergeamos todas las familias_key que compartan ese prefijo. Si no hay
  // guion en el nombre, fallback a familia_key para no romper legacy.
  const familiasProd = useMemo(() => {
    const mapa = new Map();
    for (const p of filteredProdsRaw) {
      const nombre = String(p.nombre || "").trim();
      const idx = nombre.lastIndexOf(" - ");
      const nombreFamiliaDelNombre = idx > 0 ? nombre.substring(0, idx).trim() : nombre;
      const color = idx > 0 ? nombre.substring(idx + 3).trim() : null;
      const famKey = (p.familia_key || "").trim().toUpperCase();
      // Feb/2026 fix: cuando hay guion en el nombre, agrupamos SIEMPRE por el
      // prefijo (ignoramos familia_key). Esto mergea duplicados que vienen de
      // importaciones distintas de un mismo producto con familia_key cambiada.
      const groupingKey = idx > 0
        ? nombreFamiliaDelNombre.toUpperCase()
        : (famKey || nombreFamiliaDelNombre.toUpperCase());
      if (!mapa.has(groupingKey)) {
        mapa.set(groupingKey, { nombreDisplay: nombreFamiliaDelNombre, items: [], famKey, groupingKey });
      }
      const bucket = mapa.get(groupingKey);
      // Protección anti-duplicado: si el mismo producto_id ya está, no lo añadas
      // dos veces (puede pasar con fuzzy filter que matchea el mismo prod por
      // nombre Y color Y familia_key).
      if (!bucket.items.some((it) => it.id === p.id)) {
        bucket.items.push({ ...p, _color: color });
      }
    }
    return Array.from(mapa.entries()).map(([_key, bucket]) => {
      // Oct/2026 fix: incluir TODAS las variantes de la familia, no solo las
      // que matchearon la búsqueda. Antes, buscar "0.7mm" en una familia
      // "CINTA SATIN 7mm" solo mostraba las variantes cuyo nombre contenía
      // "0.7mm" como string. Negro no aparecía porque su nombre decía
      // "7mm" sin "0.". Ahora, cuando ≥1 variante matchea, mostramos el
      // GRUPO completo para que el vendedor vea TODOS los colores — así
      // puede agregar manualmente los que la IA no reconoció.
      const yaEnBucket = new Set(bucket.items.map((it) => it.id));
      // Jun/2026: hermanos vía índice (antes recorría los 650+ productos por cada familia).
      const candidatos = [...(prodIndex.byPrefix.get(bucket.groupingKey) || []), ...(bucket.famKey ? (prodIndex.byFamKey.get(bucket.famKey) || []) : [])];
      const hermanos = [];
      for (const p of candidatos) {
        if (yaEnBucket.has(p.id)) continue;
        yaEnBucket.add(p.id);
        const nombre = String(p.nombre || "").trim();
        const idx = nombre.lastIndexOf(" - ");
        hermanos.push({ ...p, _color: idx > 0 ? nombre.substring(idx + 3).trim() : null, _notMatched: true });
      }
      // Jun/2026: colores en orden alfabético. Nov/2026: si la búsqueda coincide solo con ALGUNAS
      // variantes (ej. "papel coreano rojo"), esas van primero y el resto de colores después.
      const _cmp = (a, b) => String(a._color || a.nombre || "").localeCompare(String(b._color || b.nombre || ""), "es", { sensitivity: "base", numeric: true });
      const prods = hermanos.length > 0 ? [...bucket.items.sort(_cmp), ...hermanos.sort(_cmp)] : [...bucket.items].sort(_cmp);
      const disponibleTotal = prods.reduce((a, p) => a + Math.max(0, (p.stock_actual || 0) - (p.stock_reservado || 0)), 0);
      const primero = prods.find((p) => p.tiene_imagen || p.imagen_path) || prods[0];
      const primerConGuion = prods.find((p) => String(p.nombre || "").lastIndexOf(" - ") > 0);
      const familia = primerConGuion
        ? String(primerConGuion.nombre).substring(0, String(primerConGuion.nombre).lastIndexOf(" - ")).trim()
        : bucket.nombreDisplay;
      return { familia, prods, primero, disponibleTotal, matchedCount: bucket.items.length, totalCount: prods.length };
    });
  }, [filteredProdsRaw, prodIndex]);
  const familiasProdTotal = familiasProd.length;

  // Feb/2026: Al cerrar vía X / ESC / gesto atrás, si hay cambios sin guardar mostramos
  // modal con 3 opciones (Cancelar / Descartar / Guardar y salir). Al editar, si el
  // autoguardado falló o hay algo pendiente, SIEMPRE preguntar.
  const requestClose = () => {
    const hayCambiosSinGuardar = existing
      ? (autoSaveStatus === "saving" || autoSaveStatus === "error" || !!autoSaveTimerRef.current)
      : items.length > 0;
    if (hayCambiosSinGuardar) {
      setExitConfirm({ kind: "close" });
      return;
    }
    if (existing) flushAutoSaveAndClose(); else onClose();
  };
  // Jun/2026: en Android el gesto "deslizar desde el borde" dispara atrás y cerraba el
  // pedido sin preguntar. Lo interceptamos mientras el armador esté abierto.
  useBackTrap(true, requestClose);
  useBeforeUnloadGuard(existing ? autoSaveStatus !== "saved" && autoSaveStatus !== "idle" : items.length > 0);

  // Búsqueda difusa de clientes en el input del pedido (nombre / teléfono / ciudad / doc)
  const clientesFiltrados = (() => {
    if (!cliSearch.trim()) return clientes.slice(0, 30);
    return fuzzyFilter(clientes, cliSearch, ["nombre", "telefono", "ciudad", "email", "departamento", "documento"]).slice(0, 30);
  })();

  return (
    <>
    <Dialog open onOpenChange={(v) => {
      if (saving) return;
      if (!v) requestClose();
    }}>
      <DialogContent
        className="max-w-[95vw] xl:max-w-[1400px] max-h-[95vh] overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)]"
        data-testid="pedido-builder"
        onPointerDownOutside={(e) => {
          // Feb/2026: si el click fue dentro de OTRO dialog/popover (ej. IA, variant picker,
          // preview, toast, tooltip de Radix), lo ignoramos — no es un "click fuera" real.
          const t = e.detail?.originalEvent?.target || e.target;
          if (t && t.closest && (
            t.closest('[role="dialog"]') ||
            t.closest('[data-radix-popper-content-wrapper]') ||
            t.closest('[data-sonner-toaster]') ||
            t.closest('[data-radix-portal]')
          )) {
            e.preventDefault();
            return;
          }
          // Jun/2026: un click fuera JAMÁS cierra el pedido solo. Si hay items
          // (o cambios pendientes al editar) preguntamos; si no hay nada, también
          // bloqueamos el cierre accidental — el usuario cierra con la X.
          e.preventDefault();
          const hayCambios = existing
            ? (autoSaveStatus === "saving" || autoSaveStatus === "error" || !!autoSaveTimerRef.current || items.length > 0)
            : items.length > 0;
          if (hayCambios) {
            setExitConfirm({ kind: "close" });
          }
        }}
        onInteractOutside={(e) => {
          // Mismo filtro: no cerrar por interacciones con dialogs anidados o portales
          const t = e.detail?.originalEvent?.target || e.target;
          if (t && t.closest && (
            t.closest('[role="dialog"]') ||
            t.closest('[data-radix-popper-content-wrapper]') ||
            t.closest('[data-sonner-toaster]') ||
            t.closest('[data-radix-portal]')
          )) {
            e.preventDefault();
            return;
          }
          e.preventDefault();
        }}
        onFocusOutside={(e) => {
          // CRÍTICO: cuando el usuario hace focus en un input del IA dialog,
          // Radix normalmente lo captura y lo devuelve a su propio dialog.
          // Aquí: si el foco va al IA, respétalo (preventDefault = no re-captures).
          if (e.target?.closest?.("[data-ia-overlay]")) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <DialogTitle className="font-display text-2xl">{existing ? `Editar pedido ${existing.numero || ""}` : "Nuevo pedido"}</DialogTitle>
            {existing && esPostDespacho && (
              <div className="text-xs font-semibold flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800" data-testid="pedido-post-despacho-aviso">
                <Truck className="w-3 h-3" /> Pedido {existing.estado} · solo items y descuento · sin auto-guardado
              </div>
            )}
            {existing && !esPostDespacho && (
              <div
                className={`text-xs font-semibold flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-colors ${
                  autoSaveStatus === "saving" ? "bg-blue-100 text-blue-700" :
                  autoSaveStatus === "saved" ? "bg-emerald-100 text-emerald-700" :
                  autoSaveStatus === "error" ? "bg-rose-100 text-rose-700" :
                  "bg-muted text-muted-foreground"
                }`}
                data-testid="pedido-autosave-status"
                title={autoSaveError || (autoSaveLastAt ? `Último guardado: ${autoSaveLastAt.toLocaleTimeString("es-CO")}` : "Los cambios se guardan solos")}
              >
                {autoSaveStatus === "saving" && (<><RefreshCw className="w-3 h-3 animate-spin" /> Guardando…</>)}
                {autoSaveStatus === "saved" && (<><Check className="w-3 h-3" /> Guardado{autoSaveLastAt ? ` · ${autoSaveLastAt.toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" })}` : ""}</>)}
                {autoSaveStatus === "error" && (<><X className="w-3 h-3" /> Error al guardar</>)}
                {autoSaveStatus === "idle" && (<><Check className="w-3 h-3 opacity-60" /> Auto-guardado activo</>)}
              </div>
            )}
          </div>
        </DialogHeader>
        <form onSubmit={save} className="mt-4 space-y-4 min-w-0">
          {esPostDespacho && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="post-despacho-campos-bloqueados">
              Pedido ya {existing.estado}: solo se pueden cambiar <b>productos, cantidades, precios y descuento</b>. Cliente, vendedor, fecha y datos generales quedan bloqueados.
            </div>
          )}
          <fieldset disabled={esPostDespacho} className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${esPostDespacho ? "opacity-60 pointer-events-none" : ""}`}>
            <div>
              <Label>Cliente*</Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1 min-w-0">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 pointer-events-none" />
                  <Input
                    value={esPostDespacho ? (cliente?.nombre || existing?.cliente_nombre || "") : cliSearch}
                    readOnly={esPostDespacho}
                    onChange={(e) => setCliSearch(e.target.value)}
                    onFocus={() => setCliSearchOpen(true)}
                    onBlur={() => setTimeout(() => setCliSearchOpen(false), 200)}
                    placeholder={cliente ? cliente.nombre : (existing?.cliente_nombre || "Buscar cliente por nombre, teléfono o ciudad...")}
                    className="pl-9 h-11"
                    data-testid="pedido-cliente-search"
                  />
                  {cliSearchOpen && cliSearch && (
                    <div className="absolute z-20 mt-1 w-full bg-card border border-border rounded-md shadow-lg max-h-72 overflow-y-auto" data-testid="pedido-cliente-dropdown">
                      {clientesFiltrados.length === 0 ? (
                        <div className="px-3 py-3 text-xs text-muted-foreground">Sin resultados. ¿Crear un cliente nuevo?</div>
                      ) : clientesFiltrados.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => { setClienteId(c.id); setCliSearch(c.nombre); setCliSearchOpen(false); }}
                          className="w-full text-left px-3 py-2 hover:bg-muted/50 border-b border-border/40 last:border-0"
                          data-testid={`pedido-cliente-opt-${c.id}`}
                        >
                          <div className="text-sm font-medium">
                            {c.nombre} {c.segmento === "vip" && <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 font-bold ml-1">VIP</span>}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {[c.telefono, c.ciudad, c.departamento].filter(Boolean).join(" · ") || "Sin datos de contacto"}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button type="button" onClick={() => setQuickCliOpen(true)}
                  className="h-11 px-3 rounded-md border border-primary/40 bg-primary/5 text-primary text-xs font-medium hover:bg-primary/10 flex items-center gap-1 whitespace-nowrap flex-shrink-0"
                  data-testid="pedido-nuevo-cliente" title="Crear cliente nuevo">
                  <Plus className="w-3.5 h-3.5" /> Nuevo
                </button>
              </div>
              {cliente && (
                <div className="text-[11px] text-primary mt-1 flex items-center gap-2">
                  <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> {cliente.nombre}</span>
                  <button
                    type="button"
                    onClick={() => { setClienteId(""); setCliSearch(""); }}
                    className="text-muted-foreground hover:text-rose-600 text-[11px] underline"
                    data-testid="pedido-cliente-limpiar"
                  >
                    cambiar
                  </button>
                </div>
              )}
              {politica && <div className="text-xs text-primary mt-1">Aplicando política: <b>{politica.nombre}</b> ({politica.descuento_pct}% descuento base)</div>}

              {/* Feb/2026: Asignar pedido a otro vendedor (solo con permiso) */}
              {canAsignarVendedor && vendedoresAsignables.length > 0 && (
                <div className="mt-3 p-2.5 rounded-lg border border-amber-200 bg-amber-50/60" data-testid="pedido-vendedor-asignar-box">
                  <Label className="text-xs text-amber-900 font-semibold">Vendedor asignado</Label>
                  <select
                    value={vendedorAsigId || ""}
                    onChange={(e) => setVendedorAsigId(e.target.value)}
                    className="mt-1 w-full h-10 px-2 rounded-md border border-amber-300 bg-card text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    data-testid="pedido-vendedor-asignar-select"
                  >
                    <option value="">— Pedido a mi nombre —</option>
                    {vendedoresAsignables.map((v) => (
                      <option key={v.id} value={v.id} data-testid={`pedido-vendedor-opt-${v.id}`}>
                        {v.name}{v.is_me ? " (yo)" : ""}
                      </option>
                    ))}
                  </select>
                  <div className="text-[10px] text-amber-900/70 mt-1">
                    Este pedido quedará a nombre del vendedor seleccionado. Tu usuario quedará registrado como quien lo subió (auditoría).
                  </div>
                </div>
              )}
            </div>

            {/* Factura electrónica */}
            {cliente && (
              <div className={`p-3 rounded-lg border ${requiereFE && !cliente?.rut ? "border-rose-300 bg-rose-50" : "border-border bg-muted/30"}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={requiereFE}
                    onChange={(e) => setRequiereFE(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-primary cursor-pointer"
                    data-testid="requiere-factura-electronica"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <FileText className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />Requiere factura electrónica
                      {cliente?.rut ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 uppercase font-bold">RUT cargado</span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 uppercase font-bold">Sin RUT</span>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {requiereFE && !cliente?.rut ? (
                        <><AlertTriangle className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />El cliente <b>no tiene RUT cargado</b>. No podrás guardar el pedido con factura electrónica hasta subir el RUT. <a href={`/clientes?abrir=${cliente.id}`} target="_blank" rel="noreferrer" className="text-primary underline">Ir a ficha del cliente</a></>
                      ) : (
                        <>Márcalo si el cliente pedirá factura electrónica DIAN. Se validará que tenga RUT.</>
                      )}
                    </div>
                  </div>
                </label>
              </div>
            )}

            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Fecha</Label>
                <DateField value={fecha} onChange={setFecha} className="w-full mt-1 h-11" data-testid="pedido-fecha" />
              </div>
              <div>
                <Label>Ciudad</Label>
                <Input value={ciudad} onChange={(e) => setCiudad(e.target.value)} className="h-11 mt-1" placeholder={cliente?.ciudad || "Ciudad"} />
              </div>
              <div>
                <Label>Método</Label>
                <select value={metodo} onChange={(e) => setMetodo(e.target.value)} className="w-full h-11 mt-1 border border-border rounded-md px-2 bg-card text-sm">
                  <option value="">—</option>
                  {METODOS_PAGO.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
          </fieldset>

          {/* Productos */}
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-foreground">Productos ({items.length})</div>
              <button type="button" onClick={togglePrecios}
                className="text-[11px] font-semibold text-muted-foreground hover:text-primary flex items-center gap-1 px-2 py-1 rounded-md hover:bg-muted/50"
                data-testid="toggle-rango-precios"
                title="Muestra u oculta el rango sugerido y mínimo (para privacidad si el cliente ve tu pantalla)">
                {mostrarPrecios ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {mostrarPrecios ? "Ocultar rango" : "Ver rango"}
              </button>
            </div>
            {/* Buscador + filtro por categoría en dropdown compacto (evita desbordamiento del modal) */}
            <div className="flex gap-2 mb-2 items-stretch">
              <button
                type="button"
                onClick={() => setShowIntel(true)}
                className="h-10 px-3 rounded-md bg-gradient-to-r from-primary to-fuchsia-500 text-white font-semibold text-sm shadow hover:opacity-95 flex items-center gap-1.5 whitespace-nowrap"
                data-testid="pedido-intel-btn"
                title="Pegar WhatsApp o subir foto de remisión y que la IA arme el pedido"
              >
                <Sparkles className="w-4 h-4" />
                Montar con IA
              </button>
            </div>
            <div className="flex gap-2 mb-3 items-stretch">
              <div className="relative flex-1 min-w-0">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                <Input value={prodSearch} onChange={(e) => setProdSearch(e.target.value)} placeholder={catalogoCargando ? "Cargando catálogo…" : productos.length ? "Buscar por nombre, código (EF26-192), SKU o color..." : "Catálogo no disponible"} className="pl-9 h-10" data-testid="pedido-buscar-prod" />
                {!catalogoCargando && productos.length === 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 z-20 rounded-lg border border-rose-300 bg-rose-50 text-rose-700 text-xs px-3 py-2 flex items-center justify-between gap-2" data-testid="catalogo-error">
                    <span>No hay productos cargados{catalogoError ? ` (${catalogoError})` : ""}. El buscador no puede funcionar sin el catálogo.</span>
                    <button type="button" onClick={cargarCatalogo} className="font-bold underline whitespace-nowrap" data-testid="catalogo-reintentar">Reintentar</button>
                  </div>
                )}
              </div>
              {categoriasProd.length > 0 && (
                <select
                  value={prodCat}
                  onChange={(e) => setProdCat(e.target.value)}
                  className="h-10 border border-border rounded-md px-2 bg-card text-sm max-w-[180px] focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="pedido-cat-select"
                  title="Filtrar por categoría"
                >
                  <option value="">Todas · {productos.length}</option>
                  {categoriasProd.map(([cat, count]) => (
                    <option key={cat} value={cat}>{cat} · {count}</option>
                  ))}
                </select>
              )}
            </div>
            {/* Sugerencias de categorías según lo que escribe el vendedor (aparecen sobre el listado) */}
            {prodSearch && !prodCat && categoriasProd.length > 0 && (() => {
              const term = prodSearch.toLowerCase();
              const sugerencias = categoriasProd.filter(([cat]) => cat.toLowerCase().includes(term)).slice(0, 6);
              if (sugerencias.length === 0) return null;
              return (
                <div className="flex gap-1.5 flex-wrap mb-2" data-testid="pedido-cat-sugerencias">
                  <span className="text-[10px] uppercase font-bold text-muted-foreground/80 self-center mr-1">Categorías:</span>
                  {sugerencias.map(([cat, count]) => (
                    <button key={cat} type="button" onClick={() => setProdCat(cat)}
                      className="h-7 px-2.5 rounded-full text-[11px] font-semibold border border-primary/40 text-primary bg-primary/5 hover:bg-primary/10"
                      data-testid={`pedido-cat-sug-${cat.substring(0, 20)}`}
                    >{cat} · {count}</button>
                  ))}
                </div>
              );
            })()}
            <div className="mb-3">
              {(prodSearch || prodCat) && familiasProd.length > 0 && (
                <div className="w-full bg-card border border-border rounded-md shadow-sm max-h-[420px] overflow-y-auto">
                  <div className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-3 py-1.5 text-[11px] text-slate-600 dark:text-slate-200 flex items-center justify-between">
                    <span><b>{familiasProdTotal}</b> {familiasProdTotal === 1 ? "familia" : "familias"} · {filteredProdsRaw.length} productos</span>
                    {(prodSearch || prodCat) && (
                      <button type="button" onClick={() => { setProdSearch(""); setProdCat(""); setExpandedFamilia(null); }}
                        className="text-primary text-[11px] font-semibold hover:underline"
                        data-testid="pedido-limpiar-filtros"
                      >Limpiar filtros</button>
                    )}
                  </div>
                  {familiasProd.map((grupo) => {
                    const esGrupo = grupo.prods.length > 1;
                    const sinStock = grupo.disponibleTotal <= 0;
                    // Auto-expandir cuando el usuario está buscando: le mostramos TODOS
                    // los colores sin obligarlo a clickear el chevron (mar/2026).
                    const abierta = expandedFamilia === grupo.familia || (!!prodSearch && esGrupo);
                    // Chulito: al menos una variante (o el producto único) ya está en el pedido
                    const familiaAgregada = grupo.prods.some((p) => items.some((it) => it.producto_id === p.id));
                    const clickHandler = () => {
                      if (esGrupo) {
                        setExpandedFamilia(abierta ? null : grupo.familia);
                      } else if (!sinStock) {
                        addProducto(grupo.prods[0]);
                      }
                    };
                    const productoUnico = !esGrupo ? grupo.prods[0] : null;
                    const yaEnPedidoUnico = productoUnico ? items.some((it) => it.producto_id === productoUnico.id) : false;
                    return (
                      <div key={grupo.familia} className="border-b border-border/60 last:border-0">
                        <div className={`w-full flex items-center gap-2 pr-2 ${!esGrupo && sinStock ? "opacity-50 bg-muted/20" : familiaAgregada ? "bg-emerald-50/70" : ""}`}>
                        <div role="button" tabIndex={-1} title="Ver foto"
                          onClick={(e) => { if (grupo.primero.imagen_path || grupo.primero.tiene_imagen) { e.preventDefault(); e.stopPropagation(); setFotoPreview(esGrupo ? grupo.primero : grupo.prods[0]); } }}
                          data-testid={`prod-familia-foto-${grupo.primero.id}`}
                          className={`w-10 h-10 ml-3 rounded-md overflow-hidden flex-shrink-0 relative ${(grupo.primero.imagen_path || grupo.primero.tiene_imagen) ? "cursor-zoom-in hover:ring-2 hover:ring-primary" : ""} ${familiaAgregada ? "border-2 border-emerald-500 ring-1 ring-emerald-300" : "border border-border/60 bg-muted"}`}>
                          {grupo.primero.imagen_path || grupo.primero.tiene_imagen ? (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/catalogo/img/${grupo.primero.id}?w=200`}
                                 alt={grupo.familia} loading="lazy" decoding="async"
                                 className={`w-full h-full object-cover ${sinStock ? "grayscale" : ""}`}
                                 onError={(e) => (e.target.style.display = "none")} />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted-foreground/40 text-[10px] font-bold">SIN</div>
                          )}
                          {familiaAgregada && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shadow-md border-2 border-white" data-testid={`prod-familia-check-${grupo.familia.substring(0, 20)}`}>
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="3.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                            </div>
                          )}
                        </div>
                        <button type="button" onClick={clickHandler}
                          disabled={!esGrupo && sinStock}
                          className={`flex-1 min-w-0 text-left px-3 py-2 flex items-center gap-3 ${!esGrupo && sinStock ? "cursor-not-allowed" : familiaAgregada ? "hover:bg-emerald-100/70" : "hover:bg-muted/40"}`}
                          data-testid={`prod-familia-${grupo.familia.substring(0, 20)}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium flex items-center gap-2">
                              <span className={`min-w-0 break-words leading-tight text-[12px] sm:text-sm ${familiaAgregada ? "text-emerald-700" : ""}`}>{grupo.familia}</span>
                              {esGrupo && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-semibold uppercase flex items-center gap-0.5 flex-shrink-0">
                                  {grupo.prods.length} colores
                                </span>
                              )}
                              {sinStock && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 font-bold uppercase flex-shrink-0">Sin stock</span>
                              )}
                              {familiaAgregada && !esGrupo && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold uppercase flex-shrink-0">En pedido</span>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground flex justify-between">
                              <span className="truncate">{grupo.primero.categoria_nombre || grupo.primero.categoria || ""}</span>
                              <span className={sinStock ? "text-rose-600 font-semibold" : "text-muted-foreground"}>{grupo.disponibleTotal} disp. total</span>
                            </div>
                          </div>
                          {esGrupo && (
                            <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform flex-shrink-0 ${abierta ? "rotate-90" : ""}`} />
                          )}
                        </button>
                        {/* Input de cantidad inline SOLO para familias de un único producto —
                           permite al vendedor teclear la cantidad antes de agregarlo, sin
                           tener que bajar hasta la fila del pedido (mar/2026). */}
                        {productoUnico && !sinStock && !yaEnPedidoUnico && (() => {
                          const pendingRawU = pendingQty[productoUnico.id];
                          const pendingNumU = pendingRawU ? parseFloat(String(pendingRawU).replace(",", ".")) : 0;
                          const hasPendingU = isFinite(pendingNumU) && pendingNumU > 0;
                          const isFocusedU = focusedProductId === productoUnico.id;
                          const highlightU = isFocusedU || hasPendingU;
                          const prvU = pricingFor(productoUnico);
                          return (
                          <div className={`flex items-center gap-1 pl-2 pr-1 transition-all ${highlightU ? "bg-amber-100 ring-2 ring-amber-400 rounded-md p-1 shadow-md" : ""}`} onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={pendingQty[productoUnico.id] || ""}
                              onChange={(e) => setPendingQtyFor(productoUnico.id, e.target.value)}
                              onFocus={() => setFocusedProductId(productoUnico.id)}
                              onBlur={() => setFocusedProductId((prev) => (prev === productoUnico.id ? null : prev))}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  addProducto(productoUnico);
                                }
                              }}
                              placeholder={(productoUnico.unidad === "metro" || productoUnico.unidad === "yarda") ? "13.5" : "1"}
                              aria-label="Cantidad"
                              title={`Cantidad · Enter para agregar${(productoUnico.unidad === "metro" || productoUnico.unidad === "yarda") ? " · admite decimales (13.5)" : ""}`}
                              className={`${highlightU ? "w-20 h-10 text-lg font-bold border-2 border-amber-500 bg-white text-amber-900" : "w-14 h-8 text-xs border border-border bg-background"} rounded-md text-center tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all`}
                              data-testid={`prod-qty-${productoUnico.id}`}
                            />
                            {/* Feb/2026: precio inline opcional al agregar. Si está vacío usa el sugerido. */}
                            <div className="flex flex-col">
                              <input
                                type="text"
                                inputMode="numeric"
                                value={pendingPrice[productoUnico.id] || ""}
                                onChange={(e) => setPendingPriceFor(productoUnico.id, e.target.value.replace(/[^\d]/g, ""))}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addProducto(productoUnico); } }}
                                placeholder={`${prvU.sugerido || 0}`}
                                aria-label="Precio unitario"
                                title={`Precio · sug ${formatCOP(prvU.sugerido || 0)} · mín ${formatCOP(prvU.minimo || 0)}`}
                                className={`${highlightU ? "w-28 h-10 text-sm font-bold border-2 border-amber-500 bg-white text-amber-900" : "w-24 h-8 text-xs border border-border bg-background"} rounded-md text-center tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all`}
                                data-testid={`prod-price-${productoUnico.id}`}
                              />
                              <span className="text-[9px] text-muted-foreground text-center leading-none mt-0.5" title={`Sugerido ${formatCOP(prvU.sugerido || 0)} · Mínimo ${formatCOP(prvU.minimo || 0)}`}>
                                min {Math.round((prvU.minimo || 0) / 1000)}k · sug {Math.round((prvU.sugerido || 0) / 1000)}k
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => addProducto(productoUnico)}
                              className={`${highlightU ? "h-10 px-3 text-sm" : "h-8 px-2.5 text-xs"} rounded-md bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-all`}
                              title="Agregar al pedido"
                              data-testid={`prod-add-${productoUnico.id}`}
                            >
                              Agregar
                            </button>
                          </div>
                          );
                        })()}
                        {/* Feb/2026: edición inline cuando el producto único YA está en el pedido.
                            Muestra la cantidad actual con controles −/+/× para no obligar al
                            vendedor a scrollear entre 150 items del pedido. */}
                        {productoUnico && yaEnPedidoUnico && (() => {
                          const itemIdxU = items.findIndex((it) => it.producto_id === productoUnico.id);
                          if (itemIdxU < 0) return null;
                          const itemU = items[itemIdxU];
                          return (
                            <div className="flex items-center gap-1 pl-2 pr-1" onClick={(e) => e.stopPropagation()} data-testid={`prod-inline-edit-${productoUnico.id}`}>
                              <button
                                type="button"
                                onClick={() => updateItem(itemIdxU, { cantidad: Math.max(0, (Number(itemU.cantidad) || 0) - 1) })}
                                className="w-7 h-8 rounded-md border border-emerald-300 text-emerald-700 bg-white hover:bg-emerald-100 text-sm font-bold"
                                title="Restar 1"
                                data-testid={`prod-minus-${productoUnico.id}`}
                              >
                                −
                              </button>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={itemU.cantidad}
                                onChange={(e) => updateItem(itemIdxU, { cantidad: e.target.value.replace(",", ".") })}
                                aria-label="Cantidad en pedido"
                                title="Editá la cantidad directamente"
                                className="w-14 h-8 rounded-md border border-emerald-400 bg-white text-xs text-center tabular-nums font-semibold text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                data-testid={`prod-inline-qty-${productoUnico.id}`}
                              />
                              <button
                                type="button"
                                onClick={() => updateItem(itemIdxU, { cantidad: (Number(itemU.cantidad) || 0) + 1 })}
                                className="w-7 h-8 rounded-md border border-emerald-300 text-emerald-700 bg-white hover:bg-emerald-100 text-sm font-bold"
                                title="Sumar 1"
                                data-testid={`prod-plus-${productoUnico.id}`}
                              >
                                +
                              </button>
                              <button
                                type="button"
                                onClick={() => removeItem(itemIdxU)}
                                className="w-7 h-8 rounded-md border border-rose-300 text-rose-600 bg-white hover:bg-rose-50 text-xs"
                                title="Quitar del pedido"
                                data-testid={`prod-remove-${productoUnico.id}`}
                              >
                                ×
                              </button>
                            </div>
                          );
                        })()}
                        </div>
                        {/* Sub-lista de colores/variantes (solo cuando la familia está abierta) */}
                        {esGrupo && abierta && (
                          <div className="bg-slate-50/60 dark:bg-slate-800/60 pl-6 pr-3 pb-2" data-testid={`familia-open-${grupo.familia.substring(0, 20)}`}>
                            {(() => {
                              // Feb/2026: banner visible siempre que haya ítems agregables (con stock y no
                              // en el pedido aún). El vendedor puede editar cantidades y agregar en batch.
                              const agregables = grupo.prods.filter((p) => {
                                const disp = (p.stock_actual || 0) - (p.stock_reservado || 0);
                                return disp > 0 && !items.some((it) => it.producto_id === p.id);
                              });
                              const parseCant = (raw) => {
                                if (raw === undefined || raw === "") return 0;
                                const n = parseFloat(String(raw).replace(",", "."));
                                return isFinite(n) && n > 0 ? n : 0;
                              };
                              const conCantidad = agregables.filter((p) => parseCant(pendingQty[p.id]) > 0);
                              const conCant = conCantidad.length;
                              const totalCantPendiente = conCantidad.reduce((s, p) => s + parseCant(pendingQty[p.id]), 0);
                              if (agregables.length === 0) return null;
                              return (
                                <div className="flex items-center justify-between gap-2 py-2 px-2 mb-1 bg-primary/10 border border-primary/30 rounded-md sticky top-0 z-10">
                                  <div className="text-[11px] text-primary font-semibold">
                                    {conCant > 0
                                      ? `${conCant} ${conCant === 1 ? "ítem" : "ítems"} con cantidad · ${totalCantPendiente} un total`
                                      : `${agregables.length} ${agregables.length === 1 ? "color disponible" : "colores disponibles"} — escribe cantidades y agrégalos de una vez`}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => addTodosDelGrupo(grupo)}
                                    disabled={conCant === 0}
                                    className={`h-7 px-3 rounded-md text-[11px] font-bold flex items-center gap-1 ${conCant === 0 ? "bg-primary/40 text-white cursor-not-allowed" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}
                                    data-testid={`prod-agregar-todos-${grupo.familia.substring(0, 20)}`}
                                    title={conCant === 0 ? "Escribe una cantidad en al menos un ítem para agregar en batch" : "Agregar todos los ítems con cantidad al pedido de una sola vez"}
                                  >
                                    + Agregar {conCant > 0 ? `los ${conCant}` : "seleccionados"}
                                  </button>
                                </div>
                              );
                            })()}
                            {grupo.prods.map((p) => {
                              const disp = (p.stock_actual || 0) - (p.stock_reservado || 0);
                              const sin = disp <= 0;
                              const pr = pricingFor(p);
                              const itemIdx = items.findIndex((it) => it.producto_id === p.id);
                              const yaAgregado = itemIdx >= 0;
                              const itemActual = yaAgregado ? items[itemIdx] : null;
                              // Feb/2026: resaltar fila cuando el vendedor está escribiendo
                              // cantidad o enfoca el input, para que no se confunda al teclear
                              // en una lista larga. "Pending" = tiene cantidad escrita > 0.
                              const pendingRaw = pendingQty[p.id];
                              const pendingNum = pendingRaw ? parseFloat(String(pendingRaw).replace(",", ".")) : 0;
                              const hasPending = isFinite(pendingNum) && pendingNum > 0;
                              const isFocused = focusedProductId === p.id;
                              const highlight = !yaAgregado && !sin && (isFocused || hasPending);
                              return (
                                <div key={p.id} className={`flex items-center gap-1 py-0.5 px-1 border-b border-border/40 last:border-0 transition-all duration-150 ${sin ? "opacity-50" : yaAgregado ? "bg-emerald-50" : highlight ? "bg-amber-100 ring-2 ring-amber-400 border border-amber-400 rounded-md py-1.5 my-0.5 shadow-md relative z-10" : ""}`} data-testid={`prod-variante-row-${p.id}`}>
                                  <div role="button" tabIndex={-1} title="Ver foto"
                                    onClick={(e) => { if (p.imagen_path || p.tiene_imagen) { e.preventDefault(); e.stopPropagation(); setFotoPreview(p); } }}
                                    data-testid={`prod-variante-foto-${p.id}`}
                                    className={`${highlight ? "w-9 h-9" : "w-7 h-7"} ml-2 rounded overflow-hidden flex-shrink-0 border transition-all ${(p.imagen_path || p.tiene_imagen) ? "cursor-zoom-in hover:ring-2 hover:ring-primary" : ""} ${yaAgregado ? "border-emerald-500 ring-1 ring-emerald-500" : highlight ? "border-amber-500 ring-2 ring-amber-400" : "border-border/40 bg-muted"} relative`}>
                                    {p.imagen_path || p.tiene_imagen ? (
                                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/catalogo/img/${p.id}?w=200`}
                                           alt="" loading="lazy" decoding="async"
                                           className={`w-full h-full object-cover ${sin ? "grayscale" : ""}`} />
                                    ) : null}
                                    {yaAgregado && (
                                      <div className="absolute inset-0 bg-emerald-500/70 flex items-center justify-center">
                                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                      </div>
                                    )}
                                  </div>
                                  <button type="button" onClick={() => !sin && !yaAgregado && addProducto(p)}
                                    disabled={sin || yaAgregado}
                                    className={`flex-1 min-w-0 text-left py-1 px-2 rounded flex items-center gap-2 ${sin ? "cursor-not-allowed" : yaAgregado ? "cursor-default" : "hover:bg-white"}`}
                                    data-testid={`prod-variante-${p.id}`}
                                  >
                                    <div className="flex-1 min-w-0 text-xs">
                                      <div>
                                        <span className={`${highlight ? "text-base font-black text-amber-900" : "font-medium"} ${yaAgregado ? "text-emerald-700" : ""} transition-all`} data-testid={`prod-variante-nombre-${p.id}`}>{p._color || p.nombre}</span>
                                        <span className={`${highlight ? "text-amber-800 font-bold" : "text-muted-foreground"} ml-2 font-mono`}>{p.codigo || p.sku || "sin código"}</span>
                                        {p.oculto_venta && <span className="ml-2 text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold" title="Oculto a la venta: solo el director lo ve" data-testid={`prod-oculto-${p.id}`}>oculto</span>}
                                      </div>
                                      {p.descripcion && (
                                        <div className={`${highlight ? "text-sm font-semibold text-amber-900" : "text-[10px] text-muted-foreground/80"} break-words mt-0.5 transition-all`} title={p.descripcion}>
                                          {p.descripcion}
                                        </div>
                                      )}
                                    </div>
                                    <div className={`${highlight ? "text-sm font-bold" : "text-[11px]"} flex-shrink-0 flex items-center gap-1.5 transition-all`}>
                                      <span className={sin ? "text-rose-600 font-semibold" : highlight ? "text-amber-900" : "text-muted-foreground"}>{sin ? "Sin stock" : `${disp} disp.`}</span>
                                      {mostrarPrecios && !sin && <span className={highlight ? "text-amber-900 font-bold" : "text-muted-foreground"}>{formatCOP(pr.sugerido)}</span>}
                                    </div>
                                  </button>
                                  {/* Feb/2026: edición inline. Cuando ya está en el pedido, permitir
                                      modificar cantidad DESDE el buscador — evita scrollear entre 150 items. */}
                                  {yaAgregado && itemActual && (
                                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()} data-testid={`prod-variante-inline-edit-${p.id}`}>
                                      <button
                                        onClick={() => { const nv = (Number(itemActual.cantidad) || 0) - 1; if (nv <= 0) removeItem(itemIdx); else updateItem(itemIdx, { cantidad: nv }); }}
                                        onClick={() => updateItem(itemIdx, { cantidad: Math.max(0, (Number(itemActual.cantidad) || 0) - 1) })}
                                        className="w-6 h-7 rounded-md border border-emerald-300 text-emerald-700 bg-white hover:bg-emerald-100 text-sm font-bold"
                                        title="Restar 1 (en 0 se quita del pedido)"
                                        data-testid={`prod-variante-minus-${p.id}`}
                                      >
                                        −
                                      </button>
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        value={itemActual.cantidad}
                                        onChange={(e) => updateItem(itemIdx, { cantidad: e.target.value.replace(",", ".") })}
                                        aria-label="Cantidad en pedido"
                                        title="Editá la cantidad directamente"
                                        className="w-14 h-7 rounded-md border border-emerald-400 bg-white text-xs text-center tabular-nums font-semibold text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                        data-testid={`prod-variante-inline-qty-${p.id}`}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => updateItem(itemIdx, { cantidad: (Number(itemActual.cantidad) || 0) + 1 })}
                                        className="w-6 h-7 rounded-md border border-emerald-300 text-emerald-700 bg-white hover:bg-emerald-100 text-sm font-bold"
                                        title="Sumar 1"
                                        data-testid={`prod-variante-plus-${p.id}`}
                                      >
                                        +
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => removeItem(itemIdx)}
                                        className="w-6 h-7 rounded-md border border-rose-300 text-rose-600 bg-white hover:bg-rose-50 text-xs"
                                        title="Quitar del pedido"
                                        data-testid={`prod-variante-remove-${p.id}`}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  )}
                                  {!sin && !yaAgregado && (() => {
                                    const prv = pricingFor(p);
                                    return (
                                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        value={pendingQty[p.id] || ""}
                                        onChange={(e) => setPendingQtyFor(p.id, e.target.value)}
                                        onFocus={() => setFocusedProductId(p.id)}
                                        onBlur={() => setFocusedProductId((prev) => (prev === p.id ? null : prev))}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            e.preventDefault();
                                            addProducto(p);
                                          }
                                        }}
                                        placeholder={(p.unidad === "metro" || p.unidad === "yarda") ? "13.5" : "1"}
                                        aria-label="Cantidad"
                                        title={`Cantidad · Enter para agregar${(p.unidad === "metro" || p.unidad === "yarda") ? " · admite decimales (13.5)" : ""}`}
                                        className={`${highlight ? "w-16 h-10 text-lg font-bold border-2 border-amber-500 bg-white ring-2 ring-amber-300 text-amber-900" : "w-12 h-7 text-xs border border-border bg-background"} rounded-md text-center tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all`}
                                        data-testid={`prod-variante-qty-${p.id}`}
                                      />
                                      {/* Feb/2026: precio inline opcional. Si lo escribe, sobrescribe el sugerido al agregar. */}
                                      <div className="flex flex-col">
                                        <input
                                          type="text"
                                          inputMode="numeric"
                                          value={pendingPrice[p.id] || ""}
                                          onChange={(e) => setPendingPriceFor(p.id, e.target.value.replace(/[^\d]/g, ""))}
                                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addProducto(p); } }}
                                          placeholder={`${prv.sugerido || 0}`}
                                          aria-label="Precio unitario"
                                          title={`Precio · sug ${formatCOP(prv.sugerido || 0)} · mín ${formatCOP(prv.minimo || 0)}`}
                                          className={`${highlight ? "w-24 h-10 text-sm font-bold border-2 border-amber-500 bg-white ring-2 ring-amber-300 text-amber-900" : "w-20 h-7 text-[11px] border border-border bg-background"} rounded-md text-center tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all`}
                                          data-testid={`prod-variante-price-${p.id}`}
                                        />
                                        <span className="text-[8px] text-muted-foreground text-center leading-none mt-0.5" title={`Sugerido ${formatCOP(prv.sugerido || 0)} · Mínimo ${formatCOP(prv.minimo || 0)}`}>
                                          min {Math.round((prv.minimo || 0) / 1000)}k · sug {Math.round((prv.sugerido || 0) / 1000)}k
                                        </span>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => addProducto(p)}
                                        className={`${highlight ? "h-10 px-3 text-sm" : "h-7 px-2 text-[11px]"} rounded-md bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-all`}
                                        title="Agregar al pedido"
                                        data-testid={`prod-variante-add-${p.id}`}
                                      >
                                        +
                                      </button>
                                    </div>
                                    );
                                  })()}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {items.length === 0 && (
              <div className="text-center py-8 text-sm text-muted-foreground/70">Busca y agrega productos al pedido.</div>
            )}
            {items.length > 0 && (<>
              {itemsBajoMinimo.length > 0 && (
                <div className="mb-2 flex items-center gap-2 flex-wrap rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900" data-testid="bajo-minimo-banner">
                  <span className="font-semibold">
                    <AlertTriangle className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />{itemsBajoMinimo.length} ítem{itemsBajoMinimo.length === 1 ? "" : "s"} bajo el precio mínimo
                    {itemsBajoMinimo.some((x) => x.sinMotivo) ? ` · ${itemsBajoMinimo.filter((x) => x.sinMotivo).length} sin motivo` : " · todos con motivo"}
                  </span>
                  <div className="flex gap-1 flex-wrap ml-auto">
                    {itemsBajoMinimo.slice(0, 12).map(({ idx, sinMotivo }) => (
                      <button key={idx} type="button" onClick={() => irAlItem(idx)}
                        className={`h-6 min-w-[28px] px-1.5 rounded-full text-[11px] font-bold border ${sinMotivo ? "bg-rose-600 text-white border-rose-600" : "bg-white text-rose-700 border-rose-300"}`}
                        title={sinMotivo ? "Falta motivo — ir al ítem" : "Con motivo — ir al ítem"} data-testid={`bajo-minimo-ir-${idx}`}>#{idx + 1}</button>
                    ))}
                    {itemsBajoMinimo.length > 12 && <span className="text-[11px]">+{itemsBajoMinimo.length - 12}</span>}
                  </div>
                </div>
              )}
              <div className="divide-y divide-slate-100">
                {items.map((it, idx) => {
                  const p = productosById.get(it.producto_id);
                  const disponible = p ? (p.stock_actual || 0) - (p.stock_reservado || 0) : 0;
                  const pr = p ? pricingFor(p) : { sugerido: 0, minimo: 0 };
                  const precio = Number(it.precio_unitario) || 0;
                  const bajoMin = precio < pr.minimo;
                  const sub = Math.round(precio * (Number(it.cantidad) || 0));
                  const esUltimo = ultimoAgregado && it.producto_id === ultimoAgregado;
                  const rowCls = editandoItemIdx === idx
                    ? "bg-amber-50 ring-2 ring-amber-300 px-2 -mx-1"
                    : bajoMin
                      ? (it.motivo_excepcion ? "bg-rose-50/50 border-l-4 border-l-rose-300 pl-2 -ml-1" : "bg-rose-50 border-l-4 border-l-rose-500 pl-2 -ml-1")
                      : esUltimo ? "bg-primary/5 ring-2 ring-primary/40 px-2 -mx-1" : "";
                  return (
                    <div
                      key={`${it.producto_id}-${it.es_surtido ? "sur" : "u"}-${idx}`}
                      className={`py-1.5 transition-colors rounded-lg relative ${rowCls}`}
                      data-pid={it.producto_id}
                      data-recien-agregado={esUltimo ? "1" : undefined}
                      data-testid={`item-row-${idx}`}
                      data-bajo-minimo={bajoMin ? (it.motivo_excepcion ? "con-motivo" : "sin-motivo") : undefined}
                    >
                      {/* Feb/2026: Layout responsive — en móvil apila nombre arriba y
                          controles abajo para evitar overflow. En md+ usa grid-cols-12. */}
                      <div className="flex flex-col md:grid md:grid-cols-12 md:gap-2 md:items-center gap-2">
                        <div className="md:col-span-5 min-w-0">
                          <button
                            type="button"
                            onClick={() => p && setPreviewItem({ producto: p, item: it, pricing: pr })}
                            className="text-left w-full group min-w-0"
                            data-testid={`item-preview-open-${idx}`}
                            title="Ver detalle del producto"
                          >
                            <div className="text-sm font-medium text-foreground flex items-center gap-2 group-hover:text-primary transition-colors min-w-0">
                              <span className="min-w-0 flex-1 break-words leading-tight text-[12px] sm:text-sm">{p?.nombre || "Producto"}</span>
                              {it.es_surtido && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary text-primary-foreground uppercase font-bold flex-shrink-0" data-testid={`item-surtido-badge-${idx}`}>Surtido</span>
                              )}
                              <Eye className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity flex-shrink-0" />
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {it.es_surtido ? (
                                <>Bodega definirá colores.{mostrarPrecios && <> Precio del padre: {formatCOP(pr.sugerido)}.</>}</>
                              ) : (
                                <>
                                  {p?.codigo || p?.sku || ""} · {p?.unidad || ""} · disp. {disponible}
                                </>
                              )}
                            </div>
                            {pr.memo && !it.es_surtido && (
                              <div className="text-[11px] text-primary mt-0.5" data-testid={`item-precio-memo-${idx}`}>
                                Precio recordado con este cliente ({formatCOP(pr.memo.ultimo_precio)}
                                {pr.memo.ultima_fecha ? ` · ${_diasDesde(pr.memo.ultima_fecha)}` : ""})
                              </div>
                            )}
                          </button>
                          {!it.es_surtido && pr.minimo > 0 && (
                            <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 flex-wrap mt-0.5">
                              <span>Usar:</span>
                              <button type="button" onClick={() => updateItem(idx, { precio_unitario: pr.sugerido })}
                                className={`underline decoration-dotted underline-offset-2 hover:text-primary ${precio === pr.sugerido ? "text-primary font-semibold" : ""}`}
                                title="Click para poner el precio sugerido" data-testid={`item-usar-sugerido-${idx}`}>sug {formatCOP(pr.sugerido)}</button>
                              <span>·</span>
                              <button type="button" onClick={() => updateItem(idx, { precio_unitario: pr.minimo })}
                                className={`underline decoration-dotted underline-offset-2 hover:text-rose-700 ${precio === pr.minimo ? "text-rose-700 font-semibold" : ""}`}
                                title="Click para poner el precio mínimo autorizado" data-testid={`item-usar-minimo-${idx}`}>mín {formatCOP(pr.minimo)}</button>
                            </div>
                          )}
                        </div>
                        {/* Controles: en móvil fila compacta con qty+precio+subtotal+trash */}
                        <div className="md:col-span-2 flex md:block items-center gap-2">
                          <span className="md:hidden text-[10px] font-bold uppercase text-muted-foreground w-12 flex-shrink-0">Cant.</span>
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={it.cantidad}
                            onChange={(e) => updateItem(idx, { cantidad: e.target.value.replace(",", ".") })}
                            onFocus={() => setEditandoItemIdx(idx)}
                            onBlur={() => setEditandoItemIdx((cur) => (cur === idx ? null : cur))}
                            className={`h-9 md:h-8 text-right tabular-nums transition-colors flex-1 md:flex-none min-w-0 ${editandoItemIdx === idx ? "ring-2 ring-amber-400 border-amber-400" : ""}`}
                            data-testid={`item-cant-${idx}`}
                          />
                        </div>
                        <div className="md:col-span-3">
                          <div className="flex items-center gap-2 md:block">
                            <span className="md:hidden text-[10px] font-bold uppercase text-muted-foreground w-12 flex-shrink-0">Precio</span>
                            <div className="flex flex-col sm:flex-row gap-1 flex-1 min-w-0">
                              <MoneyInput
                                value={it.precio_unitario}
                                onValueChange={(v) => updateItem(idx, { precio_unitario: v })}
                                onFocus={() => setEditandoItemIdx(idx)}
                                onBlur={() => setEditandoItemIdx((cur) => (cur === idx ? null : cur))}
                                className={`h-9 text-right flex-1 min-w-0 transition-colors ${bajoMin ? "border-rose-400 bg-rose-50" : ""} ${editandoItemIdx === idx ? "ring-2 ring-amber-400 border-amber-400" : ""}`}
                                data-testid={`item-precio-${idx}`}
                              />
                              {(() => {
                                // Feb/2026: aplicar el mismo precio a todos los items del mismo grupo
                                // (familia_key coincidente). Respeta mínimo de cada uno.
                                if (!p || items.length < 2) return null;
                                const famKey = grupoPrecioKey(p);
                                if (!famKey) return null;
                                if ((famKeyCounts.get(famKey) || 0) < 2) return null;
                                return (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const precioBase = Number(it.precio_unitario) || 0;
                                      let aplicados = 0;
                                      let ajustados = 0;
                                      setItems((prev) => prev.map((otro, oIdx) => {
                                        if (oIdx === idx) return otro;
                                        const otroP = productosById.get(otro.producto_id);
                                        if (!otroP || grupoPrecioKey(otroP) !== famKey) return otro;
                                        const otroPr = pricingFor(otroP);
                                        let precioFinal = precioBase;
                                        let motivo = otro.motivo_excepcion || "";
                                        if (precioBase < otroPr.minimo && otroPr.minimo > 0) {
                                          motivo = motivo || `Precio unificado con "${p.nombre}" (bajo mínimo)`;
                                          ajustados++;
                                        }
                                        aplicados++;
                                        return { ...otro, precio_unitario: precioBase, motivo_excepcion: motivo };
                                      }));
                                      toast.success(
                                        `Precio ${formatCOP(precioBase)} aplicado a ${aplicados} ítem${aplicados === 1 ? "" : "s"} del grupo${ajustados > 0 ? ` (${ajustados} bajo mínimo — requerirá motivo)` : ""}`
                                      );
                                    }}
                                    className="h-9 px-2 rounded-md border border-primary/30 text-primary text-[10px] font-bold hover:bg-primary/5 whitespace-nowrap flex-shrink-0"
                                    title={`Aplicar este precio a los otros ${(famKeyCounts.get(famKey) || 1) - 1} ítems del grupo "${famKey}"`}
                                    data-testid={`item-precio-aplicar-grupo-${idx}`}
                                  >
                                    Aplicar a los {(famKeyCounts.get(famKey) || 1) - 1}
                                  </button>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                        <div className="md:col-span-1 flex md:block items-center justify-between gap-2">
                          <span className="md:hidden text-[10px] font-bold uppercase text-muted-foreground">Subtotal</span>
                          <span className="text-sm font-semibold tabular-nums md:text-right md:block">{formatCOP(sub)}</span>
                        </div>
                        <div className="md:col-span-1 md:text-right flex md:block justify-end">
                          <button type="button" onClick={() => { setReemplazarIdx(idx); setReemplazarSearch(""); }}
                            className="p-2 flex-shrink-0 text-muted-foreground/60 hover:text-primary"
                            title="Cambiar este ítem por otro producto (sin borrarlo)"
                            data-testid={`item-reemplazar-btn-${idx}`}>
                            <Search className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => setNotaAbiertaIdx((cur) => (cur === idx ? null : idx))}
                            className={`p-2 flex-shrink-0 ${it.notas ? "text-amber-600" : "text-muted-foreground/60 hover:text-amber-600"}`}
                            title={it.notas ? `Nota: ${it.notas}` : "Agregar nota para bodega (ej. recargo en dorado)"}
                            data-testid={`item-nota-btn-${idx}`}>
                            <StickyNote className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => removeItem(idx)} className="p-2 text-muted-foreground/70 hover:text-rose-600 flex-shrink-0" data-testid={`item-remove-${idx}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                      {(notaAbiertaIdx === idx || it.notas) && (
                        <div className="mt-1 pl-1 flex items-center gap-2" data-testid={`item-nota-row-${idx}`}>
                          <StickyNote className="w-3 h-3 text-amber-600 flex-shrink-0" />
                          {notaAbiertaIdx === idx ? (
                            <Input autoFocus value={it.notas || ""} onChange={(e) => updateItem(idx, { notas: e.target.value })}
                              onBlur={() => setNotaAbiertaIdx((cur) => (cur === idx ? null : cur))}
                              onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setNotaAbiertaIdx(null); }}
                              className="h-7 text-xs bg-amber-50/60 border-amber-200" placeholder="Nota para bodega / remisión (ej. Rojo 2, Rosa 2 · recargo en dorado)" data-testid={`item-nota-input-${idx}`} />
                          ) : (
                            <button type="button" onClick={() => setNotaAbiertaIdx(idx)} className="text-[11px] text-amber-700 text-left truncate hover:underline" data-testid={`item-nota-text-${idx}`}>{it.notas}</button>
                          )}
                        </div>
                      )}
                      {bajoMin && (
                        <div className="mt-2 p-2 bg-rose-50 border border-rose-200 rounded-md">
                          <div className="text-xs text-rose-800 font-medium mb-1 flex items-center gap-2 flex-wrap">
                            <span><AlertTriangle className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />Ítem #{idx + 1} · Precio bajo mínimo autorizado. Requiere aprobación del director.</span>
                            <button type="button" onClick={() => updateItem(idx, { precio_unitario: pr.minimo, motivo_excepcion: "" })}
                              className="h-6 px-2 rounded-full bg-white border border-rose-300 text-rose-700 font-bold hover:bg-rose-100"
                              title="Subir el precio al mínimo (ya no requiere aprobación)" data-testid={`item-subir-a-minimo-${idx}`}>Subir a mín {formatCOP(pr.minimo)}</button>
                          </div>
                          <Input value={it.motivo_excepcion || ""} onChange={(e) => updateItem(idx, { motivo_excepcion: e.target.value })} className="h-8 text-sm bg-card" placeholder="Motivo de la excepción (obligatorio)" data-testid={`item-motivo-exc-${idx}`} />
                        </div>
                      )}
                      {/* Toggle surtido: cliente pide "colores variados" y el operador reparte al despachar */}
                      <div className="mt-1 flex items-center gap-2 pl-1">
                        <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer select-none" title="El cliente eligió colores/variantes al azar. El operador repartirá al despachar.">
                          <input
                            type="checkbox"
                            checked={!!it.es_surtido}
                            onChange={(e) => updateItem(idx, { es_surtido: e.target.checked })}
                            className="w-3.5 h-3.5 accent-primary"
                            data-testid={`item-surtido-toggle-${idx}`}
                          />
                          <span className={it.es_surtido ? "text-primary font-semibold" : ""}>
                            🎨 Surtido — operador escoge colores al despachar
                          </span>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>)}
          </div>

          {/* Oct/2026 v4: Resumen por familia — PRIORIZA familia_key como
              clave de agrupación (es el campo canónico que une variantes).
              Como display, muestra el prefijo del nombre antes del PRIMER " - ".
              Fallback: si no hay familia_key, usa el prefijo del nombre. */}
          {items.length >= 1 && (() => {
            const prefijoNombre = (nombre) => {
              const n = String(nombre || "").trim();
              const idx = n.indexOf(" - ");
              return (idx > 0 ? n.slice(0, idx) : n).trim().replace(/\s{2,}/g, " ");
            };
            const porRef = new Map();
            for (const it of items) {
              const prod = productosById.get(it.producto_id);
              if (!prod) continue;
              const famKey = String(prod.familia_key || "").trim().toUpperCase();
              const prefijo = prefijoNombre(prod.nombre);
              // Clave canónica: familia_key si existe, si no el prefijo del nombre.
              // Esto hace que todas las variantes de color de un mismo producto
              // (CURUBA, KAKI, MENTA, etc.) se agrupen bajo UNA sola línea.
              const refKey = famKey || prefijo.toUpperCase() || "SIN_REF";
              if (!porRef.has(refKey)) {
                porRef.set(refKey, {
                  refKey,
                  refNombre: prefijo || prod.familia_key || "Sin referencia",
                  cantidadTotal: 0,
                  subtotalTotal: 0,
                });
              }
              const r = porRef.get(refKey);
              const cant = Number(it.cantidad) || 0;
              const precio = Number(it.precio_unitario) || 0;
              r.cantidadTotal += cant;
              r.subtotalTotal += cant * precio;
            }
            const refs = Array.from(porRef.values());
            if (refs.length === 0) return null;
            return (
              <details open className="rounded-xl border-2 border-primary/20 bg-primary/5" data-testid="pedido-subtotales-por-ref">
                <summary className="cursor-pointer select-none p-3 text-xs font-bold uppercase tracking-wider text-primary flex items-center gap-1.5 marker:hidden [&::-webkit-details-marker]:hidden">
                  <Layers className="w-3.5 h-3.5" />
                  Resumen por familia
                  <span className="text-[9px] opacity-60 normal-case tracking-normal ml-1">v3</span>
                  <span className="ml-auto text-[10px] font-semibold text-muted-foreground normal-case tracking-normal">
                    {refs.length} {refs.length === 1 ? "referencia" : "referencias"} · click para colapsar
                  </span>
                </summary>
                <div className="px-3 pb-3 space-y-1.5">
                  {refs.map((r, i) => (
                    <div key={i} className="bg-white dark:bg-slate-900 rounded-lg p-2 border border-primary/15 flex items-baseline justify-between gap-2">
                      <span className="font-semibold text-[12px] sm:text-sm break-words min-w-0" title={r.refNombre}>{r.refNombre}</span>
                      <span className="text-sm font-bold tabular-nums text-primary flex-shrink-0">
                        {r.cantidadTotal} un · {formatCOP(r.subtotalTotal)}
                      </span>
                    </div>
                  ))}
                </div>
              </details>
            );
          })()}

          {/* Totales */}
          <div className="flex justify-end">
            <div className="w-72 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="tabular-nums">{formatCOP(subtotal)}</span></div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Descuento</span>
                <MoneyInput value={descuento} onValueChange={setDescuento} className="h-8 w-32 text-right" />
              </div>
              <div className="flex justify-between border-t border-border pt-2 text-base"><span className="font-semibold">Total</span><span className="font-semibold tabular-nums" data-testid="pedido-total">{formatCOP(total)}</span></div>
            </div>
          </div>

          {/* Widget de cupo de crédito en vivo */}
          <CupoLivePreview cupoInfo={cupoInfo} total={total} existingPedidoSaldo={existing?.saldo ?? existing?.total} />

          <div>
            <Label>Notas</Label>
            <textarea value={notas} disabled={esPostDespacho} onChange={(e) => setNotas(e.target.value)} className={`w-full mt-1 border border-border rounded-md px-3 py-2 text-sm min-h-[60px] ${esPostDespacho ? "opacity-60" : ""}`} placeholder="Observaciones para el pedido..." />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={existing ? flushAutoSaveAndClose : onClose} data-testid="pedido-cancel">
              {existing ? "Cerrar" : "Cancelar"}
            </Button>
            <Button type="submit" disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="pedido-save">
              {saving ? "Guardando..." : existing ? "Guardar y cerrar" : "Crear pedido (borrador)"}
            </Button>
          </div>
        </form>

        {/* Oct/2026: FAB para pedidos largos — flota en la esquina inferior
            derecha y scrolea instantáneamente al buscador de productos (que
            está arriba del todo). Se muestra solo cuando el pedido tiene >=5
            items para no estorbar en pedidos pequeños.
            Feb/2026: se renderiza vía Portal al body porque DialogContent usa
            transform:translate que convierte a `position:fixed` en relativo al
            modal, haciendo que la lupa quedara "atrapada" dentro del scroll. */}
        {items.length >= 5 && createPortal(
          <button
            type="button"
            onClick={() => {
              const input = document.querySelector('[data-testid="pedido-buscar-prod"]');
              if (input) {
                input.scrollIntoView({ behavior: "smooth", block: "center" });
                setTimeout(() => input.focus(), 400);
              }
            }}
            className="fixed bottom-5 right-5 md:bottom-8 md:right-8 z-[80] h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-2xl hover:scale-105 active:scale-95 transition flex items-center justify-center ring-4 ring-primary/20"
            title={`Buscar y agregar producto (${items.length} items en el pedido)`}
            data-testid="pedido-fab-search"
          >
            <Search className="w-6 h-6" />
          </button>,
          document.body
        )}
      </DialogContent>
      <QuickClienteModal
        open={quickCliOpen}
        onOpenChange={setQuickCliOpen}
        onCreated={(cli) => { setClientes((prev) => [...prev, cli]); setClienteId(cli.id); }}
      />

      {/* Selector de variante cuando el producto elegido es un padre */}
      <Dialog open={variantPickerOpen} onOpenChange={(v) => { setVariantPickerOpen(v); if (!v) { setVariantPadre(null); setVariantList([]); } }}>
        <DialogContent className="max-w-lg" data-testid="variant-picker-modal">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Layers className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase text-primary tracking-wider">Elegir variante</div>
                <DialogTitle className="font-display text-2xl mt-1">{variantPadre?.nombre}</DialogTitle>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Este producto tiene variantes agrupadas. Elige cuál agregar al pedido, o marca <b>Surtido</b> si el cliente quiere que la bodega elija los colores.</p>
          </DialogHeader>
          {/* Opción Surtido — "bodega decide" */}
          {!variantLoading && variantList.length > 0 && (
            <button
              type="button"
              onClick={addSurtido}
              className="mt-3 w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-dashed border-primary/50 hover:border-primary hover:bg-primary/5 transition-all text-left"
              data-testid="variant-surtido-opt"
            >
              <div className="w-10 h-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
                <span className="text-lg">🎨</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-foreground">Surtido — la bodega decide los colores</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  Ideal cuando el cliente dice "mándemelos surtidos". Precio: {formatCOP(variantPadre?.precio_sugerido || 0)}. Stock agregado: {variantList.reduce((s, v) => s + Math.max(0, (v.stock_actual || 0) - (v.stock_reservado || 0)), 0)}.
                </div>
              </div>
              <span className="text-[10px] px-2 py-1 rounded-full bg-primary/20 text-primary uppercase font-bold">Recomendado</span>
            </button>
          )}
          <div className="mt-4 max-h-[45vh] overflow-y-auto border border-border rounded-lg divide-y divide-border/60">
            {variantLoading && <div className="p-6 text-center text-sm text-muted-foreground">Cargando variantes...</div>}
            {!variantLoading && variantList.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground">Este producto padre aún no tiene variantes agrupadas.</div>
            )}
            {variantList.map((v) => {
              const disp = (v.stock_actual || 0) - (v.stock_reservado || 0);
              const yaAgregado = items.some((it) => it.producto_id === v.id);
              const sinStock = disp <= 0;
              const disabled = yaAgregado || sinStock;
              return (
                <button key={v.id} type="button" onClick={() => !disabled && addVariante(v)} disabled={disabled}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-accent"}`}
                  data-testid={`variant-opt-${v.id}`}>
                  <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold flex-shrink-0">
                    {(v.variante_label || v.nombre || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] sm:text-sm font-medium text-foreground break-words leading-tight">{v.variante_label || v.nombre}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {(v.codigo || v.sku) && <span className="font-mono">{v.codigo || v.sku}</span>}
                      <span className="ml-2">Sug: {formatCOP(v.precio_sugerido || 0)}</span>
                      <span className={`ml-2 ${sinStock ? "text-rose-600 font-semibold" : ""}`}>Stock: {disp}</span>
                    </div>
                  </div>
                  {sinStock && <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 uppercase font-bold">Sin stock</span>}
                  {yaAgregado && !sinStock && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/20 text-primary uppercase font-semibold">agregado</span>}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview grande del item con imagen e info */}
      <Dialog open={reemplazarIdx !== null} onOpenChange={(v) => !v && setReemplazarIdx(null)}>
        <DialogContent className="max-w-xl" data-testid="reemplazar-item-modal">
          {reemplazarIdx !== null && items[reemplazarIdx] && (() => {
            const actual = items[reemplazarIdx];
            const pActual = productosById.get(actual.producto_id);
            const enPedido = new Set(items.map((it) => it.producto_id));
            const base = productos.filter((p) => !p.es_padre && !enPedido.has(p.id));
            const resultados = reemplazarSearch.trim().length >= 2
              ? fuzzySearch(base, reemplazarSearch, ["nombre", "sku", "codigo", "familia_key", "categoria_nombre", "categoria", "descripcion", "color"], { limit: 40, boostFields: ["sku", "codigo"], strict: true })
              : [];
            const reemplazar = (nuevo) => {
              const disp = Math.max(0, (nuevo.stock_actual || 0) - (nuevo.stock_reservado || 0));
              if (disp <= 0) { toast.error(`${nuevo.nombre} sin stock disponible`); return; }
              const pr = pricingFor(nuevo);
              const cant = Math.min(Number(actual.cantidad) || 1, disp);
              setItems((prev) => agruparPorFamilia(prev.map((it, i) => (i === reemplazarIdx
                ? { ...it, producto_id: nuevo.id, cantidad: cant, precio_unitario: pr.sugerido, motivo_excepcion: "", es_surtido: false }
                : it))));
              setReemplazarIdx(null);
              toast.success(`Cambiado: ${pActual?.nombre || "ítem"} → ${nuevo.nombre}${cant !== (Number(actual.cantidad) || 1) ? ` (cantidad ajustada a ${cant} por stock)` : ""}`);
            };
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-xl">Cambiar ítem</DialogTitle>
                  <div className="text-xs text-muted-foreground">Actual: <b className="text-foreground">{pActual?.nombre || actual.producto_id}</b> · {actual.cantidad} {pActual?.unidad || "u"}. Busca el producto correcto; se conserva la cantidad.</div>
                </DialogHeader>
                <Input autoFocus value={reemplazarSearch} onChange={(e) => setReemplazarSearch(e.target.value)} placeholder="Buscar por nombre, código o color…" data-testid="reemplazar-item-search" />
                <div className="max-h-80 overflow-auto rounded-md border border-border divide-y divide-border/60" data-testid="reemplazar-item-resultados">
                  {reemplazarSearch.trim().length < 2 && <div className="p-3 text-xs text-muted-foreground">Escribe al menos 2 letras.</div>}
                  {reemplazarSearch.trim().length >= 2 && resultados.length === 0 && <div className="p-3 text-xs text-muted-foreground">Sin resultados.</div>}
                  {resultados.map((p, i) => {
                    const disp = Math.max(0, (p.stock_actual || 0) - (p.stock_reservado || 0));
                    const pr = pricingFor(p);
                    return (
                      <button type="button" key={p.id} disabled={disp <= 0} onClick={() => reemplazar(p)}
                        className={`w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-primary/5 ${disp <= 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                        data-testid={`reemplazar-item-opcion-${i}`}>
                        <div className="w-9 h-9 rounded bg-muted overflow-hidden flex-shrink-0">
                          {(p.imagen_path || p.tiene_imagen) && <img src={`${API}/catalogo/img/${p.id}?w=200`} alt="" loading="lazy" className="w-full h-full object-cover" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{p.nombre}</div>
                          <div className="text-[11px] text-muted-foreground font-mono">{p.codigo || p.sku || ""} · disp. {disp}</div>
                        </div>
                        {mostrarPrecios && <div className="text-xs tabular-nums text-right"><div className="font-semibold text-primary">{formatCOP(pr.sugerido)}</div><div className="text-muted-foreground">mín {formatCOP(pr.minimo)}</div></div>}
                      </button>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
      <Dialog open={!!fotoPreview} onOpenChange={(v) => !v && setFotoPreview(null)}>
        <DialogContent className="max-w-lg" data-testid="foto-preview-modal">
          {fotoPreview && (() => {
            const fp = fotoPreview; const fpr = pricingFor(fp);
            const yaEsta = items.some((it) => it.producto_id === fp.id);
            const disp = Math.max(0, (fp.stock_actual || 0) - (fp.stock_reservado || 0));
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-xl leading-tight">{fp.nombre}</DialogTitle>
                  <div className="text-xs text-muted-foreground font-mono">{fp.codigo || fp.sku || ""} · disp. {disp} {fp.unidad || ""}</div>
                </DialogHeader>
                <div className="w-full aspect-square max-h-[60vh] rounded-lg border border-border bg-muted overflow-hidden relative">
                  <div className="absolute inset-0 animate-pulse bg-muted" aria-hidden="true" />
                  <img src={`${API}/catalogo/img/${fp.id}?w=800`} alt={fp.nombre} className="relative w-full h-full object-contain" data-testid="foto-preview-img" />
                </div>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  {mostrarPrecios && <div className="text-sm">Sug. <b className="text-primary">{formatCOP(fpr.sugerido)}</b> · mín {formatCOP(fpr.minimo)}</div>}
                  <div className="flex gap-2 ml-auto">
                    <Button variant="outline" onClick={() => setFotoPreview(null)} data-testid="foto-preview-cerrar">Cerrar</Button>
                    <Button disabled={yaEsta || disp <= 0} onClick={() => { addProducto(fp); setFotoPreview(null); }} data-testid="foto-preview-agregar">{yaEsta ? "Ya está en el pedido" : "Agregar al pedido"}</Button>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
      <Dialog open={!!previewItem} onOpenChange={(v) => !v && setPreviewItem(null)}>
        <DialogContent className="max-w-2xl" data-testid="item-preview-modal">
          {previewItem && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    <Eye className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-[11px] font-bold uppercase text-primary tracking-wider">Detalle del ítem</div>
                    <DialogTitle className="font-display text-2xl mt-1">{previewItem.producto.nombre}</DialogTitle>
                  </div>
                </div>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-2">
                <div className="md:col-span-2">
                  {previewItem.producto.imagen_path ? (
                    <img
                      src={`${API}/catalogo/img/${previewItem.producto.id}?w=800`}
                      alt={previewItem.producto.nombre}
                      loading="lazy"
                      decoding="async"
                      className="w-full aspect-square object-cover rounded-lg border border-border bg-muted"
                      data-testid="item-preview-image"
                    />
                  ) : (
                    <div className="w-full aspect-square rounded-lg bg-muted border border-dashed border-border flex flex-col items-center justify-center text-muted-foreground gap-2">
                      <Layers className="w-10 h-10 opacity-40" />
                      <div className="text-[11px] uppercase tracking-wider">Sin foto</div>
                    </div>
                  )}
                </div>
                <div className="md:col-span-3 space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 bg-muted/40 rounded-md">
                      <div className="text-[10px] font-bold uppercase text-muted-foreground">SKU</div>
                      <div className="font-mono text-xs">{previewItem.producto.sku || "—"}</div>
                    </div>
                    <div className="p-2 bg-muted/40 rounded-md">
                      <div className="text-[10px] font-bold uppercase text-muted-foreground">Unidad</div>
                      <div className="text-xs">{previewItem.producto.unidad || "u"}</div>
                    </div>
                    <div className="p-2 bg-muted/40 rounded-md">
                      <div className="text-[10px] font-bold uppercase text-muted-foreground">Cantidad pedida</div>
                      <div className="text-sm font-semibold tabular-nums">{previewItem.item.cantidad}</div>
                    </div>
                    <div className="p-2 bg-muted/40 rounded-md">
                      <div className="text-[10px] font-bold uppercase text-muted-foreground">Stock disponible</div>
                      <div className="text-sm font-semibold tabular-nums">
                        {Math.max(0, (previewItem.producto.stock_actual || 0) - (previewItem.producto.stock_reservado || 0))}
                      </div>
                    </div>
                  </div>
                  <VentasResumenLine productoId={previewItem.producto.id} />
                  {mostrarPrecios && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-2 bg-primary/5 border border-primary/20 rounded-md">
                        <div className="text-[10px] font-bold uppercase text-primary">Precio sugerido</div>
                        <div className="text-sm font-semibold tabular-nums text-primary">{formatCOP(previewItem.pricing.sugerido)}</div>
                      </div>
                      <div className="p-2 bg-muted/40 rounded-md">
                        <div className="text-[10px] font-bold uppercase text-muted-foreground">Mínimo autorizado</div>
                        <div className="text-sm font-semibold tabular-nums">{formatCOP(previewItem.pricing.minimo)}</div>
                      </div>
                    </div>
                  )}
                  <div className="p-2 bg-emerald-50 border border-emerald-100 rounded-md">
                    <div className="text-[10px] font-bold uppercase text-emerald-700">Subtotal del ítem</div>
                    <div className="text-lg font-bold tabular-nums text-emerald-800">
                      {formatCOP(Math.round((Number(previewItem.item.precio_unitario) || 0) * (Number(previewItem.item.cantidad) || 0)))}
                    </div>
                  </div>
                  {previewItem.producto.descripcion && (
                    <div className="text-xs text-muted-foreground leading-relaxed">
                      {previewItem.producto.descripcion}
                    </div>
                  )}
                  {previewItem.item.es_surtido && (
                    <div className="p-2 bg-amber-50 border border-amber-200 rounded-md text-[11px] text-amber-800">
                      <b>Surtido:</b> la bodega definirá los colores al preparar el pedido.
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={() => setPreviewItem(null)} data-testid="item-preview-close">Cerrar</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
    {/* Feb/2026: dialog de pedido inteligente (IA parsea texto/foto) */}
    <IntelligentOrderDialog
      open={showIntel}
      onClose={() => setShowIntel(false)}
      productos={productos}
      onConfirmar={async (parsed) => {
        // Oct/2026 bug fix v2: antes se descartaban items con producto_id
        // duplicado. Ahora SUMAMOS las cantidades — así si el usuario escribe
        // "cinta verde 6" y "cinta verde oscuro 6" y la IA los resuelve a la
        // misma variante, el carrito termina con cantidad 12 en vez de perder
        // 6 unidades. También corrige el bug reportado "12 colores → solo 6
        // agregados" cuando la IA tiene matches ambiguos. Y usa UNA sola
        // llamada funcional setItems para evitar stale state en el loop.
        const porPid = new Map(); // pid → { row (primero), cantidadTotal }
        let sinPid = 0;
        for (const row of parsed) {
          const pid = row.producto?.producto_id;
          if (!pid) { sinPid++; continue; }
          const cant = Number(row.cantidad || 0);
          if (!porPid.has(pid)) porPid.set(pid, { row, cantidadTotal: 0 });
          porPid.get(pid).cantidadTotal += cant;
        }
        // Separar lo que YA está en el pedido vs. lo nuevo
        const bumpeados = new Map(); // pid → cantExtra
        const nuevos = [];
        const faltanEnProductos = [];
        for (const [pid, info] of porPid) {
          const existe = items.some((it) => it.producto_id === pid);
          if (existe) {
            bumpeados.set(pid, info.cantidadTotal);
            continue;
          }
          const full = productosById.get(pid);
          const target = full || {
            id: pid, sku: info.row.producto.sku, nombre: info.row.producto.nombre,
            stock_actual: info.row.producto.disponible, stock_reservado: 0,
            precio_sugerido: info.row.producto.precio_sugerido, imagen_path: info.row.producto.imagen_path,
            unidad: info.row.producto.unidad, _permitir_sin_stock: true,
          };
          if (!full) faltanEnProductos.push(target);
          const pr = pricingFor(target);
          // Jun/2026: si la IA leyó el precio en la remisión, ese manda.
          nuevos.push({
            producto_id: pid,
            cantidad: info.cantidadTotal,
            precio_unitario: info.row.precio_unitario_ia || pr.sugerido || info.row.producto.precio_sugerido || 0,
            motivo_excepcion: "",
          });
        }
        if (faltanEnProductos.length) {
          setProductos((prev) => {
            const existing = new Set(prev.map((x) => x.id));
            return [...prev, ...faltanEnProductos.filter((x) => !existing.has(x.id))];
          });
        }
        // UNA sola actualización funcional: bump cantidades existentes +
        // agregar nuevos ítems.
        if (bumpeados.size > 0 || nuevos.length > 0) {
          setItems((prev) => {
            const updated = prev.map((it) =>
              bumpeados.has(it.producto_id)
                ? { ...it, cantidad: (Number(it.cantidad) || 0) + bumpeados.get(it.producto_id) }
                : it
            );
            return agruparPorFamilia([...updated, ...nuevos]);
          });
        }
        const partes = [];
        if (nuevos.length) partes.push(`${nuevos.length} nuevo${nuevos.length === 1 ? "" : "s"}`);
        if (bumpeados.size) partes.push(`${bumpeados.size} con cantidad sumada`);
        if (sinPid) partes.push(`${sinPid} sin resolver`);
        if (nuevos.length === 0 && bumpeados.size === 0) {
          toast.info("No se agregó nada al pedido");
        } else {
          toast.success(`IA: ${partes.join(" · ")}`);
        }
      }}
    />

    {/* Feb/2026: modal de confirmación de salida con 3 opciones */}
    {exitConfirm && (
      <Dialog
        open
        onOpenChange={(v) => {
          if (!v && !exitSaving) {
            // Si cierran este modal sin elegir, cancelamos la salida.
            if (exitConfirm.kind === "nav" && exitConfirm.reset) exitConfirm.reset();
            setExitConfirm(null);
          }
        }}
      >
        <DialogContent
          className="max-w-md grid-cols-[minmax(0,1fr)]"
          data-testid="exit-confirm-modal"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <ShieldAlert className="w-6 h-6 text-amber-500" />
              Tienes un pedido sin terminar
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {existing
              ? "Hay cambios aún no guardados en este pedido. ¿Qué quieres hacer?"
              : `Tienes ${items.length} ítem${items.length === 1 ? "" : "s"} agregado${items.length === 1 ? "" : "s"} que aún no se guardan. ¿Qué quieres hacer?`}
          </p>
          <div className="mt-4 flex flex-col gap-2">
            <button
              type="button"
              disabled={exitSaving}
              onClick={async () => {
                setExitSaving(true);
                let ok = false;
                try {
                  ok = existing ? await autoSaveExisting() : await autoSaveBorrador();
                } finally {
                  setExitSaving(false);
                }
                // Jun/2026: si NO se pudo guardar, el pedido se queda abierto
                // (antes cerraba igual y el vendedor perdía el trabajo).
                if (!ok) return;
                const target = exitConfirm;
                setExitConfirm(null);
                if (target.kind === "nav") target.proceed();
                else onClose();
              }}
              className="h-12 px-4 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="exit-save-btn"
            >
              {exitSaving ? <><RefreshCw className="w-4 h-4 animate-spin" /> Guardando...</> : <>💾 Guardar borrador y salir</>}
            </button>
            <button
              type="button"
              disabled={exitSaving}
              onClick={() => {
                // Descartar: limpiar snapshot local y salir
                try { if (!existing) localStorage.removeItem(BORRADOR_LS_KEY); } catch {}
                const target = exitConfirm;
                setExitConfirm(null);
                if (target.kind === "nav") target.proceed();
                else onClose();
              }}
              className="h-11 px-4 rounded-xl border-2 border-rose-200 bg-rose-50 text-rose-700 font-semibold hover:bg-rose-100 disabled:opacity-60"
              data-testid="exit-discard-btn"
            >
              🗑 Salir sin guardar (descartar)
            </button>
            <button
              type="button"
              disabled={exitSaving}
              onClick={() => {
                if (exitConfirm.kind === "nav" && exitConfirm.reset) exitConfirm.reset();
                setExitConfirm(null);
              }}
              className="h-10 px-4 rounded-xl text-muted-foreground hover:bg-muted font-medium disabled:opacity-60"
              data-testid="exit-cancel-btn"
            >
              Cancelar (seguir editando)
            </button>
          </div>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
}

/* ---------------- Pedido Detail with State Actions ---------------- */
function PedidoDetail({ pedido: initial, onClose }) {
  const [pedido, setPedido] = useState(initial);
  // Jun/2026: la lista abre el detalle con la versión "resumen" y luego llega el
  // pedido completo — sincronizar el estado local cuando cambia la prop.
  useEffect(() => { setPedido(initial); }, [initial]);
  const [cancelMotivo, setCancelMotivo] = useState("");
  const [showCancel, setShowCancel] = useState(false);
  const [showDescartar, setShowDescartar] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [taking, setTaking] = useState(false);
  // Flag común: hay un cambio de estado (confirmar/cancelar/descartar/entregar) en curso.
  // Bloquea el cierre de modales de confirmación mientras la petición está viva.
  const [changingEstado, setChangingEstado] = useState(false);
  const [showComision, setShowComision] = useState(false); // por defecto oculta (modo "seguro para mostrar al cliente")
  const [guiaMenu, setGuiaMenu] = useState(null); // {pedidoId, nombre}
  const [excepciones, setExcepciones] = useState([]);
  const [decidiendoExc, setDecidiendoExc] = useState(null); // {id, tipo}
  const [motivoDecision, setMotivoDecision] = useState("");
  const [decidiendoExcSaving, setDecidiendoExcSaving] = useState(false);
  // Estado del modal "Definir surtido"
  const [surtidoItem, setSurtidoItem] = useState(null); // item completo
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [surtidoDesglose, setSurtidoDesglose] = useState([]); // [{variante_id, variante_nombre, cantidad, stock_disponible, sku}]
  const [surtidoLoading, setSurtidoLoading] = useState(false);
  const [surtidoSaving, setSurtidoSaving] = useState(false);
  const { user } = useAuth();
  const canTomarP = useHasPerm("despachos.tomar");
  const canGestP = useHasPerm("despachos.gestionar");
  // Nov/2026: la ubicación en bodega (P1-03…) es para quien empaca (operadores), no para vendedores.
  const verUbicacion = user?.role === "operador" || canTomarP || canGestP;
  const canTomar = canTomarP || canGestP;
  const canAprobarExc = useHasPerm("excepciones.aprobar");
  const isAdmin = user?.role === "admin";
  const puedeAprobarExc = canAprobarExc || isAdmin;
  const tomadoPorMi = pedido.operador_id === user?.id;
  // Feb/2026: regla reforzada — un borrador está en manos del vendedor; solo
  // puede tomarse cuando pase a 'confirmado'. El backend ya lo rechaza con
  // 403, pero ocultamos el botón para no confundir al operador.
  const puedeTomar = canTomar && !pedido.operador_id && pedido.estado === "confirmado";
  const puedeLiberar = (tomadoPorMi || isAdmin) && pedido.operador_id && pedido.estado !== "despachado";
  // Puede definir surtido: SOLO operador que TOMÓ el pedido, o director.
  // Regla del negocio Mar 7, 2026: sin tomar el pedido, el operador no puede
  // hacer nada (ni surtido, ni despachar, ni editar guía). Debe tomarlo primero.
  const puedeDefinirSurtido = (
    isAdmin || (canTomar && tomadoPorMi)
  ) && ["borrador", "confirmado"].includes(pedido.estado);

  // Edición del pedido: solo si NO está en estado terminal y NO está bloqueada.
  // El vendedor dueño puede editar sus items (agregar/quitar).
  const canVentasEditarBuilder = useHasPerm("ventas.editar");
  // Jun/2026: permiso especial para editar items/descuento de pedidos ya despachados/entregados.
  const canEditarDespachados = useHasPerm("ventas.editar_despachados");
  const esVendedorDueño = pedido.vendedor_id === user?.id;
  const edicionBloqueada = !!pedido.edicion_bloqueada;
  const estadoEditable = ["borrador", "confirmado"].includes(pedido.estado);
  const esPostDespacho = ["despachado", "entregado"].includes(pedido.estado);
  const puedeEditarPostDespacho = esPostDespacho && !edicionBloqueada && (isAdmin || canEditarDespachados);
  const puedeEditarItems = puedeEditarPostDespacho || (estadoEditable && !edicionBloqueada && (
    isAdmin || (canVentasEditarBuilder && esVendedorDueño)
  ));
  // Operador o director bloquea la edición (cuando ya empacó).
  const puedeBloquearEdicion = estadoEditable && !edicionBloqueada && (
    isAdmin || (tomadoPorMi && canTomar)
  );
  // Solo director puede desbloquear.
  const puedeDesbloquearEdicion = edicionBloqueada && isAdmin;
  const [editorAbierto, setEditorAbierto] = useState(false);
  // Modal de devolución de pedido (mar/2026)
  const [devolucionAbierta, setDevolucionAbierta] = useState(false);
  // ¿El usuario puede gestionar despachos? (operador o director)
  const puedeGestionarDespachos = !!(user?.permissions?.["despachos.tomar"] ||
                                     user?.permissions?.["despachos.gestionar"] ||
                                     canTomar);

  const bloquearEdicion = async () => {
    if (!window.confirm("¿Bloquear la edición del pedido? El vendedor ya no podrá agregar ni quitar items. Solo el director podrá desbloquear.")) return;
    try {
      await api.post(`/pedidos/${pedido.id}/bloquear-edicion`);
      const { data } = await api.get(`/pedidos/${pedido.id}`);
      setPedido(data);
      toast.success("Edición bloqueada. El vendedor ya no puede modificar.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo bloquear");
    }
  };

  const desbloquearEdicion = async () => {
    try {
      await api.post(`/pedidos/${pedido.id}/desbloquear-edicion`);
      const { data } = await api.get(`/pedidos/${pedido.id}`);
      setPedido(data);
      toast.success("Edición desbloqueada.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo desbloquear");
    }
  };

  // Cargar sugerencia proporcional al abrir el modal de surtido
  useEffect(() => {
    if (!surtidoItem) { setSurtidoDesglose([]); return; }
    (async () => {
      setSurtidoLoading(true);
      try {
        const iid = surtidoItem.id || surtidoItem.producto_id;
        const { data } = await api.get(`/pedidos/${pedido.id}/items/${iid}/sugerir-surtido`);
        setSurtidoDesglose(
          (data.variantes || []).map((v) => ({
            variante_id: v.variante_id,
            variante_nombre: v.variante_nombre,
            sku: v.sku,
            codigo: v.codigo,
            stock_disponible: v.stock_disponible,
            cantidad: v.sugerido || 0,
            tiene_imagen: !!v.tiene_imagen,
          }))
        );
      } catch (e) {
        toast.error(e?.response?.data?.detail || "No se pudo cargar la sugerencia");
      } finally { setSurtidoLoading(false); }
    })();
  }, [surtidoItem, pedido.id]);

  const guardarSurtido = async () => {
    if (!surtidoItem) return;
    const iid = surtidoItem.id || surtidoItem.producto_id;
    const total = Number(surtidoItem.cantidad || 0);
    const suma = surtidoDesglose.reduce((s, d) => s + (Number(d.cantidad) || 0), 0);
    if (suma !== total) {
      toast.error(`La suma (${suma}) debe cuadrar con el total del ítem (${total}).`);
      return;
    }
    setSurtidoSaving(true);
    try {
      const desglose = surtidoDesglose
        .filter((d) => Number(d.cantidad) > 0)
        .map((d) => ({ variante_id: d.variante_id, cantidad: Number(d.cantidad) }));
      const { data } = await api.post(`/pedidos/${pedido.id}/items/${iid}/definir-surtido`, { desglose });
      setPedido(data);
      toast.success("Surtido definido. Ya se puede despachar el pedido.");
      setSurtidoItem(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el surtido");
    } finally { setSurtidoSaving(false); }
  };

  // Traer solicitudes de excepción asociadas a este pedido (para mostrar
  // inline su estado y ahorrar el viaje a otra página).
  const loadExcepciones = async () => {
    const tieneAlguna = pedido.bloqueado_por_excepcion
      || (pedido.items || []).some((it) => it.requiere_excepcion);
    if (!tieneAlguna) { setExcepciones([]); return; }
    try {
      const { data } = await api.get(`/excepciones?pedido_id=${pedido.id}`);
      setExcepciones(data || []);
    } catch {}
  };
  useEffect(() => { loadExcepciones(); /* eslint-disable-next-line */ }, [pedido.id, pedido.bloqueado_por_excepcion]);

  const decidirExc = async () => {
    if (!decidiendoExc) return;
    setDecidiendoExcSaving(true);
    try {
      await api.post(`/excepciones/${decidiendoExc.id}/decidir`, {
        decision: decidiendoExc.tipo, motivo: motivoDecision || null,
      });
      toast.success(decidiendoExc.tipo === "aprobar" ? "Excepción aprobada" : "Excepción rechazada");
      setDecidiendoExc(null); setMotivoDecision("");
      const { data } = await api.get(`/pedidos/${pedido.id}`);
      setPedido(data);
      loadExcepciones();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo decidir");
    } finally { setDecidiendoExcSaving(false); }
  };

  const tomar = async () => {
    setTaking(true);
    try {
      const res = await api.post(`/pedidos/${pedido.id}/tomar`);
      const { data } = await api.get(`/pedidos/${pedido.id}`);
      setPedido(data);
      if (res?.data?.auto_confirmado) {
        toast.success("Pedido tomado y confirmado. Listo para despachar.");
      } else {
        toast.success("Pedido tomado. Ya es tuyo para despacho.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "No se pudo tomar";
      toast.error(detail);
      // Si otro operador lo tomó → refrescar
      if (e?.response?.status === 409) {
        const { data } = await api.get(`/pedidos/${pedido.id}`);
        setPedido(data);
      }
    } finally { setTaking(false); }
  };

  const liberar = async () => {
    if (!window.confirm("¿Liberar este pedido? Otro operador podrá tomarlo.")) return;
    setTaking(true);
    try {
      await api.post(`/pedidos/${pedido.id}/liberar`);
      const { data } = await api.get(`/pedidos/${pedido.id}`);
      setPedido(data);
      toast.success("Pedido liberado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo liberar");
    } finally { setTaking(false); }
  };

  // Pagos state
  const [pagosData, setPagosData] = useState({ pagos: [], resumen: null });
  const [pagoOpen, setPagoOpen] = useState(false);
  const [pagoForm, setPagoForm] = useState({ monto: "", metodo: "transferencia", referencia: "", notas: "", file: null });
  const [pagoSaving, setPagoSaving] = useState(false);
  const puedeAprobarPago = useHasPerm("pagos.aprobar");
  const puedeReportarPago = useHasPerm("pagos.reportar");

  const refrescarPedidoYPagos = useCallback(async () => {
    try {
      const [full, resp] = await Promise.all([
        api.get(`/pedidos/${pedido.id}`),
        api.get(`/pagos/pedido/${pedido.id}`),
      ]);
      setPedido(full.data);
      setPagosData(resp.data);
    } catch {}
  }, [pedido.id]);

  const loadPagos = useCallback(async () => {
    try {
      const { data } = await api.get(`/pagos/pedido/${pedido.id}`);
      setPagosData(data);
    } catch {
      setPagosData({ pagos: [], resumen: null });
    }
  }, [pedido.id]);
  useEffect(() => { loadPagos(); }, [loadPagos]);

  const aprobarPago = async (p) => {
    if (!window.confirm(`¿Aprobar pago de ${formatCOP(p.monto)} de ${p.subido_por_nombre || "vendedor"}?`)) return;
    try {
      await api.post(`/pagos/${p.id}/aprobar`, {});
      toast.success("Pago aprobado. Saldo del pedido actualizado.");
      refrescarPedidoYPagos();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo aprobar"); }
  };

  const rechazarPago = async (p) => {
    const motivo = window.prompt("Motivo del rechazo (obligatorio):", "");
    if (!motivo || !motivo.trim()) { toast.error("Debes escribir un motivo"); return; }
    try {
      await api.post(`/pagos/${p.id}/rechazar`, { motivo: motivo.trim() });
      toast.success("Pago rechazado. El vendedor recibirá una notificación.");
      refrescarPedidoYPagos();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo rechazar"); }
  };

  const registrarPago = async (e) => {
    e.preventDefault();
    if (!pagoForm.file) { toast.error("Adjunta el comprobante (imagen o PDF)"); return; }
    const monto = Number(pagoForm.monto);
    if (!monto || monto <= 0) { toast.error("Monto inválido"); return; }
    // Guard local: no permitir monto > saldo pendiente
    const saldoPend = Number(pagosData?.resumen?.saldo ?? pedido?.saldo_pendiente ?? pedido?.total ?? 0);
    if (saldoPend > 0 && monto > saldoPend) {
      toast.error(`El monto (${formatCOP(monto)}) supera el saldo pendiente (${formatCOP(saldoPend)}). Registra máximo ${formatCOP(saldoPend)}.`);
      return;
    }
    setPagoSaving(true);
    try {
      const fd = new FormData();
      fd.append("pedido_id", pedido.id);
      fd.append("monto", String(Number(pagoForm.monto)));
      fd.append("metodo", pagoForm.metodo);
      if (pagoForm.referencia) fd.append("referencia", pagoForm.referencia);
      if (pagoForm.notas) fd.append("notas", pagoForm.notas);
      fd.append("file", pagoForm.file);
      await api.post("/pagos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pago registrado. Queda pendiente de aprobación por dirección.");
      setPagoOpen(false);
      setPagoForm({ monto: "", metodo: "transferencia", referencia: "", notas: "", file: null });
      loadPagos();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo registrar el pago"); }
    finally { setPagoSaving(false); }
  };

  const change = async (estado, extra = {}) => {
    setChangingEstado(true);
    try {
      const { data } = await api.post(`/pedidos/${pedido.id}/estado`, { estado, ...extra });
      // Si fue borrador que se borra del sistema, cerramos el modal y refrescamos
      if (data?.deleted) {
        onClose(true); // señala al padre que hubo cambio
        return;
      }
      setPedido(data);
      toast.success(`Pedido ${ESTADO_META[estado].label.toLowerCase()}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cambiar estado"); throw e; }
    finally { setChangingEstado(false); }
  };

  const [pdfFormatoOpen, setPdfFormatoOpen] = useState(false);
  const [pdfPending, setPdfPending] = useState(null); // {tipo, conRotulo}

  const abrirDescargaPdf = (tipo = "COTIZACION", conRotulo = false) => {
    setPdfPending({ tipo, conRotulo });
    setPdfFormatoOpen(true);
  };

  const downloadPdf = async (tipo = "COTIZACION", conRotulo = false, format = "pdf") => {
    setDownloading(true);
    try {
      const params = { tipo };
      if (conRotulo) params.con_rotulo = true;
      if (format === "png") {
        // Feb/2026: pedimos png-multi para recibir una imagen por página.
        // Así una cotización larga se guarda como 2+ archivos independientes
        // en la galería del cliente (letra legible) en vez de un PNG gigante.
        params.format = "png-multi";
        const { data } = await api.get(`/pedidos/${pedido.id}/pdf`, { params, responseType: "json" });
        const paginas = data.paginas || [];
        const prefix = data.filename_prefix || "Documento";
        const numero = data.numero || pedido.numero;
        for (const pag of paginas) {
          const bin = atob(pag.png_base64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          const blob = new Blob([bytes], { type: "image/png" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = pag.filename || `${prefix}-Beem-${numero}-p${pag.n}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          // Esperamos 200ms entre descargas para que el navegador (y móvil)
          // procese cada archivo sin bloquear (algunos bloquean multi-download).
          await new Promise((r) => setTimeout(r, 200));
        }
        toast.success(
          paginas.length === 1
            ? "Imagen descargada"
            : `${paginas.length} imágenes descargadas — se guardaron en tu galería como archivos separados`
        );
        return;
      }
      const { data } = await api.get(`/pedidos/${pedido.id}/pdf`, { params, responseType: "blob" });
      const mime = "application/pdf";
      const ext = "pdf";
      const url = URL.createObjectURL(new Blob([data], { type: mime }));
      const a = document.createElement("a");
      const prefix = tipo === "REMISION" ? (conRotulo ? "Remision-Rotulo" : "Remision") : tipo === "COMPROBANTE" ? "Comprobante" : tipo === "ORDEN" ? "Orden-Pedido" : "Cotizacion";
      a.href = url; a.download = `${prefix}-Beem-${pedido.numero}.${ext}`; a.click();
      URL.revokeObjectURL(url);
      if (tipo === "REMISION") {
        toast.success(conRotulo
          ? "Remisión + Rótulo descargado. Imprime, pega el rótulo y despacha."
          : "Remisión descargada. Nº asignado en el PDF."
        );
      } else {
        toast.success("PDF descargado");
      }
    } catch (e) {
      let detail = e?.response?.data?.detail;
      if (!detail && e?.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text())?.detail; } catch { /* no-json */ }
      }
      const status = e?.response?.status;
      toast.error(`No se pudo descargar${detail ? `: ${detail}` : status ? ` (error ${status})` : " (sin conexión)"}`);
    } finally { setDownloading(false); }
  };

  const m = ESTADO_META[pedido.estado];

  // Permisos para acciones (ocultamos botones que no puede usar)
  const canVentasCrear = useHasPerm("ventas.crear");
  const canVentasEditar = useHasPerm("ventas.editar");
  const canVentasCancelar = useHasPerm("ventas.cancelar");
  const canDespachosGestionar = useHasPerm("despachos.gestionar");
  const canConfirmar = isAdmin || canVentasCrear || canVentasEditar;
  const canDespachar = isAdmin || canDespachosGestionar;
  const canCancelar = isAdmin || canVentasCancelar;
  // Vendedor dueño puede descartar SU propio borrador (se elimina del sistema).
  const canDescartarBorrador = isAdmin || canCancelar || (pedido.estado === "borrador" && pedido.vendedor_id === user?.id);
  // "Entregado" ahora es exclusivo del vendedor dueño del pedido (o director).
  // Operadores no pueden confirmar entregas — solo el vendedor tiene contacto con el cliente.
  const canEntregar = isAdmin || (canVentasCrear && pedido?.vendedor_id === user?.id);
  const isAdminOrDirector = isAdmin;

  // Modal guía de despacho
  const [showDespachar, setShowDespachar] = useState(false);
  const [guiaNumero, setGuiaNumero] = useState("");
  const [guiaTransp, setGuiaTransp] = useState("");
  const [guiaFile, setGuiaFile] = useState(null);
  const [costoTransp, setCostoTransp] = useState("");
  const [beemPagoFlete, setBeemPagoFlete] = useState(false);
  const [motivoFleteBeem, setMotivoFleteBeem] = useState("");
  const [recogeEnBodega, setRecogeEnBodega] = useState(false);
  const [umbralFlete, setUmbralFlete] = useState(600000);
  const [dispatching, setDispatching] = useState(false);
  const [guiaSubiendo, setGuiaSubiendo] = useState(false); // upload directo desde "Editar guía"
  // Jun/2026: a partir de $600.000 EXACTOS (>=) Beem asume el flete.
  const requiereCostoTransp = !recogeEnBodega && (pedido.total || 0) >= umbralFlete;

  useEffect(() => {
    api.get("/pedidos/_/empresa")
      .then(({ data }) => { if (data?.umbral_flete_gratis) setUmbralFlete(Number(data.umbral_flete_gratis)); })
      .catch(() => {});
  }, []);

  const enviarDespacho = async () => {
    if (!recogeEnBodega) {
      if (!guiaNumero.trim()) { toast.error("Escribe el número de guía"); return; }
      if (!guiaFile) { toast.error("Adjunta la foto o documento de la guía"); return; }
      if ((requiereCostoTransp || beemPagoFlete) && (!costoTransp || Number(costoTransp) <= 0)) {
        toast.error("Registra el valor exacto del flete antes de despachar.");
        return;
      }
      if (beemPagoFlete && !requiereCostoTransp && !motivoFleteBeem.trim()) {
        toast.error("Escribe el motivo por el que Beem asume el flete.");
        return;
      }
    }
    setDispatching(true);
    try {
      const form = new FormData();
      if (recogeEnBodega) {
        form.append("recogio_en_bodega", "true");
      } else {
        form.append("guia_numero", guiaNumero.trim());
        if (guiaTransp) form.append("transportadora", guiaTransp);
        if (costoTransp && Number(costoTransp) > 0) form.append("costo_transporte", String(Number(costoTransp)));
        form.append("beem_pago_flete", beemPagoFlete ? "true" : "false");
        if (beemPagoFlete && motivoFleteBeem.trim()) form.append("motivo_flete_beem", motivoFleteBeem.trim());
        form.append("file", guiaFile);
      }
      const { data } = await api.post(`/pedidos/${pedido.id}/despachar`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const cobrado = Number(data.flete_cliente || 0);
      if (data.recogio_en_bodega) {
        toast.success(`Pedido marcado como entregado en bodega. El vendedor fue notificado.`);
      } else if (data.flete_excepcion_beem) {
        toast.success(`Pedido despachado. Beem asume el flete ($${Number(data.costo_transporte || costoTransp).toLocaleString("es-CO")}) como gasto. El vendedor fue notificado.`);
      } else if (cobrado > 0) {
        toast.success(`Pedido despachado. Se sumó $${cobrado.toLocaleString("es-CO")} de flete al total. El vendedor fue notificado.`);
      } else {
        toast.success(`Pedido despachado. Guía ${data.guia?.numero}. El vendedor fue notificado.`);
      }
      setShowDespachar(false);
      setGuiaNumero(""); setGuiaTransp(""); setGuiaFile(null); setCostoTransp(""); setBeemPagoFlete(false); setMotivoFleteBeem(""); setRecogeEnBodega(false);
      setPedido(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo despachar");
    } finally { setDispatching(false); }
  };

  const [editandoCostoTransp, setEditandoCostoTransp] = useState(false);
  const [nuevoCostoTransp, setNuevoCostoTransp] = useState("");
  const guardarCostoTransp = async () => {
    const val = Math.max(0, Number(nuevoCostoTransp) || 0);
    try {
      const { data } = await api.patch(`/pedidos/${pedido.id}/costo-transporte`, { costo_transporte: val });
      toast.success("Costo de transporte actualizado");
      setPedido(data);
      setEditandoCostoTransp(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    }
  };

  // Regla del negocio Mar 2026: el operador debe haber "tomado" el pedido
  // antes de despachar / editar guía / editar costo transporte. Sin tomarlo,
  // el backend responde 403 → ocultamos los botones para no engañar al usuario.
  const puedeDespacharAhora = isAdmin || (canDespachar && tomadoPorMi);
  const puedeEditarLogistica = isAdmin || ((canTomarP || canGestP) && tomadoPorMi);

  const actions = {
    borrador: [
      canConfirmar && { label: "Confirmar", icon: Check, action: () => change("confirmado"), color: "bg-blue-600 hover:bg-blue-700 text-white" },
      canDescartarBorrador && { label: "Descartar borrador", icon: X, action: () => setShowDescartar(true), color: "border border-rose-300 text-rose-700 hover:bg-rose-50" },
    ].filter(Boolean),
    confirmado: [
      puedeDespacharAhora && { label: "Marcar despachado", icon: Truck, action: () => setShowDespachar(true), color: "bg-amber-600 hover:bg-amber-700 text-white" },
      // Si el operador ve el pedido pero aún no lo tomó, mostramos hint visual (no botón).
      canDespachar && !puedeDespacharAhora && pedido.estado === "confirmado" && !pedido.operador_id && { label: "Toma el pedido para despachar", icon: Truck, action: () => {}, color: "border border-amber-200 text-amber-700 opacity-60 cursor-not-allowed pointer-events-none", disabledHint: true },
      canCancelar && { label: "Cancelar", icon: X, action: () => setShowCancel(true), color: "border border-border hover:bg-muted/40" },
    ].filter(Boolean),
    despachado: [
      canEntregar && { label: "Marcar entregado", icon: PackageCheck, action: () => change("entregado"), color: "bg-primary text-primary-foreground hover:bg-primary/90" },
      canCancelar && { label: "Cancelar", icon: X, action: () => setShowCancel(true), color: "border border-border hover:bg-muted/40" },
    ].filter(Boolean),
    entregado: [],
    cancelado: [],
  };

  return (
    <Dialog open onOpenChange={(v) => {
      // Bloquear el cierre del detalle mientras hay una acción crítica en curso
      // (evita que despacho / cambio de estado / edición de guía queden a medias).
      if (dispatching || changingEstado || surtidoSaving || decidiendoExcSaving || pagoSaving || guiaSubiendo) return;
      if (!v) onClose();
    }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)]" data-testid="pedido-detail">
        <DialogHeader>
          <DialogTitle className="font-display text-lg md:text-2xl flex items-center gap-2 md:gap-3 flex-wrap">
            <FileText className="w-5 h-5 md:w-6 md:h-6 text-muted-foreground/70 flex-shrink-0" />
            {pedido.numero ? (
              <span className="break-all">{pedido.numero}</span>
            ) : (
              <span className="text-slate-600">Borrador</span>
            )}
            <span className={`text-xs px-2 py-1 rounded-full ${m.color}`}>{m.label}</span>
            {pedido.requiere_factura_electronica && (
              <span className="text-[10px] px-2 py-1 rounded-full bg-blue-100 text-blue-800 font-bold uppercase tracking-wide" data-testid="badge-factura-electronica">
                <FileText className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />Factura electrónica
              </span>
            )}
          </DialogTitle>
          <p className="text-sm text-muted-foreground break-words">{pedido.cliente_nombre} · {formatDate(pedido.fecha)}{pedido.ciudad ? ` · ${pedido.ciudad}` : ""}</p>
        </DialogHeader>

        {/* Meta */}
        {excepciones.length > 0 && (
          <div className="mt-4 rounded-xl border border-border/60 bg-card overflow-hidden" data-testid="pedido-excepciones-inline">
            <div className="px-3 py-2 bg-muted/50 border-b border-border/60 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-amber-700" />
              <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
                Solicitudes de excepción de precio
              </span>
              <span className="text-[10px] text-muted-foreground ml-auto">
                {excepciones.filter((e) => e.estado === "pendiente").length} pendiente{excepciones.filter((e) => e.estado === "pendiente").length !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="divide-y divide-border/60">
              {excepciones.map((exc) => {
                const meta = {
                  pendiente: { color: "bg-amber-500/15 text-amber-800 border-amber-500/40", label: "Pendiente" },
                  aprobada: { color: "bg-emerald-500/15 text-emerald-700 border-emerald-500/40", label: "Aprobada" },
                  rechazada: { color: "bg-rose-500/15 text-rose-700 border-rose-500/40", label: "Rechazada" },
                }[exc.estado] || { color: "bg-muted text-foreground border-border", label: exc.estado };
                return (
                  <div key={exc.id} className="px-3 py-2.5" data-testid={`exc-inline-${exc.id}`}>
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[12px] sm:text-sm font-medium break-words min-w-0">{exc.producto_nombre}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider border ${meta.color}`}>
                            {meta.label}
                          </span>
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-2">
                          <span>Solicitado: <b className="text-foreground/80 tabular-nums">{formatCOP(exc.precio_solicitado || 0)}</b></span>
                          <span>·</span>
                          <span>Mínimo: <b className="text-foreground/80 tabular-nums">{formatCOP(exc.precio_minimo || 0)}</b></span>
                          {exc.motivo && <><span>·</span><span className="italic">"{exc.motivo}"</span></>}
                        </div>
                        {exc.estado !== "pendiente" && (
                          <div className="text-[10px] text-muted-foreground mt-0.5">
                            {exc.aprobador_nombre} · {(exc.decision_at || "").slice(0, 16).replace("T", " ")}
                            {exc.motivo_decision ? ` — ${exc.motivo_decision}` : ""}
                          </div>
                        )}
                      </div>
                      {puedeAprobarExc && exc.estado === "pendiente" && (
                        <div className="flex gap-1 shrink-0">
                          <button
                            onClick={() => { setDecidiendoExc({ id: exc.id, tipo: "aprobar" }); setMotivoDecision(""); }}
                            className="h-8 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
                            data-testid={`exc-aprobar-${exc.id}`}
                          >
                            Aprobar
                          </button>
                          <button
                            onClick={() => { setDecidiendoExc({ id: exc.id, tipo: "rechazar" }); setMotivoDecision(""); }}
                            className="h-8 px-3 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold"
                            data-testid={`exc-rechazar-${exc.id}`}
                          >
                            Rechazar
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {pedido.bloqueado_por_excepcion && (
              <div className="px-3 py-2 bg-amber-50 border-t border-amber-200 text-[11px] text-amber-900">
                <b>Este pedido está bloqueado</b> hasta que se resuelvan las solicitudes pendientes. {puedeAprobarExc ? "Puedes aprobarlas o rechazarlas desde aquí mismo." : "Un director las revisará pronto."}
              </div>
            )}
          </div>
        )}
        {pedido.bloqueado_por_excepcion && excepciones.length === 0 && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900" data-testid="pedido-bloqueado">
            <b><AlertTriangle className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />Pedido bloqueado:</b> hay solicitudes de excepción pendientes de aprobación. No podrá confirmarlo hasta que un director las apruebe.
          </div>
        )}
        <div className={`grid ${pedido.comision_total !== undefined ? "grid-cols-4" : "grid-cols-3"} gap-3 mt-4`}>
          <div className="bg-muted/40 border border-border rounded-lg p-3">
            <div className="text-[10px] font-bold uppercase text-muted-foreground">Vendedor</div>
            <div className="text-sm font-medium mt-1">{pedido.vendedor_nombre}</div>
          </div>
          <div className="bg-muted/40 border border-border rounded-lg p-3">
            <div className="text-[10px] font-bold uppercase text-muted-foreground">Metodo pago</div>
            <div className="text-sm font-medium mt-1">{pedido.metodo_pago || "—"}</div>
          </div>
          <div className="bg-muted/40 border border-border rounded-lg p-3">
            <div className="text-[10px] font-bold uppercase text-muted-foreground">Total</div>
            <div className="text-sm font-semibold tabular-nums mt-1">{formatCOP(pedido.total)}</div>
          </div>
          {pedido.comision_total !== undefined && (
          <button
            type="button"
            onClick={() => setShowComision((s) => !s)}
            className="text-left bg-accent border border-primary/20 rounded-lg p-3 hover:bg-primary/10 transition-colors relative"
            title={showComision ? "Ocultar (modo cliente)" : "Mostrar mi comisión (solo tú la ves)"}
            data-testid="toggle-comision"
          >
            <div className="text-[10px] font-bold uppercase text-primary flex flex-wrap items-center gap-x-1">
              <span>Mi comisión</span>
              <span className="text-[9px] text-muted-foreground/70 normal-case tracking-normal whitespace-nowrap">
                {showComision ? "· ocultar" : "· ver"}
              </span>
            </div>
            <div className="text-sm font-semibold tabular-nums mt-1 text-primary">
              {showComision ? formatCOP(pedido.comision_total || 0) : "•••••"}
            </div>
          </button>
          )}
        </div>

        {/* Aviso "modo cliente" solo si el usuario ve su comisión */}
        {pedido.comision_total !== undefined && !showComision && (
          <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground bg-muted/30 border border-dashed border-border rounded-md px-3 py-2">
            <span>👁️‍🗨️</span>
            <span>Vista segura para mostrar al cliente — tu comisión está oculta. Haz click en la tarjeta para verla.</span>
          </div>
        )}

        {/* Items — tabla en md+, cards en móvil (Feb/2026 fix overflow) */}
        <div className="mt-4 bg-card border border-border rounded-lg overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 border-b border-border/60 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            <div className="col-span-6">Producto</div>
            <div className="col-span-2 text-right">Cantidad</div>
            <div className="col-span-2 text-right">Precio</div>
            <div className="col-span-2 text-right">Subtotal</div>
          </div>
          {pedido.items?.map((it, i) => (
            <div key={i} className="flex flex-col md:grid md:grid-cols-12 md:gap-2 px-3 md:px-4 py-3 border-b border-border/60 last:border-0 text-sm gap-1.5">
              <div className="md:col-span-5 min-w-0">
                <div className="font-medium text-foreground flex items-center gap-2 flex-wrap">
                  {it.producto_nombre}
                  {it.es_surtido && !it.desglose && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 uppercase font-bold" data-testid={`item-surtido-pendiente-${i}`}>Surtido pendiente</span>
                  )}
                  {it.es_surtido && it.desglose && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 uppercase font-bold" data-testid={`item-surtido-listo-${i}`}>Surtido definido</span>
                  )}
                </div>
                {(it.codigo || it.sku || it.ubicacion?.codigo) && (
                  <div className="text-xs text-muted-foreground font-mono flex items-center gap-2">
                    {it.codigo || it.sku}
                    {verUbicacion && it.ubicacion?.seccion_id && <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-800 border border-sky-200 font-sans" title={`${it.ubicacion.zona} · ${it.ubicacion.seccion}`} data-testid={`item-ubicacion-${i}`}>{it.ubicacion.codigo}</span>}
                  </div>
                )}
                {it.descripcion && (
                  <div className="text-[11px] text-muted-foreground/80 leading-tight mt-0.5" title={it.descripcion}>
                    {it.descripcion}
                  </div>
                )}
                {it.requiere_excepcion && <div className="text-[10px] text-rose-600 mt-0.5">Excepción: {it.motivo_excepcion}</div>}
                {it.notas && (
                  <div className="mt-1 inline-flex items-start gap-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid={`item-nota-detalle-${i}`}>
                    <StickyNote className="w-3 h-3 mt-0.5 flex-shrink-0" /><span>{it.notas}</span>
                  </div>
                )}
                {it.es_surtido && it.desglose && (
                  <div className="mt-1 pl-2 border-l-2 border-primary/30 space-y-0.5">
                    {it.desglose.map((d, di) => (
                      <div key={di} className="text-[11px] text-muted-foreground flex items-center gap-1">
                        <span className="text-primary">•</span>
                        <span>{d.variante_nombre}</span>
                        <span className="ml-auto font-mono">{d.cantidad}</span>
                      </div>
                    ))}
                  </div>
                )}
                {it.es_surtido && !it.desglose && puedeDefinirSurtido && (
                  <button
                    type="button"
                    onClick={() => setSurtidoItem(it)}
                    className="mt-1 h-7 px-2 rounded-md bg-primary text-primary-foreground text-[11px] font-semibold hover:bg-primary/90 inline-flex items-center gap-1"
                    data-testid={`definir-surtido-btn-${i}`}
                  >
                    🎨 Definir surtido
                  </button>
                )}
                {it.es_surtido && !it.desglose && !puedeDefinirSurtido && canTomar && !tomadoPorMi && (
                  <div className="mt-1 text-[11px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded px-2 py-1 inline-flex items-center gap-1" data-testid={`surtido-tomar-primero-${i}`}>
                    🔒 Toma el pedido para poder definir el surtido
                  </div>
                )}
              </div>
              <div className="flex md:block items-baseline justify-between md:col-span-2 md:text-right tabular-nums">
                <span className="md:hidden text-[10px] font-bold uppercase text-muted-foreground">Cantidad</span>
                <span>{formatNumber(it.cantidad)} {it.unidad}</span>
              </div>
              <div className="flex md:block items-baseline justify-between md:col-span-2 md:text-right">
                <span className="md:hidden text-[10px] font-bold uppercase text-muted-foreground">Precio</span>
                <div className="text-right">
                  <div className="tabular-nums text-muted-foreground">{formatCOP(it.precio_unitario)}</div>
                  {it.precio_sugerido_snapshot !== undefined && (
                    <div className="text-[10px] text-muted-foreground/70">sug {formatCOP(it.precio_sugerido_snapshot)}</div>
                  )}
                </div>
              </div>
              <div className="flex md:block items-baseline justify-between md:col-span-2 md:text-right">
                <span className="md:hidden text-[10px] font-bold uppercase text-muted-foreground">Subtotal</span>
                <div className="text-right">
                  <div className="tabular-nums font-semibold">{formatCOP(it.subtotal)}</div>
                  {showComision && it.comision_pct !== undefined && (
                    <div className="text-[10px] text-primary">comisión {it.comision_pct}% = {formatCOP(it.comision_valor)}</div>
                  )}
                </div>
              </div>
              <div className="hidden md:block md:col-span-1"></div>
            </div>
          ))}
        </div>

        {/* Totales */}
        <div className="flex justify-end mt-3">
          <div className="w-80 space-y-1 text-sm">
            <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="tabular-nums">{formatCOP(pedido.subtotal)}</span></div>
            {pedido.descuento > 0 && <div className="flex justify-between text-muted-foreground"><span>Descuento</span><span className="tabular-nums">-{formatCOP(pedido.descuento)}</span></div>}
            {pedido.flete_cliente > 0 && (
              <div className="flex justify-between text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid="pedido-flete-line">
                <span className="flex items-center gap-1 font-semibold"><Truck className="w-3.5 h-3.5" /> Flete a cobrar</span>
                <span className="tabular-nums font-semibold">+{formatCOP(pedido.flete_cliente)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-border pt-2 text-base font-semibold"><span>Total</span><span className="tabular-nums">{formatCOP(pedido.total)}</span></div>
            {pedido.flete_cliente > 0 && (
              <div className="mt-2 p-2.5 rounded-lg bg-amber-100/60 border border-amber-300 text-amber-900 text-xs flex items-start gap-2" data-testid="pedido-flete-aviso">
                <Truck className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <b>Flete pagado contra entrega por Beem.</b>{" "}
                  {pedido.flete_cliente_motivo ? (
                    <span>{pedido.flete_cliente_motivo}. </span>
                  ) : null}
                  Se agregó al total del pedido para cobrar al cliente ({formatCOP(pedido.flete_cliente)}).
                </div>
              </div>
            )}
            {pedido.pronto_pago_ganado && pedido.pronto_pago_descuento_aplicado > 0 && (
              <div className="mt-2 p-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs" data-testid="pronto-pago-ganado">
                🎉 <b>Pronto pago aplicado</b>: se descontó {formatCOP(pedido.pronto_pago_descuento_aplicado)} porque pagó a tiempo.
              </div>
            )}
            {!pedido.pronto_pago_ganado && pedido.descuento_pronto_pago_potencial > 0 && pedido.fecha_limite_pronto_pago && (
              <div className="mt-2 p-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs" data-testid="pronto-pago-disponible">
                🎁 <b>Descuento pronto pago disponible</b>: {formatCOP(pedido.descuento_pronto_pago_potencial)} si paga completo antes del <b>{formatDate(pedido.fecha_limite_pronto_pago)}</b>. Total con descuento: <b>{formatCOP(pedido.total - pedido.descuento_pronto_pago_potencial)}</b>.
              </div>
            )}
          </div>
        </div>

        {/* Pagos y saldo */}
        <div className="mt-6" data-testid="pedido-pagos-section">
          {/* Cupos disponibles del cliente (mar/2026): panel visible solo si hay cupos activos */}
          <CuposDisponiblesPanel
            clienteId={pedido.cliente_id}
            pedido={pedido}
            onAplicado={async () => {
              try {
                const { data } = await api.get(`/pedidos/${pedido.id}`);
                setPedido(data);
                loadPagos?.();
              } catch { /* silencioso */ }
            }}
          />
          <div className="flex items-center justify-between mb-2 mt-3">
            <div className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-primary" />
              <div className="text-sm font-semibold text-foreground">Pagos y comprobantes</div>
            </div>
            {puedeReportarPago && (
              pedido.estado === "borrador" ? (
                <div className="relative group">
                  <Button disabled className="h-9 bg-muted text-muted-foreground text-xs cursor-not-allowed" data-testid="new-pago-btn-disabled">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Nuevo pago
                  </Button>
                  <div className="absolute right-0 top-full mt-1 z-10 hidden group-hover:block bg-foreground text-background text-[11px] px-2 py-1 rounded whitespace-nowrap shadow-lg">
                    Confirma el pedido antes de registrar un pago
                  </div>
                </div>
              ) : (
                <Button onClick={() => setPagoOpen(true)} className="h-9 bg-primary hover:bg-primary/90 text-primary-foreground text-xs" data-testid="new-pago-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Nuevo pago
                </Button>
              )
            )}
          </div>
          {pagosData.resumen && (
            <div className="grid grid-cols-3 gap-2 text-xs mb-2">
              <div className="p-2 bg-emerald-50 border border-emerald-100 rounded-lg">
                <div className="text-[10px] font-bold uppercase text-emerald-700">Aprobado</div>
                <div className="text-sm font-semibold tabular-nums text-emerald-800">{formatCOP(pagosData.resumen.total_aprobado)}</div>
              </div>
              <div className="p-2 bg-amber-50 border border-amber-100 rounded-lg">
                <div className="text-[10px] font-bold uppercase text-amber-700">Pendiente</div>
                <div className="text-sm font-semibold tabular-nums text-amber-800">{formatCOP(pagosData.resumen.total_pendiente)}</div>
              </div>
              <div className={`p-2 rounded-lg border ${pagosData.resumen.esta_pagado ? "bg-primary/10 border-primary/30" : "bg-muted/40 border-border"}`}>
                <div className={`text-[10px] font-bold uppercase ${pagosData.resumen.esta_pagado ? "text-primary" : "text-muted-foreground"}`}>{pagosData.resumen.esta_pagado ? "Pagado" : "Saldo"}</div>
                <div className="text-sm font-semibold tabular-nums text-foreground">{formatCOP(pagosData.resumen.saldo)}</div>
              </div>
            </div>
          )}
          {pagosData.pagos.length === 0 ? (
            <div className="p-4 border border-dashed border-border rounded-lg text-center text-xs text-muted-foreground">
              {pedido.estado === "borrador"
                ? "Este pedido está en borrador. Confírmalo primero para poder registrar pagos."
                : puedeReportarPago
                ? "Aún no hay pagos registrados. Usa \"Nuevo pago\" para adjuntar el comprobante."
                : "Aún no hay pagos registrados para este pedido."}
            </div>
          ) : (
            <div className="border border-border rounded-lg divide-y divide-border/60 max-h-56 overflow-y-auto">
              {pagosData.pagos.map((p) => {
                const meta = p.estado === "aprobado" ? { cls: "bg-emerald-100 text-emerald-800", icon: Check }
                           : p.estado === "rechazado" ? { cls: "bg-rose-100 text-rose-700", icon: X }
                           : { cls: "bg-amber-100 text-amber-800", icon: Clock };
                const Icon = meta.icon;
                return (
                  <div key={p.id} className="flex items-center gap-3 px-3 py-2 text-xs" data-testid={`pedido-pago-row-${p.id}`}>
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full font-medium ${meta.cls}`}>
                      <Icon className="w-3 h-3" /> {p.estado}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-foreground truncate">
                        <span className="tabular-nums font-semibold">{formatCOP(p.monto)}</span> · <span className="capitalize">{p.metodo}</span>
                        {p.referencia && <span className="font-mono text-muted-foreground ml-1">· {p.referencia}</span>}
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {p.subido_por_nombre} · {formatDate(p.subido_at)}
                        {p.motivo_rechazo && <span className="text-rose-600 ml-1">· {p.motivo_rechazo}</span>}
                      </div>
                    </div>
                    {puedeAprobarPago && p.estado === "pendiente" && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => aprobarPago(p)} title="Aprobar"
                          className="h-7 w-7 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center justify-center"
                          data-testid={`pedido-aprobar-pago-${p.id}`}>
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => rechazarPago(p)} title="Rechazar"
                          className="h-7 w-7 rounded-md bg-rose-50 text-rose-700 hover:bg-rose-100 flex items-center justify-center"
                          data-testid={`pedido-rechazar-pago-${p.id}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {pedido.notas && <div className="mt-4 p-3 bg-muted/40 border border-border rounded-lg text-sm text-foreground/80"><b>Notas:</b> {pedido.notas}</div>}
        {pedido.cancelled_motivo && <div className="mt-4 p-3 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-800"><b>Cancelado:</b> {pedido.cancelled_motivo}</div>}

        {/* Bloque de operador asignado / tomar */}
        {(canTomar || pedido.operador_nombre) && (
          <div className={`mt-4 p-4 rounded-lg border flex items-center justify-between gap-3 flex-wrap ${
            tomadoPorMi ? "bg-emerald-500/10 border-emerald-500/30" :
            pedido.operador_nombre ? "bg-blue-500/10 border-blue-500/30" :
            "bg-amber-500/10 border-amber-500/30"
          }`} data-testid="operador-block">
            <div className="flex items-center gap-3 min-w-0">
              <span className={`w-10 h-10 rounded-full flex items-center justify-center ${tomadoPorMi ? "bg-emerald-500/15 text-emerald-700" : pedido.operador_nombre ? "bg-blue-500/15 text-blue-700" : "bg-amber-500/15 text-amber-700"}`}>{tomadoPorMi ? <UserRound className="w-5 h-5" /> : pedido.operador_nombre ? <Lock className="w-5 h-5" /> : <Hand className="w-5 h-5" />}</span>
              <div className="min-w-0">
                {pedido.operador_nombre ? (
                  <>
                    <div className="text-sm font-semibold text-foreground">
                      {tomadoPorMi ? "Tú tomaste este pedido" : `Tomado por ${pedido.operador_nombre}`}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {pedido.operador_tomado_at ? `Desde ${formatDateTime(pedido.operador_tomado_at)}` : ""}
                      {tomadoPorMi && " · Solo tú (o un director) puedes despacharlo"}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm font-semibold text-foreground">Pedido sin operador asignado</div>
                    <div className="text-xs text-muted-foreground">Tómalo para reservarlo y evitar que otro operador lo prepare a la vez.</div>
                  </>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              {puedeTomar && (
                <button onClick={tomar} disabled={taking} className="h-10 px-4 rounded-full text-sm font-semibold flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white transition-all btn-tactile" data-testid="tomar-pedido-btn">
                  🙋 {taking ? "Tomando..." : "Tomar este pedido"}
                </button>
              )}
              {puedeLiberar && (
                <button onClick={liberar} disabled={taking} className="h-10 px-4 rounded-full text-sm font-medium flex items-center gap-2 border border-border hover:bg-accent transition-all" data-testid="liberar-pedido-btn">
                  🔓 Liberar
                </button>
              )}
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="mt-4 flex gap-4 text-xs text-muted-foreground flex-wrap">
          <span>Creado: {formatDateTime(pedido.created_at)}</span>
          {pedido.confirmed_at && <span>Confirmado: {formatDateTime(pedido.confirmed_at)}</span>}
          {pedido.dispatched_at && <span>Despachado: {formatDateTime(pedido.dispatched_at)}</span>}
          {pedido.delivered_at && <span>Entregado: {formatDateTime(pedido.delivered_at)}</span>}
          {pedido.cancelled_at && <span>Cancelado: {formatDateTime(pedido.cancelled_at)}</span>}
        </div>

        {/* Guía de transporte (visible cuando ya se despachó) */}
        {pedido.guia && (
          <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800">Guía de transporte</div>
              <div className="text-sm font-semibold text-amber-900 tabular-nums">
                {pedido.guia.numero}
                {pedido.guia.transportadora && <span className="ml-2 text-amber-700 font-normal">· {pedido.guia.transportadora}</span>}
              </div>
              <div className="text-[10px] text-amber-700/80">
                Subida por {pedido.guia.subida_por_nombre} · {pedido.guia.filename} · {Math.round((pedido.guia.size || 0) / 1024)} KB
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setGuiaMenu({ pedidoId: pedido.id, nombre: pedido.numero || pedido.id.slice(0,8) })}
                className="h-9 px-3 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold flex items-center gap-1"
                data-testid="ver-guia-btn"
                title="Ver la guía adjunta o descargarla como PDF/JPG"
              >
                <Paperclip className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />Ver guía
              </button>
              {(puedeEditarLogistica) && (
                <label
                  className={`h-9 px-3 rounded-full bg-white border border-amber-400 hover:bg-amber-100 text-amber-800 text-xs font-semibold flex items-center gap-1 ${guiaSubiendo ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                  data-testid="editar-guia-btn"
                  title="Reemplaza la foto/PDF de la guía si te equivocaste"
                >
                  {guiaSubiendo ? "Subiendo..." : "✏️ Editar guía"}
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    disabled={guiaSubiendo}
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const form = new FormData();
                      form.append("file", f);
                      setGuiaSubiendo(true);
                      try {
                        await api.patch(`/pedidos/${pedido.id}/guia`, form, { headers: { "Content-Type": "multipart/form-data" } });
                        toast.success("Guía actualizada");
                        const { data } = await api.get(`/pedidos/${pedido.id}`);
                        setPedido(data);
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "No se pudo actualizar la guía");
                      } finally {
                        setGuiaSubiendo(false);
                        e.target.value = "";
                      }
                    }}
                  />
                </label>
              )}
            </div>
          </div>
        )}

        {/* Costo de transporte (solo visible para operador/director, ya filtrado en backend) */}
        {pedido.guia && typeof pedido.costo_transporte !== "undefined" && (
          <div className="mt-3 p-3 rounded-lg bg-muted/40 border border-border flex items-center justify-between gap-2 flex-wrap" data-testid="costo-transporte-block">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">💵 Costo del transporte (Beem asume)</div>
              <div className="text-lg font-semibold text-foreground tabular-nums">
                {pedido.costo_transporte != null ? formatCOP(pedido.costo_transporte) : <span className="text-muted-foreground text-sm italic">Sin registrar</span>}
              </div>
              {pedido.costo_transporte_por_nombre && (
                <div className="text-[10px] text-muted-foreground">Registrado por {pedido.costo_transporte_por_nombre}</div>
              )}
              {pedido.flete_excepcion_beem && (
                <div className="text-[11px] text-amber-800 font-semibold" data-testid="flete-excepcion-motivo">Excepción: {pedido.flete_excepcion_motivo || "sin motivo"}</div>
              )}
              <div className="text-[10px] text-muted-foreground">Se refleja en el gasto mensual. No lo ve el vendedor.</div>
            </div>
            <div className="flex gap-2">
              {!editandoCostoTransp && puedeEditarLogistica && (
                <button
                  onClick={() => { setNuevoCostoTransp(pedido.costo_transporte || ""); setEditandoCostoTransp(true); }}
                  className="h-9 px-3 rounded-full bg-card border border-border hover:bg-muted/50 text-xs font-semibold"
                  data-testid="editar-costo-transporte-btn"
                >
                  ✏️ Editar
                </button>
              )}
              {editandoCostoTransp && puedeEditarLogistica && (
                <div className="flex items-center gap-1">
                  <MoneyInput
                    value={nuevoCostoTransp}
                    onValueChange={setNuevoCostoTransp}
                    className="h-9 w-28 text-right"
                    data-testid="costo-transporte-input"
                    autoFocus
                  />
                  <button onClick={guardarCostoTransp} className="h-9 px-3 rounded-full bg-primary text-primary-foreground text-xs font-semibold" data-testid="costo-transporte-save">Guardar</button>
                  <button onClick={() => setEditandoCostoTransp(false)} className="h-9 px-3 rounded-full border border-border text-xs">Cancelar</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex gap-2 justify-between flex-wrap">
          <div className="flex gap-2 flex-wrap">
            {/* Documento visible según estado del pedido */}
            {pedido.estado === "borrador" && (
              <button onClick={() => abrirDescargaPdf("COTIZACION")} disabled={downloading} className="h-10 px-4 rounded-full text-sm font-medium flex items-center gap-2 border border-border hover:bg-accent transition-all" data-testid="download-cotizacion-btn">
                <FileText className="w-4 h-4" /> {downloading ? "Descargando..." : "Cotización PDF"}
              </button>
            )}
            {pedido.estado === "confirmado" && (
              <button onClick={() => abrirDescargaPdf("ORDEN")} disabled={downloading} className="h-10 px-4 rounded-full text-sm font-medium flex items-center gap-2 border border-primary/40 text-primary hover:bg-accent transition-all" data-testid="download-orden-btn">
                <FileText className="w-4 h-4" /> {downloading ? "Descargando..." : "Orden de pedido PDF"}
              </button>
            )}
            {/* Botón Remisión — visible desde estado "confirmado" para que el operador
                pueda imprimir la remisión + rótulo ANTES de subir la guía a la transportadora.
                Después de despachar, el vendedor también puede descargar solo la remisión. */}
            {(pedido.estado === "confirmado" || pedido.estado === "despachado" || pedido.estado === "entregado") && (
              (isAdmin || puedeGestionarDespachos) ? (
                <button onClick={() => abrirDescargaPdf("REMISION", true)} disabled={downloading} className="h-10 px-4 rounded-full text-sm font-medium flex items-center gap-2 border border-primary/40 text-primary hover:bg-accent transition-all" data-testid="download-remision-btn" title="Descarga la remisión + una segunda página con el rótulo grande para pegar en el paquete">
                  <Truck className="w-4 h-4" /> {downloading ? "Descargando..." : "Remisión + Rótulo"}
                </button>
              ) : (
                <button onClick={() => abrirDescargaPdf("REMISION", false)} disabled={downloading} className="h-10 px-4 rounded-full text-sm font-medium flex items-center gap-2 border border-primary/40 text-primary hover:bg-accent transition-all" data-testid="download-remision-btn" title="Descarga la remisión del pedido">
                  <FileText className="w-4 h-4" /> {downloading ? "Descargando..." : "Remisión PDF"}
                </button>
              )
            )}
          </div>
          {actions[pedido.estado].length > 0 && (
            <div className="flex gap-2 justify-end flex-wrap">
              {actions[pedido.estado].map((a) => (
                <button key={a.label} onClick={a.action} className={`h-10 px-4 rounded-full text-sm font-medium flex items-center gap-2 btn-tactile ${a.color}`} data-testid={`action-${a.label}`}>
                  <a.icon className="w-4 h-4" /> {a.label}
                </button>
              ))}
            </div>
          )}
          {/* Edición de items + bloqueo (Mar 8, 2026) */}
          <div className="flex gap-2 justify-end flex-wrap">
            {puedeEditarItems && (
              <button
                onClick={() => setEditorAbierto(true)}
                className={`h-10 px-4 rounded-full text-sm font-medium flex items-center gap-2 border btn-tactile ${puedeEditarPostDespacho ? "border-amber-400 text-amber-700 hover:bg-amber-50" : "border-primary/40 text-primary hover:bg-primary/5"}`}
                data-testid="editar-items-btn"
                title={puedeEditarPostDespacho ? "Editar items de un pedido ya despachado (ajusta inventario)" : "Agregar o quitar productos del pedido"}
              >
                <Edit2 className="w-4 h-4" /> {puedeEditarPostDespacho ? "Editar items (post-despacho)" : "Editar items"}
              </button>
            )}
            {/* Botón Devolución de pedido (mar/2026) — visible para operadores/directores en
                pedidos activos (confirmado, despachado, entregado). No aparece en borrador,
                cancelado ni ya devuelto. */}
            {(isAdmin || puedeGestionarDespachos) &&
             ["confirmado", "despachado", "entregado"].includes(pedido.estado) && (
              <button
                onClick={() => setDevolucionAbierta(true)}
                className="h-10 px-4 rounded-full text-sm font-medium flex items-center gap-2 border border-amber-400 text-amber-700 hover:bg-amber-50 btn-tactile"
                data-testid="devolucion-abrir-btn"
                title="Registrar devolución total o parcial del pedido"
              >
                <Undo2 className="w-4 h-4" /> Devolución
              </button>
            )}
            {puedeBloquearEdicion && (
              <button
                onClick={bloquearEdicion}
                className="h-10 px-4 rounded-full text-sm font-medium flex items-center gap-2 border border-rose-300 text-rose-700 hover:bg-rose-50 btn-tactile"
                data-testid="bloquear-edicion-btn"
                title="Bloquea al vendedor para que no modifique items después de empacar"
              >
                <Lock className="w-4 h-4" /> Bloquear edición
              </button>
            )}
            {puedeDesbloquearEdicion && (
              <button
                onClick={desbloquearEdicion}
                className="h-10 px-4 rounded-full text-sm font-medium flex items-center gap-2 border border-emerald-400 text-emerald-700 hover:bg-emerald-50 btn-tactile"
                data-testid="desbloquear-edicion-btn"
              >
                <Unlock className="w-4 h-4" /> Desbloquear
              </button>
            )}
          </div>
          {edicionBloqueada && (
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-800 flex items-start gap-2" data-testid="edicion-bloqueada-badge">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <b>Edición bloqueada</b> por {pedido.edicion_bloqueada_por_nombre || "el operador"}.
                El vendedor ya no puede modificar los items. Solo el director puede desbloquear.
              </div>
            </div>
          )}
        </div>

        {/* Modal de edición de items (reutiliza el builder pasando el pedido existente) */}
        {editorAbierto && (
          <PedidoBuilder
            existing={pedido}
            onClose={() => setEditorAbierto(false)}
            onSaved={async () => {
              setEditorAbierto(false);
              await refrescarPedidoYPagos();
              toast.success("Pedido actualizado");
            }}
          />
        )}

        {/* Modal de devolución (mar/2026) */}
        <DevolucionPedidoModal
          pedido={pedido}
          open={devolucionAbierta}
          onOpenChange={setDevolucionAbierta}
          onDevuelto={async () => {
            try {
              const { data } = await api.get(`/pedidos/${pedido.id}`);
              setPedido(data);
            } catch { /* silencioso */ }
          }}
        />

        {/* Decidir excepción inline (director/aprobador) */}
        <Dialog open={!!decidiendoExc} onOpenChange={(v) => { if (decidiendoExcSaving) return; if (!v) { setDecidiendoExc(null); setMotivoDecision(""); } }}>
          <DialogContent data-testid="decidir-exc-modal">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">
                {decidiendoExc?.tipo === "aprobar" ? "Aprobar" : "Rechazar"} solicitud
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                {decidiendoExc?.tipo === "aprobar"
                  ? "El vendedor podrá confirmar este pedido con el precio solicitado."
                  : "El vendedor tendrá que ajustar el precio o hablar contigo antes de reintentar."}
              </p>
            </DialogHeader>
            <div className="mt-3 space-y-3">
              <Input
                value={motivoDecision}
                onChange={(e) => setMotivoDecision(e.target.value)}
                placeholder="Motivo de tu decisión (opcional)"
                className="h-11"
                data-testid="decidir-exc-motivo"
                autoFocus
              />
              <Button
                onClick={decidirExc}
                disabled={decidiendoExcSaving}
                className={`w-full h-11 text-white ${decidiendoExc?.tipo === "aprobar" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}
                data-testid="decidir-exc-confirmar"
              >
                {decidiendoExcSaving ? "Enviando..." : `Confirmar ${decidiendoExc?.tipo === "aprobar" ? "aprobación" : "rechazo"}`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Cancel modal */}
        <Dialog open={showCancel} onOpenChange={(v) => { if (changingEstado) return; setShowCancel(v); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancelar pedido</DialogTitle>
              <p className="text-sm text-muted-foreground">Indique el motivo. Se registrara en la auditoria.</p>
            </DialogHeader>
            <div className="mt-4 space-y-3">
              <Input value={cancelMotivo} onChange={(e) => setCancelMotivo(e.target.value)} placeholder="Motivo de cancelacion" className="h-11" data-testid="cancel-motivo" />
              <Button className="w-full bg-rose-600 hover:bg-rose-700 text-white" disabled={!cancelMotivo || changingEstado} onClick={async () => {
                await change("cancelado", { motivo: cancelMotivo });
                setShowCancel(false); setCancelMotivo("");
              }} data-testid="cancel-confirm">{changingEstado ? "Cancelando..." : "Confirmar cancelacion"}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Descartar borrador (elimina del sistema, sin motivo) */}
        <Dialog open={showDescartar} onOpenChange={(v) => { if (changingEstado) return; setShowDescartar(v); }}>
          <DialogContent data-testid="descartar-modal">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Descartar borrador</DialogTitle>
              <p className="text-sm text-muted-foreground">
                Este borrador se <b>eliminará del sistema</b>. Se liberará el stock reservado y no quedará rastro en tus listas.
              </p>
            </DialogHeader>
            <div className="mt-4 flex gap-2 justify-end">
              <Button variant="outline" disabled={changingEstado} onClick={() => setShowDescartar(false)} data-testid="descartar-cancelar">
                Volver
              </Button>
              <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={changingEstado} onClick={async () => {
                try {
                  await change("cancelado", { motivo: "" });
                  setShowDescartar(false);
                  onClose();
                  toast.success("Borrador eliminado");
                } catch { /* toast handled inside */ }
              }} data-testid="descartar-confirm">
                {changingEstado ? "Descartando..." : "Sí, descartar"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal despachar con guía */}
        <Dialog open={showDespachar} onOpenChange={(v) => {
          // No permitir cerrar mientras se está enviando la petición (evita que el
          // pedido termine despachado sin que el operador lo sepa si cierra a mitad).
          if (dispatching) return;
          setShowDespachar(v);
          if (!v) { setGuiaNumero(""); setGuiaTransp(""); setGuiaFile(null); setCostoTransp(""); setBeemPagoFlete(false); setMotivoFleteBeem(""); setRecogeEnBodega(false); }
        }}>
          <DialogContent data-testid="despachar-modal">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Marcar como despachado</DialogTitle>
              <p className="text-sm text-muted-foreground">
                Registra la guía de la transportadora o marca que el cliente lo recogió en bodega.
              </p>
            </DialogHeader>
            <div className="mt-4 space-y-3">
              {/* Toggle: Cliente recogió en bodega */}
              <div className="p-3 rounded-lg border border-emerald-300 bg-emerald-50">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={recogeEnBodega}
                    onChange={(e) => setRecogeEnBodega(e.target.checked)}
                    className="mt-1 w-4 h-4"
                    data-testid="despachar-recoge-bodega"
                  />
                  <div>
                    <div className="text-sm font-semibold text-emerald-900">Cliente recogió en bodega</div>
                    <div className="text-[11px] text-emerald-800/80">
                      El cliente pasó por el pedido personalmente. No se requiere guía ni transportadora.
                    </div>
                  </div>
                </label>
              </div>

              {!recogeEnBodega && (<>
              <div>
                <Label>Número de guía *</Label>
                <Input
                  value={guiaNumero}
                  onChange={(e) => setGuiaNumero(e.target.value)}
                  placeholder="Ej: 1000123456789"
                  className="h-11 mt-1 tabular-nums"
                  autoFocus
                  data-testid="despachar-guia-numero"
                />
              </div>
              <div>
                <Label>Transportadora</Label>
                <select
                  value={TRANSPORTADORAS_CONOCIDAS.includes(guiaTransp) ? guiaTransp : "Otra"}
                  onChange={(e) => setGuiaTransp(e.target.value === "Otra" ? " " : e.target.value)}
                  className="w-full h-11 mt-1 border border-border rounded-md px-3 bg-card text-sm"
                  data-testid="despachar-transp"
                >
                  <option value="">— Selecciona (opcional) —</option>
                  {TRANSPORTADORAS_CONOCIDAS.filter(Boolean).map((t) => <option key={t}>{t}</option>)}
                  <option>Otra</option>
                </select>
                {!TRANSPORTADORAS_CONOCIDAS.includes(guiaTransp) && (
                  <Input
                    value={guiaTransp.trim() ? guiaTransp : ""}
                    onChange={(e) => setGuiaTransp(e.target.value)}
                    placeholder="Escribe el nombre de la transportadora"
                    className="h-11 mt-2"
                    data-testid="despachar-transp-otra"
                    autoFocus
                  />
                )}
              </div>
              <div>
                <Label>Foto o documento de la guía *</Label>
                <FileInput
                  value={guiaFile}
                  onChange={setGuiaFile}
                  accept="image/*,.pdf,.doc,.docx,.heic,.heif,.tif,.tiff,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  label="Adjuntar guía"
                  testid="despachar-guia-file"
                  className="mt-1"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Acepta PDF, Word, foto (JPG/PNG/HEIC/WEBP), o escaneo. Máx 20 MB.
                </p>
                {guiaFile && <p className="text-[10px] text-muted-foreground mt-1">{guiaFile.name} · {Math.round(guiaFile.size / 1024)} KB</p>}
              </div>
              {requiereCostoTransp && (
                <div className="p-3 rounded-lg border border-amber-300 bg-amber-50">
                  <Label className="text-amber-900 font-semibold">
                    💵 Costo del transporte * <span className="text-[10px] font-normal">(Beem asume el flete porque el pedido alcanza ${umbralFlete.toLocaleString("es-CO")})</span>
                  </Label>
                  <MoneyInput
                    value={costoTransp}
                    onValueChange={setCostoTransp}
                    className="h-11 mt-1 bg-card"
                    placeholder="Ej: 25.000"
                    data-testid="despachar-costo-transporte"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Este valor NO se cobra al cliente. Se registra como gasto del mes para el director.
                  </p>
                </div>
              )}
              {!requiereCostoTransp && (
                <div className="p-3 rounded-lg border border-amber-300 bg-amber-50/60 space-y-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={beemPagoFlete}
                      onChange={(e) => setBeemPagoFlete(e.target.checked)}
                      className="mt-1 w-4 h-4"
                      data-testid="despachar-beem-pago-flete"
                    />
                    <div>
                      <div className="text-sm font-bold text-amber-900">
                        <Truck className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />Excepción — Beem asume el flete (aunque el pedido sea menor a ${umbralFlete.toLocaleString("es-CO")})
                      </div>
                      <div className="text-[11px] text-amber-900/80">
                        Úsalo cuando esta vez <b>Beem corre con el costo del transporte</b>. El valor se registra como <b>gasto de transporte de Beem</b> y <b>NO se le cobra al cliente</b> (el total del pedido no cambia).
                      </div>
                    </div>
                  </label>
                  {beemPagoFlete && (
                    <div>
                      <Label className="text-amber-900 font-semibold">Valor del flete que paga Beem *</Label>
                      <MoneyInput
                        value={costoTransp}
                        onValueChange={setCostoTransp}
                        className="h-11 mt-1 bg-card"
                        placeholder="Ej: 12.000"
                        data-testid="despachar-flete-valor"
                      />
                      <p className="text-[10px] text-amber-900/70 mt-1">
                        Se registra como gasto del mes. No se suma al total del cliente.
                      </p>
                      <Label className="text-amber-900 font-semibold mt-2 block">Motivo de la excepción *</Label>
                      <Input
                        value={motivoFleteBeem}
                        onChange={(e) => setMotivoFleteBeem(e.target.value)}
                        className="h-10 mt-1 bg-card"
                        placeholder="Ej: cliente frecuente, compensación por demora..."
                        data-testid="despachar-flete-motivo"
                      />
                    </div>
                  )}
                </div>
              )}
              </>)}
              <Button
                onClick={enviarDespacho}
                disabled={dispatching || (!recogeEnBodega && (!guiaNumero.trim() || !guiaFile || ((requiereCostoTransp || beemPagoFlete) && !costoTransp) || (beemPagoFlete && !requiereCostoTransp && !motivoFleteBeem.trim())))}
                className="w-full h-11 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="despachar-confirm"
              >
                <Truck className="w-4 h-4 mr-2" />
                {dispatching ? "Despachando..." : (recogeEnBodega ? "Confirmar entrega en bodega" : "Despachar y notificar al vendedor")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Definir surtido modal */}
        <Dialog open={!!surtidoItem} onOpenChange={(v) => { if (surtidoSaving) return; if (!v) setSurtidoItem(null); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="surtido-modal">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Definir surtido</DialogTitle>
              <p className="text-sm text-muted-foreground">
                <b>{surtidoItem?.producto_nombre}</b> · pedido {formatNumber(surtidoItem?.cantidad || 0)} {surtidoItem?.unidad || "u"}. Distribuye las cantidades por variante.
              </p>
              {surtidoItem?.notas && (
                <div className="mt-1 inline-flex items-start gap-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="surtido-nota-vendedor">
                  <StickyNote className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /><span><b>Nota del vendedor:</b> {surtidoItem.notas}</span>
                </div>
              )}
            </DialogHeader>
            {surtidoLoading ? (
              <div className="mt-6 text-center text-sm text-muted-foreground">Calculando sugerencia proporcional al stock...</div>
            ) : (
              <>
                <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                  <div>Sugerido: proporcional al stock disponible. Puedes ajustar.</div>
                  <div>
                    Suma actual: <b className={
                      surtidoDesglose.reduce((s, d) => s + (Number(d.cantidad) || 0), 0) === Number(surtidoItem?.cantidad || 0)
                        ? "text-emerald-600" : "text-rose-600"
                    } data-testid="surtido-suma">
                      {surtidoDesglose.reduce((s, d) => s + (Number(d.cantidad) || 0), 0)} / {surtidoItem?.cantidad || 0}
                    </b>
                  </div>
                </div>
                <div className="mt-3 border border-border rounded-lg divide-y divide-border/60 max-h-[55vh] overflow-y-auto">
                  {surtidoDesglose.map((d, di) => (
                    <div key={d.variante_id} className="grid grid-cols-12 gap-2 items-center px-3 py-2">
                      <div className="col-span-1">
                        {d.tiene_imagen ? (
                          <img
                            src={`${API}/catalogo/img/${d.variante_id}?w=200`}
                            alt={d.variante_nombre}
                            className="w-12 h-12 rounded-md object-cover border border-border cursor-zoom-in bg-muted"
                            loading="lazy"
                            decoding="async"
                            onClick={() => setLightboxSrc(`${API}/catalogo/img/${d.variante_id}?w=800`)}
                            data-testid={`surtido-foto-${di}`}
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-md bg-muted flex items-center justify-center text-muted-foreground/50 border border-dashed border-border" title="Sin foto">
                            <Package className="w-5 h-5" />
                          </div>
                        )}
                      </div>
                      <div className="col-span-6">
                        <div className="text-[12px] sm:text-sm text-foreground break-words leading-tight">{d.variante_nombre}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{d.codigo || d.sku || ""} · disp. {d.stock_disponible}</div>
                      </div>
                      <div className="col-span-3 text-right text-[11px] text-muted-foreground">máx {d.stock_disponible}</div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          min="0"
                          max={d.stock_disponible}
                          step="1"
                          value={d.cantidad}
                          onChange={(e) => {
                            const v = Math.max(0, Math.min(d.stock_disponible, Number(e.target.value) || 0));
                            setSurtidoDesglose((prev) => prev.map((x, i) => i === di ? { ...x, cantidad: v } : x));
                          }}
                          className="h-9 text-right tabular-nums"
                          data-testid={`surtido-cant-${di}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setSurtidoItem(null)}>Cancelar</Button>
                  <Button onClick={guardarSurtido} disabled={surtidoSaving} className="bg-primary hover:bg-primary/90" data-testid="surtido-confirmar">
                    {surtidoSaving ? "Guardando..." : "Guardar surtido"}
                  </Button>
                </div>
              </>
            )}
            <ImageLightbox src={lightboxSrc} open={!!lightboxSrc} onOpenChange={(v) => { if (!v) setLightboxSrc(null); }} />
          </DialogContent>
        </Dialog>


        {/* Nuevo pago modal */}
        <Dialog open={pagoOpen && puedeReportarPago} onOpenChange={(v) => { if (pagoSaving) return; setPagoOpen(v); }}>
          <DialogContent data-testid="pago-modal">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Registrar pago</DialogTitle>
              <p className="text-sm text-muted-foreground">
                Pedido <b>{pedido.numero}</b> · Total {formatCOP(pedido.total)}
                {pagosData.resumen && <> · Saldo <b className="text-primary">{formatCOP(pagosData.resumen.saldo)}</b></>}
              </p>
            </DialogHeader>
            {pagosData?.resumen && (pagosData.resumen.pronto_pago || pagosData.resumen.descuento_general > 0 || pagosData.resumen.cupos_aplicados_total > 0) && (
              <div className="mt-3 rounded-xl border border-border bg-muted/30 p-3 space-y-2 text-xs" data-testid="pago-descuentos-nota">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Descuentos y ajustes de este pedido</div>
                {pagosData.resumen.descuento_general > 0 && (
                  <div data-testid="pago-descuento-general">· Descuento aplicado en el pedido: <b>−{formatCOP(pagosData.resumen.descuento_general)}</b> (ya incluido en el total){pagosData.resumen.descuento_general_detalle ? ` · ${pagosData.resumen.descuento_general_detalle}` : ""}</div>
                )}
                {pagosData.resumen.cupos_aplicados_total > 0 && (
                  <div data-testid="pago-cupos">· Saldo a favor / cupo aplicado: <b>−{formatCOP(pagosData.resumen.cupos_aplicados_total)}</b> (ya descontado del saldo)</div>
                )}
                {pagosData.resumen.pronto_pago?.estado === "aplicado" && (
                  <div className="text-emerald-700 dark:text-emerald-300" data-testid="pago-pronto-pago-aplicado">· Pronto pago <b>ganado</b>: −{formatCOP(pagosData.resumen.pronto_pago.descuento)}{pagosData.resumen.pronto_pago.total_original ? ` (total original ${formatCOP(pagosData.resumen.pronto_pago.total_original)})` : ""}. El total ya quedó con el descuento.</div>
                )}
                {pagosData.resumen.pronto_pago?.estado === "vigente" && (
                  <div className="rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 p-2 text-emerald-800 dark:text-emerald-200" data-testid="pago-pronto-pago-vigente">
                    · <b>Pronto pago disponible</b>: si el cliente completa el pago hasta el <b>{pagosData.resumen.pronto_pago.fecha_limite}</b>, se descuentan <b>{formatCOP(pagosData.resumen.pronto_pago.descuento)}</b>.
                    {" "}Le bastaría pagar <b>{formatCOP(pagosData.resumen.pronto_pago.saldo_con_descuento)}</b> en vez de {formatCOP(pagosData.resumen.saldo)}; al aprobarse el pago el sistema aplica el descuento solo y el saldo queda en cero.
                    <button type="button" onClick={() => setPagoForm({ ...pagoForm, monto: pagosData.resumen.pronto_pago.saldo_con_descuento })}
                      className="ml-2 h-7 px-2 rounded-md bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider hover:bg-emerald-700" data-testid="pago-monto-pronto-pago">
                      Usar monto con descuento
                    </button>
                  </div>
                )}
                {pagosData.resumen.pronto_pago?.estado === "vencido" && (
                  <div className="text-muted-foreground" data-testid="pago-pronto-pago-vencido">· El pronto pago (−{formatCOP(pagosData.resumen.pronto_pago.descuento)}) <b>venció el {pagosData.resumen.pronto_pago.fecha_limite}</b>; se paga el saldo completo.</div>
                )}
              </div>
            )}
            <form onSubmit={registrarPago} className="mt-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Monto (COP)*</Label>
                  <div className="relative mt-1">
                    <MoneyInput required value={pagoForm.monto}
                      onValueChange={(v) => setPagoForm({ ...pagoForm, monto: v })}
                      className="h-11 pr-16" placeholder="Ej: 50.000" data-testid="pago-monto" />
                    {pagosData?.resumen?.saldo > 0 && (
                      <button type="button"
                        onClick={() => setPagoForm({ ...pagoForm, monto: pagosData.resumen.saldo })}
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-8 px-2 text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 hover:bg-primary/20 rounded-md"
                        data-testid="pago-monto-max">
                        Todo
                      </button>
                    )}
                  </div>
                  {pagosData?.resumen?.saldo > 0 && (
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      Máx: {formatCOP(pagosData.resumen.saldo)}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Método*</Label>
                  <select value={pagoForm.metodo}
                    onChange={(e) => setPagoForm({ ...pagoForm, metodo: e.target.value })}
                    className="w-full h-11 mt-1 border border-border rounded-md px-3 bg-card text-sm" data-testid="pago-metodo">
                    <option value="transferencia">Transferencia</option>
                    <option value="consignacion">Consignación</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="nequi">Nequi</option>
                    <option value="daviplata">Daviplata</option>
                    <option value="tarjeta">Tarjeta</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Referencia / N° de operación</Label>
                <Input value={pagoForm.referencia}
                  onChange={(e) => setPagoForm({ ...pagoForm, referencia: e.target.value })}
                  className="h-11 mt-1" placeholder="Opcional" data-testid="pago-referencia" />
              </div>
              <div>
                <Label>Notas</Label>
                <Input value={pagoForm.notas}
                  onChange={(e) => setPagoForm({ ...pagoForm, notas: e.target.value })}
                  className="h-11 mt-1" placeholder="Opcional" />
              </div>
              <div>
                <Label>Comprobante (imagen o PDF, máx 8 MB)*</Label>
                <label className="mt-1 flex items-center gap-3 h-14 px-4 border-2 border-dashed border-border rounded-lg hover:border-primary/50 cursor-pointer bg-card">
                  <Upload className="w-5 h-5 text-muted-foreground/70" />
                  <div className="flex-1 text-sm text-muted-foreground truncate">
                    {pagoForm.file ? <span className="font-medium text-foreground">{pagoForm.file.name}</span> : "Seleccionar archivo..."}
                  </div>
                  <input type="file" accept="image/*,application/pdf"
                    onChange={(e) => setPagoForm({ ...pagoForm, file: e.target.files?.[0] || null })}
                    className="hidden" data-testid="pago-file" />
                </label>
              </div>
              <Button type="submit" disabled={pagoSaving} className="w-full h-11 bg-primary hover:bg-primary/90" data-testid="pago-save">
                {pagoSaving ? "Guardando..." : "Registrar pago"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        <DescargaFormatoModal
          open={pdfFormatoOpen}
          onOpenChange={setPdfFormatoOpen}
          titulo={pdfPending?.tipo === "REMISION" ? "Descargar remisión" : pdfPending?.tipo === "ORDEN" ? "Descargar orden de pedido" : pdfPending?.tipo === "COMPROBANTE" ? "Descargar comprobante" : "Descargar cotización"}
          onElegir={async (format) => {
            if (!pdfPending) return;
            await downloadPdf(pdfPending.tipo, pdfPending.conRotulo, format);
            setPdfPending(null);
          }}
        />
        {guiaMenu && (
          <DescargarGuiaMenu
            pedidoId={guiaMenu.pedidoId}
            nombreArchivo={guiaMenu.nombre}
            onClose={() => setGuiaMenu(null)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}


/* ---------------- Cupo Live Preview ---------------- */
function CupoLivePreview({ cupoInfo, total, existingPedidoSaldo }) {
  if (!cupoInfo) return null;
  const cupo = cupoInfo.capacidad_endeudamiento;
  // Si no está configurado → no mostrar nada (crédito abierto, no interesa al vendedor)
  if (cupo === null || cupo === undefined) return null;

  const deudaBruta = Number(cupoInfo.deuda_pendiente || 0);
  // Si estamos editando un pedido existente, hay que restar su saldo previo (ya viene sumado en deuda_pendiente)
  const deudaOtros = Math.max(0, deudaBruta - Number(existingPedidoSaldo || 0));
  const disponible = Math.max(0, cupo - deudaOtros);
  const esteTotal = Number(total || 0);
  const restanteDespues = disponible - esteTotal;
  const excede = restanteDespues < 0;
  const bloqueado = cupo === 0;

  return (
    <div
      className={`rounded-xl border-2 p-4 space-y-3 transition-colors ${
        bloqueado
          ? "border-rose-400 bg-rose-50/70"
          : excede
          ? "border-red-400 bg-red-50/70"
          : restanteDespues < esteTotal * 0.2
          ? "border-amber-400 bg-amber-50/70"
          : "border-emerald-300 bg-emerald-50/60"
      }`}
      data-testid="cupo-live-preview"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Wallet className={`w-4 h-4 ${excede || bloqueado ? "text-red-600" : "text-emerald-700"}`} />
          <span>Cupo de crédito de {cupoInfo.nombre}</span>
        </div>
        {bloqueado && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-rose-600 text-white font-semibold">
            SIN CRÉDITO · Solo contra entrega
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
        <CupoCell label="Cupo total" value={formatCOP(cupo)} />
        <CupoCell label="Deuda actual" value={formatCOP(deudaOtros)} muted={deudaOtros === 0} />
        <CupoCell label="Disponible" value={formatCOP(disponible)} tone={disponible === 0 ? "red" : "emerald"} />
        <CupoCell label="Este pedido" value={formatCOP(esteTotal)} tone={esteTotal > 0 ? "primary" : "muted"} />
        <CupoCell
          label={excede ? "Se pasa por" : "Le quedará"}
          value={formatCOP(Math.abs(restanteDespues))}
          tone={excede ? "red" : "emerald"}
          strong
        />
      </div>

      {!bloqueado && excede && (
        <div className="flex items-start gap-2 text-xs bg-red-100 border border-red-200 rounded-md p-2 text-red-900">
          <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">Este pedido supera el cupo disponible por {formatCOP(Math.abs(restanteDespues))}.</div>
            <div className="text-red-800">
              Podés guardar el borrador, pero al confirmar el sistema lo bloqueará. Reduce cantidades, cobra pedidos pendientes o pide autorización a un director.
            </div>
          </div>
        </div>
      )}

      {bloqueado && esteTotal > 0 && (
        <div className="flex items-start gap-2 text-xs bg-rose-100 border border-rose-200 rounded-md p-2 text-rose-900">
          <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            Este cliente está marcado <b>sin crédito</b>. Solo se puede facturar contra entrega (pago inmediato al despachar).
          </div>
        </div>
      )}

      {!excede && !bloqueado && restanteDespues < esteTotal * 0.2 && esteTotal > 0 && (
        <div className="text-xs text-amber-800">
          <AlertTriangle className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />Le quedaría poco cupo tras este pedido ({formatCOP(restanteDespues)}). Ojo si vas a montar más pedidos hoy.
        </div>
      )}
    </div>
  );
}

function CupoCell({ label, value, tone = "default", strong = false, muted = false }) {
  const toneCls =
    tone === "red" ? "text-red-700"
      : tone === "emerald" ? "text-emerald-700"
      : tone === "primary" ? "text-primary"
      : muted ? "text-muted-foreground/70"
      : "text-foreground";
  return (
    <div className="rounded-md bg-white/60 px-2 py-1.5 border border-white/80">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className={`tabular-nums ${strong ? "font-bold text-sm" : "font-semibold text-[13px]"} ${toneCls}`}>{value}</div>
    </div>
  );
}
